// GENERATED by packages/contracts/scripts/generate.ts — do not edit by hand.

/**
 * The request and response schemas as runtime values, for a server-side
 * validator. `$id`/`$schema` are stripped — see renderSchemasTs in
 * packages/contracts/scripts/lib.ts for why both would break Ajv.
 */
export const API_SCHEMAS = {
  AbandonRequest: {
    "additionalProperties": false,
    "description": "Body of POST /attempts/{id}/abandon (BACKEND_DESIGN §6.2, §12, #63). Abandonment is a session-item lifecycle fact, never a response — no response_events row of any kind is written by this route, and 'declared_unknown' has its own shape on POST /attempts/{id}/assistance rather than being reachable from here (§6.3: the two must stay distinct, and #63's tests prove it). resolutionReason is optional here for the same reason it is optional on session_items in general (session_items_skip_states_its_reason) — only a 'skipped' resolution requires one, and this route always resolves as 'abandoned', which does not.",
    "properties": {
      "resolutionReason": {
        "description": "session_items.resolution_reason — free text, read by a human, never branched on. Optional: an abandon carries no required reason the way a skip does.",
        "maxLength": 512,
        "minLength": 1,
        "type": "string"
      }
    },
    "title": "AbandonRequest",
    "type": "object"
  },
  AnswerKeyCompletenessResponse: {
    "additionalProperties": false,
    "description": "Answer of GET /answer-key-completeness (#182, D26 continued). #182 requires that an exercise with no confirmed answer key is usable for practice but produces no gradeable evidence, and that this exclusion be visible in a completeness report, never silent. This response is that report: counts, over every activity_definitions row, of which of four mutually exclusive buckets its current expected_response_versions row (the highest version for that activity) falls into. The four counts sum to the total number of activity definitions. No percentage, in any field or anywhere in this description, ever (CLAUDE.md's hard rule) — a count of pages/activities, never a bar with a number on it.",
    "properties": {
      "confirmed": {
        "description": "Activities whose current expected response version is gradeable: migration 0028's predicate holds — at least one content_provenance row names it, and none of them have an authority outside textbook/answer_key/approved_correction/founder_authored. A non-negative count, never a percentage.",
        "type": "integer"
      },
      "noExpectedResponse": {
        "description": "Activities with no expected_response_versions row at all. Kept as its own bucket rather than folded into unusable: 'nothing was ever proposed' is a different fact from 'something was proposed with no recorded source' — confirmed, proposed and unusable are all about a version that exists, and this bucket is about the absence of any version to judge. A non-negative count, never a percentage.",
        "type": "integer"
      },
      "proposed": {
        "description": "Activities whose current expected response version exists and has a content_provenance row, but that row's authority is outside the gradeable allowlist (model_knowledge; and, since migration 0040, computed - a computed key is not gradeable until a later migration says so) — migration 0028's conjunct 2 failing. A model proposed this answer; nobody competent to confirm it (D26) has. A non-negative count, never a percentage.",
        "type": "integer"
      },
      "unusable": {
        "description": "Activities whose current expected response version exists but has zero content_provenance rows — migration 0028's conjunct 1 failing: nobody recorded a source for it at all. A non-negative count, never a percentage.",
        "type": "integer"
      }
    },
    "required": [
      "confirmed",
      "proposed",
      "unusable",
      "noExpectedResponse"
    ],
    "title": "AnswerKeyCompletenessResponse",
    "type": "object"
  },
  AssessmentCreateRequest: {
    "additionalProperties": false,
    "description": "Body of POST /courses/{courseRef}/assessments (#444): an assessment the learner adds - a title, a kind, a date and the course sections it covers. A declaration by the learner, not an observation and not evidence. The title is untrusted text, stored as data.",
    "properties": {
      "date": {
        "description": "The day of the assessment, YYYY-MM-DD.",
        "format": "date",
        "type": "string"
      },
      "kind": {
        "description": "What sort of assessment it is.",
        "enum": [
          "quiz",
          "midterm",
          "exam",
          "other"
        ],
        "type": "string"
      },
      "sectionNumbers": {
        "description": "The numbers of the course sections it covers, each as a decimal string (an identifier, never a magnitude); each must be a known section of the course. At least one.",
        "items": {
          "maxLength": 6,
          "minLength": 1,
          "type": "string"
        },
        "minItems": 1,
        "type": "array",
        "uniqueItems": true
      },
      "title": {
        "description": "The learner's title, for example Calculus quiz. 1 to 120 characters once trimmed.",
        "maxLength": 120,
        "minLength": 1,
        "type": "string"
      }
    },
    "required": [
      "title",
      "kind",
      "date",
      "sectionNumbers"
    ],
    "title": "AssessmentCreateRequest",
    "type": "object"
  },
  AssessmentResponse: {
    "additionalProperties": false,
    "description": "One assessment the learner declared (#444): a title, a kind, a date and the topics it covers. A learner-entered declaration - it is neither an observation of how the learner performed nor a conclusion about what they know, and it carries no status, score, count or percentage. The title is untrusted learner text, returned as data.",
    "properties": {
      "assessmentId": {
        "description": "An identifier DaVinci minted when the learner added the assessment.",
        "maxLength": 64,
        "minLength": 1,
        "type": "string"
      },
      "courseRef": {
        "description": "Moodle's course id, as the decimal string the path parameter and the courses/ folders use (an identifier, never a magnitude).",
        "maxLength": 10,
        "minLength": 1,
        "type": "string"
      },
      "date": {
        "description": "The day of the assessment, YYYY-MM-DD.",
        "format": "date",
        "type": "string"
      },
      "kind": {
        "description": "What sort of assessment the learner said it is.",
        "enum": [
          "quiz",
          "midterm",
          "exam",
          "other"
        ],
        "type": "string"
      },
      "scope": {
        "description": "The topics (course sections) it covers, ordered by section number. Never empty.",
        "items": {
          "additionalProperties": false,
          "properties": {
            "sectionName": {
              "description": "The section's name as Moodle gave it (untrusted text, returned as data), or null.",
              "type": [
                "string",
                "null"
              ]
            },
            "sectionNumber": {
              "description": "The course section's number: an identifier, not a magnitude.",
              "type": "integer"
            },
            "topicKey": {
              "description": "An identifier, moodleCourseId:sectionNumber.",
              "type": "string"
            }
          },
          "required": [
            "topicKey",
            "sectionNumber",
            "sectionName"
          ],
          "type": "object"
        },
        "minItems": 1,
        "type": "array"
      },
      "source": {
        "const": "learner",
        "description": "Who declared the assessment. Always learner for now; Moodle-sourced assessments come later.",
        "type": "string"
      },
      "title": {
        "description": "The learner's own title for the assessment (untrusted text, returned as data).",
        "maxLength": 120,
        "minLength": 1,
        "type": "string"
      }
    },
    "required": [
      "assessmentId",
      "courseRef",
      "title",
      "kind",
      "date",
      "scope",
      "source"
    ],
    "title": "AssessmentResponse",
    "type": "object"
  },
  AssessmentsResponse: {
    "additionalProperties": false,
    "description": "Answer of GET /assessments (#444): the caller's upcoming learner-declared assessments across every ticked course, soonest first. Withdrawn and past assessments are not listed. A list of declarations, not of conclusions: nothing here says how the learner is doing.",
    "properties": {
      "assessments": {
        "items": {
          "$ref": "https://davinci.local/schemas/assessment-response.json"
        },
        "type": "array"
      }
    },
    "required": [
      "assessments"
    ],
    "title": "AssessmentsResponse",
    "type": "object"
  },
  AssistanceRequest: {
    "additionalProperties": false,
    "description": "Body of POST /attempts/{id}/assistance (BACKEND_DESIGN §6.3, §11.2, §12, #63). Carries exactly one of two shapes, which this schema cannot express as an oneOf (composition keywords are banned outright — schema-rules.test.ts) so the store validates the exclusivity instead: {responseKind, responseText} for the learner's current draft, saved as the checkpoint response_events row before the next rung is computed; or {noRecall: true} for the explicit 'I do not know yet', saved as response_kind = declared_unknown with empty text. The server never accepts a requested rung — §12 is explicit the client cannot request a level or skip to an answer — so there is no level field here at all; the next legal rung is computed from server history (assistance_events, this attempt) inside the transaction.",
    "properties": {
      "noRecall": {
        "const": true,
        "description": "The explicit 'I do not know yet' action (§6.3). When present it must be true; the store refuses this alongside responseKind/responseText.",
        "type": "boolean"
      },
      "responseKind": {
        "description": "The kind of the current draft being checkpointed. Only 'typed' or 'selected' — 'declared_unknown' has its own shape below (noRecall) rather than being spelled as a responseKind, which is what keeps the two checkpoint shapes from collapsing into one the store cannot tell apart.",
        "enum": [
          "typed",
          "selected"
        ],
        "type": "string"
      },
      "responseText": {
        "description": "The current draft text, present together with responseKind and absent together with noRecall — the store refuses any other combination.",
        "maxLength": 2000,
        "minLength": 0,
        "type": "string"
      }
    },
    "title": "AssistanceRequest",
    "type": "object"
  },
  AssistanceResponse: {
    "additionalProperties": false,
    "description": "Answer of POST /attempts/{id}/assistance (BACKEND_DESIGN §6.3, §11.2, §12, #63). No numeric level anywhere here — the granted rung is server bookkeeping, per §12's 2026-08-14 amendment on #62: the client cannot request a level and the UI never offers a picker, so the response carries the hint, its delivery id, and whether more help remains rather than the number itself. hasMoreHelp is derived from the granted rung being below the ladder's ceiling (5) without exposing the rung that produced it. Issue #188 (D1-3, HYP-UX-002): also carries nextHelp, disclosing what the *next* rung — not the one just delivered in this response — would cost, so the learner sees the cost of another hint before pressing the button again. nextHelp is present if and only if hasMoreHelp is true.",
    "properties": {
      "contentKind": {
        "description": "content_delivery_events.content_kind for this delivery — 'hint' for rungs 1-3, 'explanation' for rung 4, 'answer' for rung 5 (the genuine reveal rung).",
        "enum": [
          "hint",
          "explanation",
          "answer"
        ],
        "type": "string"
      },
      "deliveryEventId": {
        "maxLength": 36,
        "minLength": 36,
        "type": "string"
      },
      "hasMoreHelp": {
        "description": "True when the granted rung is below 5 — the ladder's ceiling — so 'Another hint' (FRONTEND_DESIGN §4.5) may still be offered. False at rung 5: the answer has been revealed and there is no further rung to grant.",
        "type": "boolean"
      },
      "language": {
        "description": "The language of the delivered text — assistance_events.language, and the same value on the content_delivery_events row. The support language at rungs 1-4, which return an authored placeholder; the expected response's own language at rung 5, which returns expected_text verbatim. BCP 47, never branched on as a literal.",
        "maxLength": 16,
        "minLength": 2,
        "type": "string"
      },
      "nextHelp": {
        "additionalProperties": false,
        "description": "Issue #188 (D1-3, HYP-UX-002): the disclosure of the rung that the *next* POST /attempts/{id}/assistance call on this attempt would grant — never the rung whose text this same response just delivered. Present if and only if hasMoreHelp is true; absent means no further rung can be granted for this attempt.",
        "properties": {
          "costsIndependence": {
            "description": "costsIndependence(kind) from @davinci/domain — derived from kind, never hand-written, so UI text describing the cost cannot contradict the rule that actually decides it.",
            "type": "boolean"
          },
          "kind": {
            "description": "The declared kind of the next rung, read from ASSISTANCE_LADDER[level].kind (@davinci/domain) — never a rung number, never a level. \"none\" is excluded: a rung that delivers nothing is not a grantable rung, so it can never be the next rung disclosed here.",
            "enum": [
              "re_presentation",
              "informative",
              "revealing"
            ],
            "type": "string"
          },
          "policyVersion": {
            "description": "ASSISTANCE_LADDER_VERSION (@davinci/domain) — invariant 3: names the policy version that produced this disclosure.",
            "maxLength": 64,
            "minLength": 1,
            "type": "string"
          }
        },
        "required": [
          "kind",
          "costsIndependence",
          "policyVersion"
        ],
        "type": "object"
      },
      "text": {
        "description": "The help text actually delivered — content_delivery_events.content_text. A Phase 1 placeholder below rung 5 (no model gateway exists yet, BACKEND_DESIGN §13), and the real expected_response_versions.expected_text at rung 5.",
        "maxLength": 4000,
        "minLength": 1,
        "type": "string"
      }
    },
    "required": [
      "deliveryEventId",
      "contentKind",
      "text",
      "language",
      "hasMoreHelp"
    ],
    "title": "AssistanceResponse",
    "type": "object"
  },
  AttemptStartResponse: {
    "additionalProperties": false,
    "description": "Answer of POST /session-items/{id}/attempts (BACKEND_DESIGN §6.2, §11.3, §12; 2026-08-14 amendment, #62). Starting an attempt returns the prompt actually shown and its promptVersionId (§11.3: evidence retains the prompt version actually used), the response mode from the activity definition, and the source indicator (§12) — and never the expected response. No expectedText, no answer, no approvedVariants, no expectedResponseVersionId: the handler that builds this response does not query expected_response_versions at all, because if the expected response reached the client every subsequent response would be contaminated and no test could tell afterwards — the ledger would look identical either way. sourceIndicator.authority is \"unrecorded\" when no content_provenance row exists for this prompt version: nothing in this schema requires one to exist, so the API names the absence rather than guessing. Issue #188 (D1-3, HYP-UX-002): also carries nextHelp, the disclosure of what the next POST /attempts/{id}/assistance call on this attempt would cost, so the learner can see that cost before ever pressing the help button — never after. nextHelp is absent exactly when no further rung can be granted for this attempt: the ladder is exhausted. D24 (#43, #106): also carries promptDeliveryId, the content_delivery_events row this prompt was recorded as — exactly one per attempt, written when it was created. The client must acknowledge it through the same presentation endpoint it already uses for hints (FRONTEND_DESIGN §4.5): an unacknowledged prompt delivery inside the attempt reads as a full answer reveal (independence-query.ts's lost-presentation rule) and would make every response in the attempt non-independent. That direction is deliberate.",
    "properties": {
      "activityId": {
        "maxLength": 36,
        "minLength": 36,
        "type": "string"
      },
      "attemptId": {
        "maxLength": 36,
        "minLength": 36,
        "type": "string"
      },
      "nextHelp": {
        "additionalProperties": false,
        "description": "Issue #188 (D1-3, HYP-UX-002): the disclosure of the rung that the next POST /attempts/{id}/assistance call on this attempt would grant — shown before the help button is ever pressed, never after. Absent means no further rung can be granted for this attempt: the ladder is exhausted.",
        "properties": {
          "costsIndependence": {
            "description": "costsIndependence(kind) from @davinci/domain — derived from kind, never hand-written, so UI text describing the cost cannot contradict the rule that actually decides it.",
            "type": "boolean"
          },
          "kind": {
            "description": "The declared kind of the next rung, read from ASSISTANCE_LADDER[level].kind (@davinci/domain) — never a rung number, never a level. \"none\" is excluded: a rung that delivers nothing is not a grantable rung, so it can never be the next rung disclosed here.",
            "enum": [
              "re_presentation",
              "informative",
              "revealing"
            ],
            "type": "string"
          },
          "policyVersion": {
            "description": "ASSISTANCE_LADDER_VERSION (@davinci/domain) — invariant 3: names the policy version that produced this disclosure.",
            "maxLength": 64,
            "minLength": 1,
            "type": "string"
          }
        },
        "required": [
          "kind",
          "costsIndependence",
          "policyVersion"
        ],
        "type": "object"
      },
      "objectiveSliceId": {
        "maxLength": 36,
        "minLength": 36,
        "type": "string"
      },
      "promptDeliveryId": {
        "description": "content_delivery_events.delivery_event_id for this prompt's own delivery row (D24, #43, #106) — content_kind 'prompt', written exactly once per attempt. The client passes this as deliveryEventId to POST /content-presentations, the same idempotent acknowledgement path used for hints, so the delivery is not conservatively treated as an unconfirmed answer reveal.",
        "maxLength": 36,
        "minLength": 36,
        "type": "string"
      },
      "promptLanguage": {
        "description": "prompt_versions.language. BCP 47.",
        "maxLength": 16,
        "minLength": 2,
        "type": "string"
      },
      "promptText": {
        "description": "prompt_versions.prompt_text.",
        "maxLength": 4000,
        "minLength": 1,
        "type": "string"
      },
      "promptVersionId": {
        "description": "prompt_versions.prompt_version_id — the exact version shown, recorded on the attempt (BACKEND_DESIGN §6.2, §11.3) so evidence names the prompt actually shown.",
        "maxLength": 36,
        "minLength": 36,
        "type": "string"
      },
      "responseMode": {
        "description": "activity_definitions.response_mode.",
        "enum": [
          "choice",
          "short_text",
          "sentence"
        ],
        "type": "string"
      },
      "sessionItemId": {
        "maxLength": 36,
        "minLength": 36,
        "type": "string"
      },
      "sourceIndicator": {
        "additionalProperties": false,
        "description": "§12's source indicator, and FRONTEND_DESIGN §4.5's source/unit context.",
        "properties": {
          "authority": {
            "description": "content_provenance.authority for this prompt version, or \"unrecorded\" when no content_provenance row exists for it — nothing in the schema requires one to exist, so the API names the absence rather than guessing.",
            "enum": [
              "textbook",
              "answer_key",
              "approved_correction",
              "founder_authored",
              "model_knowledge",
              "computed",
              "unrecorded"
            ],
            "type": "string"
          },
          "unitTitle": {
            "description": "units.title, reached by prompt_versions → activity_definitions → objective_slices → learning_objectives → units.",
            "maxLength": 200,
            "minLength": 1,
            "type": "string"
          }
        },
        "required": [
          "authority",
          "unitTitle"
        ],
        "type": "object"
      },
      "startedAt": {
        "description": "activity_attempts.occurred_at — when the prompt was shown, server clock. UTC ISO-8601.",
        "maxLength": 20,
        "minLength": 20,
        "type": "string"
      },
      "subtype": {
        "description": "activity_definitions.subtype (#190). The player cannot otherwise tell a cloze (sentence_production/controlled_completion) from ordinary short-text production, since responseMode alone collapses both to 'short_text'. This is the same activity metadata GET /practisable-activities already returns un-gated (FRONTEND_DESIGN §4.4's plan-card rule is about prompts and expected answers, never family/subtype), so carrying it here leaks nothing new. This enum is the union across every family rather than a conditional on family, because JSON Schema composition keywords (allOf/oneOf/if-then-else) are forbidden in this package (schema-rules.test.ts) — the (family, subtype) pairing itself is enforced only by the database CHECK this response is reading back. Kept in exact sync with practisable-activities-response.json's own subtype enum.",
        "enum": [
          "recognition_target_to_support",
          "production_support_to_target",
          "meaning_in_context",
          "gender",
          "number",
          "collocation",
          "sentence_use",
          "recognition",
          "form_completion",
          "independent_production",
          "error_correction",
          "new_sentence_use",
          "main_idea",
          "detail_retrieval",
          "vocabulary_grammar_in_context",
          "short_comprehension_answer",
          "controlled_completion",
          "transformation",
          "prompted_answer",
          "short_original_sentence"
        ],
        "type": "string"
      }
    },
    "required": [
      "attemptId",
      "sessionItemId",
      "activityId",
      "objectiveSliceId",
      "promptVersionId",
      "promptDeliveryId",
      "promptText",
      "promptLanguage",
      "responseMode",
      "subtype",
      "sourceIndicator",
      "startedAt"
    ],
    "title": "AttemptStartResponse",
    "type": "object"
  },
  AuthSessionResponse: {
    "additionalProperties": false,
    "description": "Who the current session belongs to (#371). Answers GET /auth/session, and is what a successful login or sign-up returns alongside the session cookies. It names the account and the display name and nothing derived: no learner id (the browser never needs to hold, and so can never send, one), no status, no is_independent. username is null for a learner who has no login — the founder, signed in by the launch link before running set-login.",
    "properties": {
      "displayName": {
        "type": [
          "string",
          "null"
        ]
      },
      "founder": {
        "description": "True when this session is the installation's founder/operator; not derived from performance.",
        "type": "boolean"
      },
      "username": {
        "type": [
          "string",
          "null"
        ]
      }
    },
    "required": [
      "username",
      "displayName",
      "founder"
    ],
    "title": "AuthSessionResponse",
    "type": "object"
  },
  BootstrapResponse: {
    "additionalProperties": false,
    "properties": {
      "createdAt": {
        "format": "date-time",
        "type": "string"
      },
      "learnerId": {
        "format": "uuid",
        "type": "string"
      }
    },
    "required": [
      "learnerId",
      "createdAt"
    ],
    "title": "BootstrapResponse",
    "type": "object"
  },
  ConsentDecisionRequest: {
    "additionalProperties": false,
    "description": "Body of POST /consent/decisions (#373, D30): one decision about one purpose. additionalProperties is false, so a client cannot name a learner (the decision is the signed-in learner's) or a time (the server records it). statementVersion must equal the current version or the request is refused with consent_statement_outdated and nothing is recorded: consent is to the words that were shown. A improvement grant is refused with training_terms_unverified until each provider's output-use terms have been checked and recorded. Withdrawing feedback stops processing at once: it cancels the learner's queued, not-yet-started jobs and deletes their staged, unsent model payloads. Anything already sent to a provider stays with it for the period the statement gives.",
    "properties": {
      "decision": {
        "enum": [
          "granted",
          "withdrawn"
        ],
        "type": "string"
      },
      "purpose": {
        "enum": [
          "feedback",
          "system_evaluation",
          "improvement"
        ],
        "type": "string"
      },
      "statementVersion": {
        "maxLength": 64,
        "minLength": 1,
        "type": "string"
      }
    },
    "required": [
      "purpose",
      "decision",
      "statementVersion"
    ],
    "title": "ConsentDecisionRequest",
    "type": "object"
  },
  ConsentDecisionsResponse: {
    "additionalProperties": false,
    "description": "The signed-in learner's current decision on each purpose (#373, D30). Answers GET /consent/decisions and is what POST /consent/decisions returns after recording. Exactly one entry per purpose. state says where the decision stands under the CURRENT statement: granted (the latest decision is a grant and was made under statementVersion), outdated (the latest is a grant, but under an older text, so the learner must be asked again), withdrawn (the latest is a withdrawal, which includes a grant and a withdrawal recorded in the same second), or none (never decided). Only granted lets anything happen: a purpose that is none, outdated or withdrawn is not consented to.",
    "properties": {
      "decisions": {
        "items": {
          "additionalProperties": false,
          "properties": {
            "purpose": {
              "enum": [
                "feedback",
                "system_evaluation",
                "improvement"
              ],
              "type": "string"
            },
            "recordedAt": {
              "description": "When the current decision was recorded; null when the learner has never decided.",
              "type": [
                "string",
                "null"
              ]
            },
            "state": {
              "enum": [
                "granted",
                "outdated",
                "withdrawn",
                "none"
              ],
              "type": "string"
            }
          },
          "required": [
            "purpose",
            "state",
            "recordedAt"
          ],
          "type": "object"
        },
        "type": "array"
      },
      "statementVersion": {
        "description": "The current statement version; the one a new decision must quote.",
        "maxLength": 64,
        "minLength": 1,
        "type": "string"
      }
    },
    "required": [
      "statementVersion",
      "decisions"
    ],
    "title": "ConsentDecisionsResponse",
    "type": "object"
  },
  ConsentStatementResponse: {
    "additionalProperties": false,
    "description": "The consent statement a learner is shown, and its version (#373, D30). Answers GET /consent/statement. Plain text only, never markup: the screen decides presentation. statementVersion is what a decision must quote back (POST /consent/decisions refuses any other), so that consent is always to the words that were shown. The three purposes are separate decisions; the providers block names every service a learner's work may be sent to and what each says about keeping it, in the providers' own terms and attributed to them; storage says where the work lives, who can read it, and how backups bear on a deletion. training says whether the optional third purpose can be granted at all: it cannot until each provider's output-use terms are checked and recorded, and unavailableReason then says so in words. summary is a digest of the providers and storage blocks, covered by the same statementVersion, and is no longer the first thing shown at the gate. gate is the short per-consent pages in each language (en, he), covered by the same statementVersion. Nothing here is a percentage, a score, or a conclusion about the learner.",
    "properties": {
      "gate": {
        "additionalProperties": false,
        "description": "The short pages the gate shows, one per consent, in each language, in the order shown.",
        "properties": {
          "en": {
            "items": {
              "additionalProperties": false,
              "properties": {
                "choice": {
                  "minLength": 1,
                  "type": "string"
                },
                "hint": {
                  "type": [
                    "string",
                    "null"
                  ]
                },
                "label": {
                  "minLength": 1,
                  "type": "string"
                },
                "lead": {
                  "type": [
                    "string",
                    "null"
                  ]
                },
                "purpose": {
                  "enum": [
                    "feedback",
                    "system_evaluation"
                  ],
                  "type": "string"
                },
                "rows": {
                  "items": {
                    "additionalProperties": false,
                    "properties": {
                      "heading": {
                        "minLength": 1,
                        "type": "string"
                      },
                      "text": {
                        "minLength": 1,
                        "type": "string"
                      }
                    },
                    "required": [
                      "heading",
                      "text"
                    ],
                    "type": "object"
                  },
                  "type": "array"
                },
                "title": {
                  "minLength": 1,
                  "type": "string"
                }
              },
              "required": [
                "purpose",
                "label",
                "title",
                "lead",
                "rows",
                "choice",
                "hint"
              ],
              "type": "object"
            },
            "type": "array"
          },
          "he": {
            "items": {
              "additionalProperties": false,
              "properties": {
                "choice": {
                  "minLength": 1,
                  "type": "string"
                },
                "hint": {
                  "type": [
                    "string",
                    "null"
                  ]
                },
                "label": {
                  "minLength": 1,
                  "type": "string"
                },
                "lead": {
                  "type": [
                    "string",
                    "null"
                  ]
                },
                "purpose": {
                  "enum": [
                    "feedback",
                    "system_evaluation"
                  ],
                  "type": "string"
                },
                "rows": {
                  "items": {
                    "additionalProperties": false,
                    "properties": {
                      "heading": {
                        "minLength": 1,
                        "type": "string"
                      },
                      "text": {
                        "minLength": 1,
                        "type": "string"
                      }
                    },
                    "required": [
                      "heading",
                      "text"
                    ],
                    "type": "object"
                  },
                  "type": "array"
                },
                "title": {
                  "minLength": 1,
                  "type": "string"
                }
              },
              "required": [
                "purpose",
                "label",
                "title",
                "lead",
                "rows",
                "choice",
                "hint"
              ],
              "type": "object"
            },
            "type": "array"
          }
        },
        "required": [
          "en",
          "he"
        ],
        "type": "object"
      },
      "providerNotes": {
        "description": "Things true of all the providers together, e.g. that the paid API is said not to train on content, and that zero retention is not assumed.",
        "items": {
          "minLength": 1,
          "type": "string"
        },
        "type": "array"
      },
      "providers": {
        "description": "Every service the work may be sent to, with its stated retention in plain words.",
        "items": {
          "additionalProperties": false,
          "properties": {
            "note": {
              "type": [
                "string",
                "null"
              ]
            },
            "product": {
              "minLength": 1,
              "type": "string"
            },
            "retention": {
              "minLength": 1,
              "type": "string"
            },
            "vendor": {
              "minLength": 1,
              "type": "string"
            }
          },
          "required": [
            "vendor",
            "product",
            "retention",
            "note"
          ],
          "type": "object"
        },
        "type": "array"
      },
      "purposes": {
        "description": "One entry per purpose, each its own recorded decision, in the order the screen shows them.",
        "items": {
          "additionalProperties": false,
          "properties": {
            "optional": {
              "description": "True when DaVinci can be used without saying yes to this purpose.",
              "type": "boolean"
            },
            "purpose": {
              "enum": [
                "feedback",
                "system_evaluation",
                "improvement"
              ],
              "type": "string"
            },
            "text": {
              "minLength": 1,
              "type": "string"
            },
            "title": {
              "minLength": 1,
              "type": "string"
            }
          },
          "required": [
            "purpose",
            "title",
            "text",
            "optional"
          ],
          "type": "object"
        },
        "type": "array"
      },
      "statementVersion": {
        "description": "The version of this text, e.g. pilot-consent@1. Changes whenever the copy changes in a way that changes what a learner agrees to; a grant under an older version is not current consent.",
        "maxLength": 64,
        "minLength": 1,
        "type": "string"
      },
      "storage": {
        "description": "Where the work is kept, who can read it, how long backups keep it, and what withdrawing and deleting do and do not reach.",
        "items": {
          "minLength": 1,
          "type": "string"
        },
        "type": "array"
      },
      "summary": {
        "description": "A few plain sentences shown before anything else: a digest of the providers and storage blocks, never claiming more than they do.",
        "items": {
          "minLength": 1,
          "type": "string"
        },
        "minItems": 1,
        "type": "array"
      },
      "training": {
        "additionalProperties": false,
        "properties": {
          "available": {
            "type": "boolean"
          },
          "unavailableReason": {
            "description": "Why improvement cannot be granted; null exactly when available is true.",
            "type": [
              "string",
              "null"
            ]
          }
        },
        "required": [
          "available",
          "unavailableReason"
        ],
        "type": "object"
      }
    },
    "required": [
      "statementVersion",
      "summary",
      "purposes",
      "providers",
      "providerNotes",
      "storage",
      "gate",
      "training"
    ],
    "title": "ConsentStatementResponse",
    "type": "object"
  },
  ContentDeliveryRequest: {
    "additionalProperties": false,
    "description": "Body of POST /content-deliveries (BACKEND_DESIGN §6.2, §12, #63). This is the one route in the whole API that may return answer-bearing text, and only because the client explicitly asked for it by name: the server resolves content_text/content_reference itself from data already in the ledger — the client never supplies content, only what it wants (invariant 1: the server derives what is exposed). attemptId is optional because this route is reachable outside an attempt (§6.3's Show source/answer from the evidence view is the real case, and independence-query.ts's out-of-attempt branch exists for exactly it). When attemptId is present the server resolves activityId and objectiveSliceId from the attempt itself and ignores any conflicting values here — a client-supplied objectiveSliceId that disagreed with the attempt's own slice would trip content_delivery_events_match_their_context as a raw trigger abort (500) rather than a validated 400, so the server does not trust the client to keep the two in step. activityId is required by the store (not by this schema, since the requirement is conditional on attemptId's absence and this schema has no composition keyword to express that) when attemptId is absent, because without an attempt the server needs another way to know which activity's content is being asked for.",
    "properties": {
      "activityId": {
        "description": "activity_definitions.activity_id — which activity's content is wanted. Required by the store when attemptId is absent; ignored by the store (resolved from the attempt instead) when attemptId is present.",
        "maxLength": 36,
        "minLength": 36,
        "type": "string"
      },
      "attemptId": {
        "description": "activity_attempts.attempt_id, when this delivery happens inside an attempt. Absent for the evidence-view/Textbook/Progress/adjudication/developer cases §12 describes, where there is no attempt at all.",
        "maxLength": 36,
        "minLength": 36,
        "type": "string"
      },
      "contentKind": {
        "description": "content_delivery_events.content_kind (content_delivery_events_content_kind_valid). 'answer' reveals expected_response_versions.expected_text for the named activity's current version — the reveal rung, and the reason this route exists as a named, explicit act rather than a side effect of anything else.",
        "enum": [
          "hint",
          "feedback",
          "explanation",
          "answer",
          "source"
        ],
        "type": "string"
      },
      "objectiveSliceId": {
        "description": "objective_slices.slice_id. Only consulted by the store when attemptId is absent — when an attempt is given, the server always uses the attempt's own objective slice rather than a value supplied here, which is what content_delivery_events_match_their_context (migration 0002) requires it to match.",
        "maxLength": 36,
        "minLength": 36,
        "type": "string"
      },
      "surface": {
        "description": "content_delivery_events.surface (content_delivery_events_surface_valid) — where the request originated.",
        "enum": [
          "activity_player",
          "evidence_view",
          "session_summary",
          "import_checking"
        ],
        "type": "string"
      }
    },
    "required": [
      "contentKind",
      "surface"
    ],
    "title": "ContentDeliveryRequest",
    "type": "object"
  },
  ContentDeliveryResponse: {
    "additionalProperties": false,
    "description": "Answer of POST /content-deliveries (BACKEND_DESIGN §6.2, §12, #63). Carries whatever content_delivery_events row the server just wrote: text and/or reference, exactly as content_delivery_events_records_what_was_delivered requires (at least one non-null). This is the one response in the whole API allowed to carry answer-bearing text, because the request named exactly what it wanted by contentKind.",
    "properties": {
      "contentKind": {
        "description": "content_delivery_events.content_kind — echoed back so the caller need not remember what it asked for.",
        "enum": [
          "hint",
          "feedback",
          "explanation",
          "answer",
          "source"
        ],
        "type": "string"
      },
      "deliveryEventId": {
        "maxLength": 36,
        "minLength": 36,
        "type": "string"
      },
      "reference": {
        "description": "content_delivery_events.content_reference — a versioned reference in place of inline text. Null when the delivery carried text directly.",
        "maxLength": 200,
        "minLength": 1,
        "type": [
          "string",
          "null"
        ]
      },
      "text": {
        "description": "content_delivery_events.content_text. Null when the delivery was carried as a reference instead — this server's composer always populates text and leaves reference null, but the resource's shape does not assume that stays true.",
        "maxLength": 4000,
        "minLength": 1,
        "type": [
          "string",
          "null"
        ]
      }
    },
    "required": [
      "deliveryEventId",
      "contentKind",
      "text",
      "reference"
    ],
    "title": "ContentDeliveryResponse",
    "type": "object"
  },
  ContentPresentationRequest: {
    "additionalProperties": false,
    "description": "Body of POST /content-presentations (BACKEND_DESIGN §6.2, §6.3, §12; FRONTEND_DESIGN §4.5, #63). FRONTEND_DESIGN §4.5 calls this 'an idempotent global presentation acknowledgement' sent from many different surfaces for the same delivery — the Idempotency-Key header alone cannot make it idempotent, because different UI contexts sending the same acknowledgement will not share one key. So the store checks for an existing content_presentation_events row for this delivery before inserting, inside the transaction, and returns it rather than attempting a second insert against content_presentation_events_unique_delivery. clientReportedAtMs is the client's own monotonic clock reading at render time, never a wall clock (§6.4) — the server never compares it to its own clock, only stores it alongside the server-received occurredAt.",
    "properties": {
      "clientReportedAtMs": {
        "description": "content_presentation_events.client_reported_at_ms — the client's monotonic clock reading at render (FRONTEND_DESIGN §5), not a wall-clock timestamp. An observation the client reports, never a server judgement — the distinction the NUMERIC_FIELDS allowlist is aimed at.",
        "type": "integer"
      },
      "deliveryEventId": {
        "description": "content_delivery_events.delivery_event_id — the delivery being acknowledged as rendered.",
        "maxLength": 36,
        "minLength": 36,
        "type": "string"
      }
    },
    "required": [
      "deliveryEventId"
    ],
    "title": "ContentPresentationRequest",
    "type": "object"
  },
  ContentPresentationResponse: {
    "additionalProperties": false,
    "description": "Answer of POST /content-presentations (BACKEND_DESIGN §6.2, §12, #63) — the presentation event id and when the server recorded it. Nothing else: content_presentation_events carries IDs and timing, never duplicate content (§6.2's table comment), and this response mirrors that discipline.",
    "properties": {
      "occurredAt": {
        "description": "content_presentation_events.occurred_at — when the server received the acknowledgement. UTC ISO-8601, YYYY-MM-DDTHH:MM:SSZ.",
        "maxLength": 20,
        "minLength": 20,
        "type": "string"
      },
      "presentationEventId": {
        "maxLength": 36,
        "minLength": 36,
        "type": "string"
      }
    },
    "required": [
      "presentationEventId",
      "occurredAt"
    ],
    "title": "ContentPresentationResponse",
    "type": "object"
  },
  CourseResponse: {
    "additionalProperties": false,
    "description": "One course the pilot's Moodle catalog lists for this learner (#436): its titles (untrusted Moodle text, returned as data), the earliest exercise still due, whether the learner ticked it and the colour that identifies it. Nothing here says how the learner is doing.",
    "properties": {
      "code": {
        "description": "The course code, for example 80134.",
        "type": "string"
      },
      "colour": {
        "description": "The learner's chosen identifier colour, or the default for the course's place in the list.",
        "enum": [
          "slate",
          "teal",
          "plum",
          "ochre",
          "moss",
          "rose"
        ],
        "type": "string"
      },
      "moodleCourseId": {
        "description": "Moodle's own course id, as the decimal string the path parameter and the courses/ folders already use (an identifier, never a magnitude).",
        "maxLength": 10,
        "minLength": 1,
        "type": "string"
      },
      "nextDue": {
        "additionalProperties": false,
        "description": "The earliest exercise of the course's context file due after now, or null when there is none or the course has no context file.",
        "properties": {
          "dueAt": {
            "description": "ISO-8601 UTC.",
            "type": "string"
          },
          "exerciseTitle": {
            "type": "string"
          }
        },
        "required": [
          "exerciseTitle",
          "dueAt"
        ],
        "type": [
          "object",
          "null"
        ]
      },
      "selected": {
        "description": "Ticked by default; false once the learner unticks it.",
        "type": "boolean"
      },
      "shortName": {
        "description": "The name shown in the course-focus picker: the learner's own short name, or the default derived from the title (trailing (1)-style numerals and 'for ... students' / Hebrew 'לתלמידי ...' qualifiers removed).",
        "maxLength": 200,
        "minLength": 1,
        "type": "string"
      },
      "shortNameIsDefault": {
        "description": "True while the learner has not set their own short name (the web offers 'reset' only when false).",
        "type": "boolean"
      },
      "symbol": {
        "additionalProperties": false,
        "description": "The identifier glyph: the learner's override, or the automatic one (subject family from the catalogue snapshot by course number; maths sub-symbol by title keyword inside mathematics only; otherwise the first letter). `letter` is always the first letter of the short name, so a client can render the 'letter' key.",
        "properties": {
          "key": {
            "enum": [
              "medicine",
              "economics",
              "languages",
              "education",
              "sociology",
              "law",
              "history",
              "politics",
              "life-sciences",
              "agriculture",
              "psychology",
              "area-studies",
              "computer-science",
              "chemistry",
              "philosophy",
              "mathematics",
              "physics",
              "religion",
              "geography",
              "earth",
              "communication",
              "arts",
              "statistics",
              "calculus",
              "linear-algebra",
              "discrete",
              "algebra",
              "geometry",
              "number-theory",
              "probability",
              "letter"
            ],
            "type": "string"
          },
          "letter": {
            "maxLength": 8,
            "minLength": 1,
            "type": "string"
          }
        },
        "required": [
          "key",
          "letter"
        ],
        "type": "object"
      },
      "symbolIsDefault": {
        "description": "True while the learner has not overridden the automatic symbol.",
        "type": "boolean"
      },
      "title": {
        "additionalProperties": false,
        "description": "The course title in Hebrew and in English (the same text in both when Moodle gave one language).",
        "properties": {
          "en": {
            "type": "string"
          },
          "he": {
            "type": "string"
          }
        },
        "required": [
          "he",
          "en"
        ],
        "type": "object"
      }
    },
    "required": [
      "moodleCourseId",
      "code",
      "title",
      "nextDue",
      "selected",
      "colour",
      "shortName",
      "shortNameIsDefault",
      "symbol",
      "symbolIsDefault"
    ],
    "title": "CourseResponse",
    "type": "object"
  },
  CoursesResponse: {
    "additionalProperties": false,
    "description": "Answer of GET /courses (#436): the courses discovered from Moodle under the policy moodle-course-discovery@1 that are not withdrawn, ordered by course code, each with the caller's own selection and colour.",
    "properties": {
      "courses": {
        "items": {
          "$ref": "https://davinci.local/schemas/course-response.json"
        },
        "type": "array"
      }
    },
    "required": [
      "courses"
    ],
    "title": "CoursesResponse",
    "type": "object"
  },
  CourseTopicsResponse: {
    "additionalProperties": false,
    "description": "What DaVinci concludes the caller can do in each topic (a section) of one course, from the caller's own checked homework (#490; UX_EXPERIENCE §4.8). Each topic is a derived conclusion (invariant 2) and names the claims it rests on and the policy versions that produced it (invariant 3). Computed on read. No count, score or percentage about knowledge appears anywhere.",
    "properties": {
      "courseRef": {
        "description": "Moodle's course id, as the decimal string of the path parameter.",
        "maxLength": 10,
        "minLength": 1,
        "type": "string"
      },
      "topics": {
        "description": "Every known topic of the course, ordered by sectionNumber. A topic with no decided claim is not_tested.",
        "items": {
          "additionalProperties": false,
          "properties": {
            "basisClaimIds": {
              "description": "The checked claims this conclusion rests on (invariant 3).",
              "items": {
                "type": "string"
              },
              "type": "array"
            },
            "contradictingClaimIds": {
              "description": "Qualifying claims that point the other way.",
              "items": {
                "type": "string"
              },
              "type": "array"
            },
            "evidence": {
              "description": "One readable entry per basis, contradicting and held claim, and per in-app exam-prep attempt that is a basis or contradicting event, oldest first (#496, #440). Observations of what was checked, never a score; claimId is for matching only and is never shown.",
              "items": {
                "additionalProperties": false,
                "properties": {
                  "claimId": {
                    "description": "For matching against basisClaimIds and contradictingClaimIds only: a claim id for homework, the attempt id for in_app.",
                    "type": "string"
                  },
                  "helpBefore": {
                    "description": "The strongest help DaVinci delivered on this question before the check (for in_app: before the attempt; a prep hint is hint); rule is a quoted course rule.",
                    "enum": [
                      "none",
                      "rule",
                      "hint",
                      "counterexample",
                      "solution"
                    ],
                    "type": "string"
                  },
                  "question": {
                    "description": "The question label, or null when none was confirmed.",
                    "type": [
                      "string",
                      "null"
                    ]
                  },
                  "role": {
                    "enum": [
                      "basis",
                      "contradicting",
                      "held_for_dispute"
                    ],
                    "type": "string"
                  },
                  "ruling": {
                    "description": "How a dispute on this claim was ruled: overturned reads as correct, upheld stands as the stored verdict. Null when never ruled. The verdict stays the checker's observation.",
                    "enum": [
                      "overturned",
                      "upheld",
                      null
                    ],
                    "type": [
                      "string",
                      "null"
                    ]
                  },
                  "sheet": {
                    "description": "The sheet's title as stored (untrusted text, returned as data).",
                    "type": "string"
                  },
                  "source": {
                    "description": "Where the answer was given. homework: a checked upload. in_app: an exam-prep attempt (#440), whose sheet and question are those of the homework claim the practice item was made from.",
                    "enum": [
                      "homework",
                      "in_app"
                    ],
                    "type": "string"
                  },
                  "uploadedAt": {
                    "description": "When the answer was observed: the upload's date for homework, the attempt's submission for in_app.",
                    "format": "date-time",
                    "type": "string"
                  },
                  "verdict": {
                    "description": "The check's verdict on the claim as stored. A refuted claim under an open dispute is held, not concluded.",
                    "enum": [
                      "proved",
                      "refuted"
                    ],
                    "type": "string"
                  }
                },
                "required": [
                  "role",
                  "source",
                  "sheet",
                  "question",
                  "uploadedAt",
                  "verdict",
                  "ruling",
                  "helpBefore",
                  "claimId"
                ],
                "type": "object"
              },
              "type": "array"
            },
            "evidenceState": {
              "description": "The projection's evidence state. Not a status and never shown as one.",
              "enum": [
                "none",
                "limited",
                "conflicting",
                "sufficient_for_label"
              ],
              "type": "string"
            },
            "heldForDisputeClaimIds": {
              "description": "Claims whose refutation is under an open dispute: nothing is concluded from them.",
              "items": {
                "type": "string"
              },
              "type": "array"
            },
            "nextEvidenceNeeded": {
              "description": "Plain language: what would move this conclusion.",
              "type": "string"
            },
            "policyVersions": {
              "additionalProperties": false,
              "description": "The versions that produced the conclusion (invariant 3).",
              "properties": {
                "ladder": {
                  "description": "The assistance-ladder version(s) that decided independence, deduplicated.",
                  "items": {
                    "type": "string"
                  },
                  "type": "array"
                },
                "mapping": {
                  "description": "The topic-mapping version: homework-topic-status@2, or topic-status@3 once the topic has an in-app exam-prep attempt (#440).",
                  "type": "string"
                },
                "postAnswerLadder": {
                  "description": "The post-answer ladder version(s) of the help rungs that decided whether an answer was revealed, deduplicated; empty when no rung was used.",
                  "items": {
                    "type": "string"
                  },
                  "type": "array"
                },
                "prep": {
                  "description": "The versions behind the in-app exam-prep part of a topic-status@3 conclusion (#440), as stamped on its rows: the grading policy (prep-grade@N), the help ladder (prep-help@N), the certificate kernel, and the independence window (independence-scope@N). Empty while the topic has no in-app attempt.",
                  "items": {
                    "type": "string"
                  },
                  "type": "array"
                },
                "status": {
                  "description": "The status-projection policy version.",
                  "type": "string"
                }
              },
              "required": [
                "status",
                "mapping",
                "ladder",
                "postAnswerLadder",
                "prep"
              ],
              "type": "object"
            },
            "reason": {
              "description": "One sentence on why the status is what it is.",
              "type": "string"
            },
            "sectionName": {
              "description": "The section's name as Moodle gave it (untrusted text, returned as data), or null.",
              "type": [
                "string",
                "null"
              ]
            },
            "sectionNumber": {
              "description": "The course section's number: an identifier, not a magnitude.",
              "type": "integer"
            },
            "status": {
              "$ref": "https://davinci.local/schemas/learner-status.json"
            },
            "topicKey": {
              "description": "An identifier, moodleCourseId:sectionNumber.",
              "type": "string"
            }
          },
          "required": [
            "topicKey",
            "sectionNumber",
            "sectionName",
            "status",
            "evidenceState",
            "basisClaimIds",
            "contradictingClaimIds",
            "heldForDisputeClaimIds",
            "evidence",
            "nextEvidenceNeeded",
            "reason",
            "policyVersions"
          ],
          "type": "object"
        },
        "type": "array"
      },
      "unresolvedExercises": {
        "description": "Exercises that could not be placed in a topic, so their claims count toward none.",
        "items": {
          "additionalProperties": false,
          "properties": {
            "exerciseId": {
              "type": "string"
            },
            "reason": {
              "type": "string"
            }
          },
          "required": [
            "exerciseId",
            "reason"
          ],
          "type": "object"
        },
        "type": "array"
      }
    },
    "required": [
      "courseRef",
      "topics",
      "unresolvedExercises"
    ],
    "title": "CourseTopicsResponse",
    "type": "object"
  },
  CourseUpdateRequest: {
    "additionalProperties": false,
    "description": "Body of PATCH /courses/{moodleCourseId} (#436): the learner's own choices about a listed course - whether it is ticked, the colour that identifies it, its short name and its symbol. A preference about what is shown; it is neither an observation of the learner nor evidence, and it changes nothing that is recorded. At least one field.",
    "minProperties": 1,
    "properties": {
      "colour": {
        "description": "The identifier colour the learner chose for the course. An identifier, never a status.",
        "enum": [
          "slate",
          "teal",
          "plum",
          "ochre",
          "moss",
          "rose"
        ],
        "type": "string"
      },
      "selected": {
        "description": "Whether the course is ticked (shown). Courses are ticked by default.",
        "type": "boolean"
      },
      "shortName": {
        "description": "The learner's own short name for the course (1-40 characters, not blank); null resets to the default derived from the title.",
        "maxLength": 40,
        "minLength": 1,
        "type": [
          "string",
          "null"
        ]
      },
      "symbol": {
        "description": "The learner's symbol override; null resets to the automatic symbol. An identifier, never a status.",
        "enum": [
          "medicine",
          "economics",
          "languages",
          "education",
          "sociology",
          "law",
          "history",
          "politics",
          "life-sciences",
          "agriculture",
          "psychology",
          "area-studies",
          "computer-science",
          "chemistry",
          "philosophy",
          "mathematics",
          "physics",
          "religion",
          "geography",
          "earth",
          "communication",
          "arts",
          "statistics",
          "calculus",
          "linear-algebra",
          "discrete",
          "algebra",
          "geometry",
          "number-theory",
          "probability",
          "letter",
          null
        ],
        "type": [
          "string",
          "null"
        ]
      }
    },
    "title": "CourseUpdateRequest",
    "type": "object"
  },
  DeleteAllDataRequest: {
    "additionalProperties": false,
    "description": "Confirms the deletion the preview described. Two fields, and both are required for different reasons. The token proves this confirmation follows a preview the founder actually saw — it is minted by the preview, single-use, and expires server-side. The retyped phrase proves a person decided: FRONTEND_DESIGN §4.10 asks for it because the difference between 'the founder decided' and 'the founder clicked' is the whole of informed consent for an irreversible action, and after Phase 1 there is real evidence behind it that cannot be reconstructed. Neither substitutes for the other. A token alone can be replayed by any local process that can read one; a phrase alone confirms nothing about which preview it belongs to.",
    "properties": {
      "confirmationPhrase": {
        "description": "The exact phrase the screen asked for. Compared server-side so the check cannot be skipped by a client that decides to be helpful — the frontend is a convenience over this API, never the place a destructive guarantee lives.",
        "maxLength": 64,
        "minLength": 1,
        "type": "string"
      },
      "token": {
        "description": "The value from the preview's token. Checked server-side for existence, expiry, single use, and that it was issued for this data root.",
        "maxLength": 128,
        "minLength": 16,
        "type": "string"
      }
    },
    "required": [
      "token",
      "confirmationPhrase"
    ],
    "title": "DeleteAllDataRequest",
    "type": "object"
  },
  DeleteAllDataResponse: {
    "additionalProperties": false,
    "description": "The deletion receipt. BACKEND_DESIGN §14.4 and issue #6: it records what was removed, when, and what deliberately survived — without itself retaining the deleted content. So every field here is a table name, a category, or a timestamp; there is no row, no value, no digest of a value, and no filename. An audit that quoted what it destroyed would be a copy of the thing the founder asked to be rid of, sitting in the one file the deletion is guaranteed not to remove.",
    "properties": {
      "appVersion": {
        "description": "Which build performed it. A receipt that cannot say what produced it is not evidence of anything a year later.",
        "maxLength": 64,
        "type": "string"
      },
      "categories": {
        "items": {
          "additionalProperties": false,
          "properties": {
            "heldFiles": {
              "type": "boolean"
            },
            "kind": {
              "enum": [
                "database",
                "blobs",
                "imports",
                "backups",
                "logs",
                "deletion-receipts",
                "unexpected"
              ],
              "type": "string"
            },
            "path": {
              "maxLength": 256,
              "type": "string"
            }
          },
          "required": [
            "kind",
            "path",
            "heldFiles"
          ],
          "type": "object"
        },
        "maxItems": 64,
        "type": "array"
      },
      "deletedAt": {
        "format": "date-time",
        "type": "string"
      },
      "notReachableByLearnerTraversal": {
        "description": "Named for what it is, not for what we would prefer it to be. It was once called 'retained by design', which in a receipt reads as a justification rather than as the open gap it is — see issue #20.",
        "items": {
          "additionalProperties": false,
          "properties": {
            "reason": {
              "maxLength": 512,
              "type": "string"
            },
            "table": {
              "maxLength": 128,
              "type": "string"
            }
          },
          "required": [
            "table",
            "reason"
          ],
          "type": "object"
        },
        "maxItems": 256,
        "type": "array"
      },
      "outsideOurControl": {
        "items": {
          "maxLength": 512,
          "type": "string"
        },
        "maxItems": 32,
        "type": "array"
      },
      "receiptWritten": {
        "description": "Whether the copy of this receipt reached disk. Issue #24: the receipt is written after the point of no return, so a full disk or a permission change between the removal and the write leaves a deletion that succeeded and a record of it that does not exist. Without this field the only expressible answers were a 200 claiming a receipt that is not there and a 500 telling the founder a destructive operation failed when everything is gone — the contract was missing a state, and both available answers were false. Required rather than optional: a response that omits it would be indistinguishable from an older client's, and the one thing this field exists to prevent is a silent wrong assumption about where the record is. Why the deletion failed to record itself is deliberately not carried here — the causes are a filesystem error whose message names an absolute path, which §12 forbids on the wire, and they go to the local server log instead.",
        "type": "boolean"
      },
      "schemaVersion": {
        "description": "The migration tag the installation was at. Needed to interpret the table names below, which are otherwise a list of words whose meaning has changed since. Named schemaVersion rather than migration because the no-magnitude rule matches any field name containing \"ratio\", and mig-ratio-n does — a false positive, but renaming the field is the cheap side of that trade and the guard should stay blunt.",
        "maxLength": 128,
        "type": "string"
      },
      "tablesEmptied": {
        "description": "Which tables held data that this deletion removed, read before the removal. Names only — never a row, never a value, never a digest of a value, and (for now) never a count: see issue #24. An audit that quoted what it destroyed would be a copy of the thing the founder asked to be rid of, sitting in the one file the deletion is guaranteed not to remove.",
        "items": {
          "maxLength": 128,
          "type": "string"
        },
        "maxItems": 256,
        "type": "array"
      },
      "tablesReinitialised": {
        "items": {
          "maxLength": 128,
          "type": "string"
        },
        "maxItems": 256,
        "type": "array"
      }
    },
    "required": [
      "deletedAt",
      "appVersion",
      "schemaVersion",
      "receiptWritten",
      "categories",
      "tablesEmptied",
      "tablesReinitialised",
      "notReachableByLearnerTraversal",
      "outsideOurControl"
    ],
    "title": "DeleteAllDataResponse",
    "type": "object"
  },
  DeletionPreviewResponse: {
    "additionalProperties": false,
    "description": "What a complete deletion would remove, shown before anything is removed. BACKEND_DESIGN §14.4 and FRONTEND_DESIGN §4.10: the founder sees every app-controlled category, the tables that would be reinitialised and which of them hold anything, what a per-learner traversal could not reach and why (issue #20), and what DaVinci cannot delete at all. Names and presence only — never a row, never a value, never a filename, and for now never a count (issue #24). A preview that echoed content would put the learner's own data on screen at the moment they asked for it to be gone. The token is single-use, expires server-side, and is what the confirmation redeems: BACKEND_DESIGN §8 keeps the expiry on the server so the dialog is never time-limited on the client.",
    "properties": {
      "categories": {
        "description": "Every app-controlled directory, whether present or not. Absent categories are listed too: 'backups: not present' is information, and silently omitting it would leave the founder unable to tell 'nothing to remove' from 'not looked at'.",
        "items": {
          "additionalProperties": false,
          "properties": {
            "holdsFiles": {
              "description": "Whether the category holds anything at all. Deliberately a boolean and not a count: schema-rules.test.ts forbids a numeric type in any schema, and that ban is not negotiable here — see issue #24, which asks whether a deletion preview is the one place a count is legitimate. Until it is answered, the founder is told what exists rather than how much of it, which is the honest side to err on.",
              "type": "boolean"
            },
            "kind": {
              "enum": [
                "database",
                "blobs",
                "imports",
                "backups",
                "logs",
                "deletion-receipts",
                "unexpected"
              ],
              "type": "string"
            },
            "path": {
              "description": "Relative to the data root, so nothing here is an absolute path a component could echo into a log or a screenshot.",
              "maxLength": 256,
              "type": "string"
            },
            "present": {
              "type": "boolean"
            }
          },
          "required": [
            "kind",
            "path",
            "present",
            "holdsFiles"
          ],
          "type": "object"
        },
        "maxItems": 64,
        "type": "array"
      },
      "confirmationPhrase": {
        "description": "The exact phrase the confirmation must echo back. Dictated by the server rather than hardcoded in the screen, for the same reason the token is: the guarantee has to live where it cannot be edited out. A frontend that chose its own phrase could choose an empty one, and the retyping is what separates \"the founder decided\" from \"the founder clicked\" for an action nothing can undo. It carries no digits — the no-magnitude rule forbids a digit-admitting pattern, and a phrase to retype has no need of one.",
        "maxLength": 64,
        "minLength": 1,
        "type": "string"
      },
      "dataRoot": {
        "description": "The absolute path of the data root. This is the one place a filesystem path deliberately crosses the API boundary, and it is worth stating why the rule elsewhere is the opposite. Error envelopes never carry a path because they are reachable by provoking a failure, and the path contains the founder's Windows username. This response is not: it is a successful answer on an authenticated session that only the launch secret can establish, and §4.10 requires the screen to explain where the data actually is — a deletion screen that cannot say what it is about to delete from is not informed consent.",
        "maxLength": 512,
        "minLength": 1,
        "type": "string"
      },
      "learnerScopedTables": {
        "description": "Enumerated from the live schema rather than from a list in code, so a table added in a later phase appears here without an edit.",
        "items": {
          "maxLength": 128,
          "type": "string"
        },
        "maxItems": 256,
        "type": "array"
      },
      "outsideOurControl": {
        "description": "What DaVinci cannot delete — exports moved elsewhere, OS-level backups, copies on other media. §14.4 requires saying so plainly, because a deletion screen that stays silent about them implies a completeness it does not have.",
        "items": {
          "maxLength": 512,
          "type": "string"
        },
        "maxItems": 32,
        "type": "array"
      },
      "retainedByDesign": {
        "description": "Tables a per-learner traversal would not reach, each with the reason. Issue #20 owns closing this; until it does, the honest thing is to show it rather than let the founder infer that everything was reachable.",
        "items": {
          "additionalProperties": false,
          "properties": {
            "reason": {
              "maxLength": 512,
              "type": "string"
            },
            "table": {
              "maxLength": 128,
              "type": "string"
            }
          },
          "required": [
            "table",
            "reason"
          ],
          "type": "object"
        },
        "maxItems": 256,
        "type": "array"
      },
      "tablesHoldingData": {
        "description": "Which of those tables actually hold something, read before anything is removed — afterwards there is nothing left to look at. Names only, no row counts: the no-magnitude rule forbids a numeric type in any schema, and issue #24 carries the question of whether this preview is the one place that should change. The distinction the founder needs to make the decision is present either way: which of their tables have data in them.",
        "items": {
          "maxLength": 128,
          "type": "string"
        },
        "maxItems": 256,
        "type": "array"
      },
      "token": {
        "additionalProperties": false,
        "description": "Single-use, and expires server-side. The client shows the deadline but never enforces it: §8 forbids a client-side time limit on a destructive confirmation, because a dialog that vanishes mid-decision is a dialog that decides for you.",
        "properties": {
          "expiresAt": {
            "format": "date-time",
            "type": "string"
          },
          "value": {
            "maxLength": 128,
            "minLength": 16,
            "type": "string"
          }
        },
        "required": [
          "value",
          "expiresAt"
        ],
        "type": "object"
      }
    },
    "required": [
      "dataRoot",
      "categories",
      "learnerScopedTables",
      "tablesHoldingData",
      "retainedByDesign",
      "outsideOurControl",
      "confirmationPhrase",
      "token"
    ],
    "title": "DeletionPreviewResponse",
    "type": "object"
  },
  ErrorResponse: {
    "additionalProperties": false,
    "description": "The one error shape the API ever returns (BACKEND_DESIGN §12: errors share code, message, details?, requestId). It is here rather than in server code because it is the single response shape every endpoint can produce, and it was the only one outside the guarantee P0.2 exists for — that a shape the server returns and a shape the client expects cannot drift apart silently (issue #14). The HTTP status is deliberately absent: it is already on the response, and two sources for one fact is the drift this pipeline exists to prevent. There is no field here for the underlying cause, and that is structural — the data root is %LOCALAPPDATA%\\davinci and contains the founder's Windows username, so anything in the browser that can provoke an error and read this body would learn it. additionalProperties: false is what makes that a property of the contract rather than of review.",
    "properties": {
      "code": {
        "description": "The closed set of failure names the API can return. A new failure mode gets a name here or reuses one; it can never arrive as free text. The server's ERROR_CODES table derives its key set from this enum, so a code in one and not the other fails typecheck rather than dead-ending a client branch that silently stops being taken. Three of these are 409s, and they are separate names rather than one because the repair differs and a screen that cannot tell them apart has to guess: confirmation_expired means the deletion token is unknown, already spent, expired, or issued for another data root, and the fix is to run the preview again; confirmation_phrase_mismatch means the retyped phrase did not match, the token is still live, and the fix is to retype it; export_ticket_invalid means the archive behind the ticket is gone. Telling a founder to start over when they need to fix one word is how a destructive confirmation becomes something people click through. idempotency_key_required and idempotency_key_reused are issue #12's pair, raised by the Idempotency-Key guard ahead of any route match on a governed mutation: the first means the header was absent or did not match the required shape, and the fix is to send one; the second means the key was already used for a request with a different body, which is a client bug — never a silent replay of the first response, because the client has to be told rather than guess which of two requests actually happened. session_already_ended and session_item_resolved are issue #62's pair, and they are two names for one reason the three 409s above are: migration 0002's activity_attempts_need_an_open_container refuses a resolved item and an ended session identically, so a single code would leave the client unable to say which container was closed — and the repair differs, since one means move to the next item and the other means start a new session. session_already_ended is also what a second POST /sessions/{id}/complete gets, from study_sessions_end_once. assistance_exhausted is issue #63's own: assistance_events.granted_level tops out at 5 (assistance_events_granted_level_on_ladder), and a further POST /attempts/{id}/assistance once rung 5 has already been granted has no next legal rung to compute — a well-formed request the ladder's own state refuses, the same shape as the other 409s here rather than a validation failure or a missing resource. payload_too_large is issue #141's own, and it belongs to no single endpoint: POST /imports sets its own bodyLimit for application/pdf, distinct from and larger than the BODY_LIMIT_BYTES every JSON endpoint shares, but the code answers BOTH limits and therefore reaches callers of every mutating route. It is a 413 rather than the generic validation_failed a malformed JSON body gets, because the fix is to send less rather than to fix a request shape. Framed here as import-only when it was added, corrected 2026-09-05 in the same pass that corrected the server's own message for naming a file and an import that the request need not contain. invalid_credentials, signup_refused, username_taken and rate_limited are issue #371's (learner accounts). invalid_credentials is the one answer POST /auth/login gives for every failure — no such account, wrong password, a disabled account, a locked-out username — because telling them apart would tell a guesser which accounts exist. signup_refused is the same discipline for POST /auth/signup: an invite that is unknown, expired or already used gets this one code. username_taken is the only sign-up refusal that names its cause, and it is reachable only by someone holding a valid invite; the invite is not consumed. rate_limited is the global cap on login or sign-up attempts, not a per-account lock (a locked account answers invalid_credentials). founder_only is the refusal of an operation that acts on the whole installation (export, deletion preview, delete-all-data, export download) when the session belongs to anyone but the founder learner. consent_required, consent_statement_outdated and training_terms_unverified are issue #373's. consent_required (403) is the refusal of any route that would store a learner's work when that learner's feedback consent is not granted under the current statement; the repair is to open the consent screen, and it is raised before the request body is read. consent_statement_outdated (409) answers POST /consent/decisions when the decision quotes a statement version other than the current one; nothing is recorded and the repair is to fetch the statement again. training_terms_unverified (409) answers a improvement grant while any provider's output-use terms are not yet checked and recorded; a withdrawal is never refused. founder_deletion_refused and import_provenance_deletion_refused are the two named refusals of a learner deleting their own account (#373), both 409 because the request is well-formed and the state refuses it, and both raised before anything is touched. founder_deletion_refused: the founder cannot be deleted this way, because the founder is the oldest learner row and deleting it would silently make a classmate the founder; use the whole-installation deletion. import_provenance_deletion_refused: the learner owns import records that published course material cites, and erasing them would change what other learners' evidence can trace (invariant 3). budget_paused (503) is issue #375's: a model-backed check was refused before any call was made because the semester model budget has been reached (a per-learner cap, the global cap, or no budget period in force). It is not the learner's fault and nothing they did can fix it, so it is not a 4xx; retrying later does not help until the founder changes the budget. The fixed message names no percentage, no remaining amount and no dollar figure, because none of those is the learner's to see. upload_limit_exceeded, reader_unavailable, transcription_not_ready and transcription_unresolved are issue #376's (the handwritten-work reader). upload_limit_exceeded (413) answers POST /homework when the file is over the page limit or has a page whose rendered size is over the pixel limit (#251): the whole upload is refused and nothing is stored, never truncated, and the repair is to send fewer or smaller pages. reader_unavailable (503) answers POST /homework while the handwriting readers are not yet pinned for use (R2, #358): no byte of the upload is read, the request is refused before the body is buffered, and nothing the learner did can fix it. transcription_not_ready (409) answers a correction or confirmation of a page whose two readings are not both in yet, or a region the page does not have: the repair is to wait and read the transcription again. transcription_unresolved (409) answers POST /homework/pages/{pageId}/confirm when a region still lacks the learner's own text, and a correction that asks to confirm as read a region whose readers disagree: a disagreement is resolved by the learner's text, never by picking a reader, so the repair is to supply the text. transcription_changed (409) answers POST /homework/pages/{pageId}/confirm when the digest the learner confirmed is not the server's current one: the page changed after it was shown, nothing is recorded, and the repair is to read the transcription again and confirm what is now on screen. homework_exercise_mismatch (409) is #437's: it answers POST /courses/{courseRef}/exercises/{exerciseId}/homework when the learner already uploaded these exact bytes for a different exercise (or for none, before exercises were named); one upload per file means the file cannot be filed under a second exercise, nothing is stored and no read is started, and the repair is to pick the exercise it was first uploaded for or send a different file. confidence_after_verdict (409) is #438's: it answers POST /homework/{id}/confidence when a check run already exists for the upload; confidence is asked only before the first verdict, nothing is recorded, and there is nothing for the learner to repair. dispute_not_refuted, dispute_not_shown and dispute_already_filed (all 409) are #438 slice 3's, and answer POST /homework/{id}/claims/{claimId}/dispute: the line's verdict is not a refutation (there is nothing to dispute); the learner has not been shown the refutation (a verdict is disputed only after it has been seen); or a dispute has already been filed for the line. Nothing is recorded; there is nothing for the learner to repair. homework_not_checkable and rung_unavailable are #406's (the homework check run, slice 2). homework_not_checkable (409) answers POST /homework/{id}/check when the upload cannot be checked as it stands: a page is not yet confirmed or still has a region open, a page was never rendered, or the upload names no course exercise whose context the server can verify. Nothing is checked and nothing is recorded; the repair is to confirm every page (or, for a missing course context, nothing the learner can do). check_scope_not_ready (409) is #646's and also answers POST /homework/{id}/check: the model call that decides which lines the check can use has not answered yet (it failed or named a line that was never sent), so nothing can be concluded. Nothing is checked and nothing is recorded (no run, verdict, rung or status); DaVinci retries on its own, and there is nothing for the learner to repair. rung_unavailable (409) answers POST /homework/claims/{claimId}/rungs when the rung cannot be recorded for that line: the line is not a certified refutation, a counterexample was asked of a refutation that is not a certified counterexample, the solution is closed before the due date or while the due date is unknown, or no solution exists to show. Nothing is recorded and nothing is shown. solution_unavailable (503) is #586's and also answers POST /homework/claims/{claimId}/rungs: the solution rung is open for that line but DaVinci could not write its worked solution (the model, the budget, the learner's consent or the question sheet is unavailable). Nothing is recorded as delivered and nothing is shown, the message names no cause, and the learner may try again later. moodle_token_malformed (400), moodle_token_rejected (409) and moodle_unreachable (502) are #497's, and answer POST /moodle/connection: the pasted text is not shaped like a Moodle token; Moodle answered but did not accept the token; Moodle did not answer. Nothing is stored in any of the three, and none of them echoes the token.",
        "enum": [
          "forbidden_host",
          "forbidden_origin",
          "unauthenticated",
          "invalid_launch_secret",
          "invalid_csrf",
          "unsupported_media_type",
          "validation_failed",
          "not_found",
          "not_initialised",
          "confirmation_expired",
          "confirmation_phrase_mismatch",
          "export_ticket_invalid",
          "idempotency_key_required",
          "idempotency_key_reused",
          "session_already_ended",
          "session_item_resolved",
          "assistance_exhausted",
          "payload_too_large",
          "invalid_credentials",
          "signup_refused",
          "username_taken",
          "rate_limited",
          "founder_only",
          "consent_required",
          "consent_statement_outdated",
          "training_terms_unverified",
          "founder_deletion_refused",
          "import_provenance_deletion_refused",
          "budget_paused",
          "upload_limit_exceeded",
          "reader_unavailable",
          "homework_exercise_mismatch",
          "transcription_not_ready",
          "transcription_unresolved",
          "transcription_changed",
          "line_has_a_refutation",
          "homework_not_checkable",
          "check_scope_not_ready",
          "confidence_after_verdict",
          "dispute_not_refuted",
          "dispute_not_shown",
          "dispute_already_filed",
          "rung_unavailable",
          "solution_unavailable",
          "moodle_token_malformed",
          "moodle_token_rejected",
          "moodle_unreachable",
          "internal_error"
        ],
        "type": "string"
      },
      "details": {
        "description": "Schema-derived validator output only, and never a free-text channel. validationDetails drops Ajv's params because it echoes attacker-controlled property names straight back; this array exists to carry which field failed and how the schema says it failed, not why the server thinks so. Be exact about what these bounds achieve: they bound length, and nothing expressible in JSON Schema can say 'not a filesystem path'. That line is held by the ValidationDetails branded type in the server, which makes validationDetails the only function able to produce a value sendError will accept here.",
        "items": {
          "maxLength": 200,
          "minLength": 1,
          "type": "string"
        },
        "maxItems": 10,
        "type": "array"
      },
      "message": {
        "description": "A fixed sentence looked up from the code, never the thrown error's own message. The client must not render it: the UI owes the founder a sentence in its own words, and echoing a server string moves that responsibility across the boundary.",
        "maxLength": 400,
        "minLength": 1,
        "type": "string"
      },
      "requestId": {
        "description": "Correlates this response with the local server log, which is where the cause actually lives. Opaque to the client — it is quoted back to the founder, never parsed. maxLength is load-bearing rather than tidy: this is the one server-supplied string the UI renders verbatim, and it renders inside the alert region someone reads at the moment something has already gone wrong. The real server cannot emit a hostile one — genReqId is eight random bytes and requestIdHeader is off — but this parser exists for the case its own comment names, a responder that is not DaVinci holding DaVinci's origin, because the loopback interface is shared with every other process on the machine. Unbounded, that field is a sentence someone else gets to write into a role=alert region. 64 characters holds the 16-hex id with room to change how ids are minted.",
        "maxLength": 64,
        "minLength": 1,
        "type": "string"
      }
    },
    "required": [
      "code",
      "message",
      "requestId"
    ],
    "title": "ErrorResponse",
    "type": "object"
  },
  ExamPrepAssessmentsResponse: {
    "additionalProperties": false,
    "description": "Answer of GET /exam-prep/assessments (#440): the assessments the caller can prepare for - the learner-declared ones that are upcoming (Moodle-sourced assessments do not exist in the data yet) - each with the id of the plan already made for it, or null. A list of declarations and of plans, not of conclusions about what the learner knows.",
    "properties": {
      "assessments": {
        "description": "Soonest first. Never filtered by the focus course.",
        "items": {
          "additionalProperties": false,
          "description": "One assessment and its plan.",
          "properties": {
            "assessment": {
              "$ref": "https://davinci.local/schemas/assessment-response.json"
            },
            "planId": {
              "description": "The id of the plan already made for this assessment, or null when there is none yet. At most one plan exists per assessment.",
              "type": [
                "string",
                "null"
              ]
            }
          },
          "required": [
            "assessment",
            "planId"
          ],
          "type": "object"
        },
        "type": "array"
      }
    },
    "required": [
      "assessments"
    ],
    "title": "ExamPrepAssessmentsResponse",
    "type": "object"
  },
  ExamPrepAttemptRequest: {
    "additionalProperties": false,
    "description": "Body of POST /exam-prep/items/{itemId}/attempts (#440): the learner's answer, as typed in the kernel's linear syntax: x = 3, y = -1. Untrusted text, stored as data. There is no verdict, independence or status field: the server decides all three.",
    "properties": {
      "answerText": {
        "description": "The answer as typed, left-to-right. 1 to 500 characters.",
        "maxLength": 500,
        "minLength": 1,
        "type": "string"
      }
    },
    "required": [
      "answerText"
    ],
    "title": "ExamPrepAttemptRequest",
    "type": "object"
  },
  ExamPrepAttemptResponse: {
    "additionalProperties": false,
    "description": "Answer of POST /exam-prep/items/{itemId}/attempts (#440): the kernel's verdict, and the item as it now stands. An unparseable answer is a 200 with result unparseable and nothing graded: the learner is asked again.",
    "properties": {
      "item": {
        "$ref": "https://davinci.local/schemas/exam-prep-item.json"
      },
      "result": {
        "description": "unparseable: re-prompt, nothing was graded or recorded as wrong. correct or incorrect: the exact verdict of the certificate kernel.",
        "enum": [
          "unparseable",
          "correct",
          "incorrect"
        ],
        "type": "string"
      }
    },
    "required": [
      "result",
      "item"
    ],
    "title": "ExamPrepAttemptResponse",
    "type": "object"
  },
  ExamPrepItem: {
    "additionalProperties": false,
    "description": "One problem of an exam-prep session (#440, #362): a changed-number variant of the learner's own sheet problem, computed and graded by the certificate kernel with no model call, or a proof pointer back to the sheet. The answer is never in it until the learner says they do not know yet. Carries no is_independent, status, score or percentage: what the work counted as is its outcome, in words.",
    "properties": {
      "attempts": {
        "description": "Every answer the learner submitted on this item, oldest first. An unparseable one is listed as such: it was re-prompted and never marked wrong.",
        "items": {
          "additionalProperties": false,
          "description": "One submitted answer.",
          "properties": {
            "answerText": {
              "description": "What the learner typed (untrusted text, returned as data); null for \"I don't know yet\".",
              "type": [
                "string",
                "null"
              ]
            },
            "attemptId": {
              "description": "An identifier DaVinci minted.",
              "type": "string"
            },
            "incorrectReason": {
              "description": "For incorrect: what failed, from the kernel's refutation.",
              "enum": [
                "condition_false",
                "condition_undefined",
                "not_in_domain",
                "hypothesis_false",
                "conclusions_hold",
                null
              ],
              "type": [
                "string",
                "null"
              ]
            },
            "result": {
              "description": "unparseable: not an answer in a form DaVinci can check, never graded. correct and incorrect: the kernel's verdict. declared_unknown: the learner said they do not know yet.",
              "enum": [
                "unparseable",
                "correct",
                "incorrect",
                "declared_unknown"
              ],
              "type": "string"
            },
            "submittedAt": {
              "description": "When the answer was submitted.",
              "format": "date-time",
              "type": "string"
            },
            "unparseableReason": {
              "description": "For unparseable: why it could not be read.",
              "enum": [
                "empty",
                "not_an_assignment",
                "two_equals",
                "bad_variable_name",
                "unknown_variable",
                "variable_assigned_twice",
                "variable_unassigned",
                "value_not_exact",
                null
              ],
              "type": [
                "string",
                "null"
              ]
            }
          },
          "required": [
            "attemptId",
            "answerText",
            "result",
            "unparseableReason",
            "incorrectReason",
            "submittedAt"
          ],
          "type": "object"
        },
        "type": "array"
      },
      "help": {
        "additionalProperties": false,
        "description": "Help on this item. A hint costs independence (it supplies information toward the answer); the reveal is answer-revealing. Both are recorded. Help on the item is prior to every later answer on it; help on another item of the topic is prior to every later answer on the topic while this session is open and for 24 hours after the later of the delivery and the finish (topic-status@3 rule 6b, using independence-scope@1's 24-hour window); such help reaches the independence decision as information toward the answer, so it never excuses a wrong answer (rule 6d). Reading an item re-shows help and writes nothing, so its text is returned only while the session is open, and a re-show counts because the open session keeps it counting; the flags below always say that it was given.",
        "properties": {
          "canAskForHint": {
            "description": "May the learner ask for the hint now? Only after a graded answer, once, while the item and session are open.",
            "type": "boolean"
          },
          "canDeclareUnknown": {
            "description": "May the learner say \"I don't know yet\" now? Any time the item and session are open.",
            "type": "boolean"
          },
          "hint": {
            "additionalProperties": false,
            "description": "The hint shown, or null: before it is given, once the session is finished, and once it is more than 24 hours old. The values lie within the bound; it does not name the answer.",
            "properties": {
              "bound": {
                "description": "A decimal string: every value in the answer lies between minus this and this.",
                "type": "string"
              },
              "code": {
                "const": "values_within",
                "description": "Which hint this is.",
                "type": "string"
              }
            },
            "required": [
              "code",
              "bound"
            ],
            "type": [
              "object",
              "null"
            ]
          },
          "hintShown": {
            "description": "A hint was given on this item, whether or not its text is still returned.",
            "type": "boolean"
          },
          "reveal": {
            "additionalProperties": false,
            "description": "The computed answer, shown after \"I don't know yet\"; null before, once the session is finished, and once it is more than 24 hours old (the answer is not re-read outside the window that priced it).",
            "properties": {
              "answerText": {
                "description": "The kernel's own answer, in the form an answer is typed: x = 3, y = -1.",
                "type": "string"
              },
              "code": {
                "const": "answer",
                "description": "Which reveal this is.",
                "type": "string"
              }
            },
            "required": [
              "code",
              "answerText"
            ],
            "type": [
              "object",
              "null"
            ]
          },
          "revealShown": {
            "description": "The answer was shown on this item (by \"I don't know yet\" or by the learner's own data export), whether or not its text is still returned.",
            "type": "boolean"
          }
        },
        "required": [
          "hint",
          "reveal",
          "hintShown",
          "revealShown",
          "canAskForHint",
          "canDeclareUnknown"
        ],
        "type": "object"
      },
      "itemId": {
        "description": "An identifier DaVinci minted.",
        "type": "string"
      },
      "kind": {
        "description": "variant: a problem DaVinci made and can check. proof_pointer: a proof or derivation DaVinci cannot check; it points back to the sheet.",
        "enum": [
          "variant",
          "proof_pointer"
        ],
        "type": "string"
      },
      "outcome": {
        "description": "What the work counted as once the item is closed: on_your_own, with_help (a hint came first), not_yet, or proof_pointer. null while the item is open. Derived by the server.",
        "enum": [
          "on_your_own",
          "with_help",
          "not_yet",
          "proof_pointer",
          null
        ],
        "type": [
          "string",
          "null"
        ]
      },
      "pointer": {
        "additionalProperties": false,
        "description": "For a proof pointer: where to look; null for a variant.",
        "properties": {
          "question": {
            "description": "The question label (untrusted text, returned as data), or null.",
            "type": [
              "string",
              "null"
            ]
          },
          "sheet": {
            "description": "The sheet's title (untrusted text, returned as data), or null.",
            "type": [
              "string",
              "null"
            ]
          },
          "why": {
            "description": "A plain sentence saying DaVinci cannot check a proof here.",
            "type": "string"
          }
        },
        "required": [
          "sheet",
          "question",
          "why"
        ],
        "type": [
          "object",
          "null"
        ]
      },
      "reason": {
        "additionalProperties": false,
        "description": "Why this problem is here (invariant 3 on the screen): \"From Sheet 3, Q2: a miss you were sure of.\"",
        "properties": {
          "question": {
            "description": "The question label (untrusted text, returned as data), or null.",
            "type": [
              "string",
              "null"
            ]
          },
          "returning": {
            "description": "Why the topic is back: after a not yet, or as the check that can earn retained. null for a first serving.",
            "enum": [
              "not_yet",
              "retention_check",
              null
            ],
            "type": [
              "string",
              "null"
            ]
          },
          "sheet": {
            "description": "The sheet's title (untrusted text, returned as data), or null.",
            "type": [
              "string",
              "null"
            ]
          },
          "tier": {
            "description": "What the learner's own homework showed about the topic.",
            "enum": [
              "sure_then_wrong",
              "wrong",
              "fixed_with_help",
              "not_tested_in_app"
            ],
            "type": "string"
          }
        },
        "required": [
          "tier",
          "sheet",
          "question",
          "returning"
        ],
        "type": "object"
      },
      "state": {
        "description": "open: can be answered. solved: answered correctly. revealed: the learner said they do not know yet and was shown the answer; the item is closed. pointer: a proof pointer.",
        "enum": [
          "open",
          "solved",
          "revealed",
          "pointer"
        ],
        "type": "string"
      },
      "stem": {
        "additionalProperties": false,
        "description": "The problem as data, for a variant; null for a proof pointer.",
        "properties": {
          "answerTemplate": {
            "description": "The shape of an answer, the variables with the value left blank: \"x = , y = \". Answers are typed left-to-right as x = 3, y = -1.",
            "type": "string"
          },
          "claim": {
            "description": "A witness: the conditions to satisfy. A counterexample: the conclusions to break.",
            "items": {
              "additionalProperties": false,
              "description": "One condition of the stem.",
              "properties": {
                "tex": {
                  "description": "The condition as LaTeX, for KaTeX. Maths is left-to-right in a right-to-left screen.",
                  "type": "string"
                },
                "text": {
                  "description": "The same condition in the linear syntax the answer is typed in, for assistive technology and copying.",
                  "type": "string"
                }
              },
              "required": [
                "tex",
                "text"
              ],
              "type": "object"
            },
            "type": "array"
          },
          "domain": {
            "description": "Where the variables range: N0 the naturals from 0, N1 the naturals from 1, Z the integers, Q the rationals.",
            "enum": [
              "N0",
              "N1",
              "Z",
              "Q"
            ],
            "type": "string"
          },
          "given": {
            "description": "The hypotheses. Empty for a witness.",
            "items": {
              "additionalProperties": false,
              "description": "One condition of the stem.",
              "properties": {
                "tex": {
                  "description": "The condition as LaTeX, for KaTeX. Maths is left-to-right in a right-to-left screen.",
                  "type": "string"
                },
                "text": {
                  "description": "The same condition in the linear syntax the answer is typed in, for assistive technology and copying.",
                  "type": "string"
                }
              },
              "required": [
                "tex",
                "text"
              ],
              "type": "object"
            },
            "type": "array"
          },
          "quantifierTex": {
            "description": "The quantifier and domain as LaTeX, for example \\exists x, y \\in \\mathbb{Z}.",
            "type": "string"
          },
          "statementTex": {
            "description": "The whole statement on one line as LaTeX.",
            "type": "string"
          },
          "taskKind": {
            "description": "witness: find values that make every condition in claim true. counterexample: the claim \"given implies claim\" is false; find values that satisfy every given condition and break claim.",
            "enum": [
              "witness",
              "counterexample"
            ],
            "type": "string"
          },
          "variables": {
            "description": "The variables an answer assigns, each exactly once, in order.",
            "items": {
              "type": "string"
            },
            "type": "array"
          }
        },
        "required": [
          "taskKind",
          "domain",
          "variables",
          "quantifierTex",
          "given",
          "claim",
          "statementTex",
          "answerTemplate"
        ],
        "type": [
          "object",
          "null"
        ]
      },
      "topic": {
        "additionalProperties": false,
        "description": "The topic it is practising.",
        "properties": {
          "sectionName": {
            "description": "The section's name as Moodle gave it (untrusted text, returned as data), or null.",
            "type": [
              "string",
              "null"
            ]
          },
          "topicKey": {
            "description": "An identifier, moodleCourseId:sectionNumber.",
            "type": "string"
          }
        },
        "required": [
          "topicKey",
          "sectionName"
        ],
        "type": "object"
      }
    },
    "required": [
      "itemId",
      "topic",
      "kind",
      "reason",
      "stem",
      "pointer",
      "state",
      "outcome",
      "attempts",
      "help"
    ],
    "title": "ExamPrepItem",
    "type": "object"
  },
  ExamPrepPlanCreateRequest: {
    "additionalProperties": false,
    "description": "Body of POST /exam-prep/plans (#440): make the plan for one upcoming assessment of the caller's. Asking again for an assessment that has a plan returns that plan. The learner is the session's, never a field here.",
    "properties": {
      "assessmentId": {
        "description": "The id of one of the caller's own upcoming assessments (GET /exam-prep/assessments).",
        "maxLength": 64,
        "minLength": 1,
        "type": "string"
      }
    },
    "required": [
      "assessmentId"
    ],
    "title": "ExamPrepPlanCreateRequest",
    "type": "object"
  },
  ExamPrepPlanResponse: {
    "additionalProperties": false,
    "description": "The plan for one assessment (#440, UX_EXPERIENCE 4.7b): which topics to work on, in the learner's order, and about how many sessions of how long. An inference from the weak-point ranking, naming the policy versions it was made under (invariant 3). It says what to practise, never what the learner knows: no status, score or percentage. Nothing is invented: a topic DaVinci has no problem for is listed in withoutProblems, and a plan with no problem at all has status no_checkable_problems.",
    "properties": {
      "assessment": {
        "additionalProperties": false,
        "description": "The assessment the plan is for.",
        "properties": {
          "assessmentId": {
            "description": "The assessment's id.",
            "type": "string"
          },
          "courseRef": {
            "description": "Moodle's course id, as a decimal string.",
            "type": "string"
          },
          "date": {
            "description": "The day of the assessment, YYYY-MM-DD.",
            "format": "date",
            "type": "string"
          },
          "kind": {
            "description": "What sort of assessment it is.",
            "enum": [
              "quiz",
              "midterm",
              "exam",
              "other"
            ],
            "type": "string"
          },
          "title": {
            "description": "The learner's own title (untrusted text, returned as data).",
            "type": "string"
          }
        },
        "required": [
          "assessmentId",
          "courseRef",
          "title",
          "kind",
          "date"
        ],
        "type": "object"
      },
      "daysLeft": {
        "description": "Whole days from today to the assessment; negative once it has passed.",
        "type": "integer"
      },
      "planId": {
        "description": "An identifier DaVinci minted.",
        "type": "string"
      },
      "policyVersions": {
        "additionalProperties": false,
        "description": "The versions the plan was made under (invariant 3).",
        "properties": {
          "plan": {
            "description": "The plan policy.",
            "type": "string"
          },
          "ranking": {
            "description": "The weak-point ranking policy the topics came from.",
            "type": "string"
          },
          "session": {
            "description": "The policy sessions are built under.",
            "type": "string"
          }
        },
        "required": [
          "plan",
          "ranking",
          "session"
        ],
        "type": "object"
      },
      "sessions": {
        "additionalProperties": false,
        "description": "The shape of the work, as data: about this many sessions of this length.",
        "properties": {
          "aboutCount": {
            "description": "About how many sessions the plan needs, sized to the topics and the days left.",
            "type": "integer"
          },
          "done": {
            "description": "How many of the plan's sessions the learner has finished.",
            "type": "integer"
          },
          "itemsPerSession": {
            "description": "How many problems a full session holds.",
            "type": "integer"
          },
          "minutes": {
            "description": "The length of a session in minutes.",
            "type": "integer"
          },
          "openSessionId": {
            "description": "The id of the session started and not yet finished, or null.",
            "type": [
              "string",
              "null"
            ]
          }
        },
        "required": [
          "aboutCount",
          "minutes",
          "itemsPerSession",
          "done",
          "openSessionId"
        ],
        "type": "object"
      },
      "status": {
        "description": "ready: there is work. no_checkable_problems: nothing in scope can be turned into a problem DaVinci can check; say so plainly. assessment_passed: the date is before today.",
        "enum": [
          "ready",
          "no_checkable_problems",
          "assessment_passed"
        ],
        "type": "string"
      },
      "topics": {
        "description": "The topics to work on, in the order that wins (the learner's newest order, else strongest first). Empty unless status is ready.",
        "items": {
          "additionalProperties": false,
          "description": "One topic of the plan. The tier is why the topic is here (what the ranking found in the learner's own checked homework), in words, never a score.",
          "properties": {
            "because": {
              "description": "The sheets and questions of the learner's own homework that put the topic here. Empty for a topic never tested in the app.",
              "items": {
                "additionalProperties": false,
                "description": "One of the learner's own sheet questions.",
                "properties": {
                  "question": {
                    "description": "The question label the learner confirmed on that sheet, for example Q2 (untrusted text, returned as data), or null.",
                    "type": [
                      "string",
                      "null"
                    ]
                  },
                  "sheet": {
                    "description": "The sheet's title as the course listed it (untrusted text, returned as data), or null.",
                    "type": [
                      "string",
                      "null"
                    ]
                  }
                },
                "required": [
                  "sheet",
                  "question"
                ],
                "type": "object"
              },
              "type": "array"
            },
            "sectionName": {
              "description": "The section's name as Moodle gave it (untrusted text, returned as data), or null.",
              "type": [
                "string",
                "null"
              ]
            },
            "tier": {
              "description": "Why the topic is on the plan, strongest first: a miss the learner was sure of, a miss, a miss fixed only with help, or not tested in the app.",
              "enum": [
                "sure_then_wrong",
                "wrong",
                "fixed_with_help",
                "not_tested_in_app"
              ],
              "type": "string"
            },
            "topicKey": {
              "description": "An identifier, moodleCourseId:sectionNumber.",
              "type": "string"
            }
          },
          "required": [
            "topicKey",
            "sectionName",
            "tier",
            "because"
          ],
          "type": "object"
        },
        "type": "array"
      },
      "withoutProblems": {
        "description": "Topics in the assessment's scope that DaVinci has no checkable problem for. Listed so the learner is told; never planned.",
        "items": {
          "additionalProperties": false,
          "description": "One topic of the plan. The tier is why the topic is here (what the ranking found in the learner's own checked homework), in words, never a score.",
          "properties": {
            "because": {
              "description": "The sheets and questions of the learner's own homework that put the topic here. Empty for a topic never tested in the app.",
              "items": {
                "additionalProperties": false,
                "description": "One of the learner's own sheet questions.",
                "properties": {
                  "question": {
                    "description": "The question label the learner confirmed on that sheet, for example Q2 (untrusted text, returned as data), or null.",
                    "type": [
                      "string",
                      "null"
                    ]
                  },
                  "sheet": {
                    "description": "The sheet's title as the course listed it (untrusted text, returned as data), or null.",
                    "type": [
                      "string",
                      "null"
                    ]
                  }
                },
                "required": [
                  "sheet",
                  "question"
                ],
                "type": "object"
              },
              "type": "array"
            },
            "sectionName": {
              "description": "The section's name as Moodle gave it (untrusted text, returned as data), or null.",
              "type": [
                "string",
                "null"
              ]
            },
            "tier": {
              "description": "Why the topic is on the plan, strongest first: a miss the learner was sure of, a miss, a miss fixed only with help, or not tested in the app.",
              "enum": [
                "sure_then_wrong",
                "wrong",
                "fixed_with_help",
                "not_tested_in_app"
              ],
              "type": "string"
            },
            "topicKey": {
              "description": "An identifier, moodleCourseId:sectionNumber.",
              "type": "string"
            }
          },
          "required": [
            "topicKey",
            "sectionName",
            "tier",
            "because"
          ],
          "type": "object"
        },
        "type": "array"
      }
    },
    "required": [
      "planId",
      "assessment",
      "status",
      "topics",
      "withoutProblems",
      "daysLeft",
      "sessions",
      "policyVersions"
    ],
    "title": "ExamPrepPlanResponse",
    "type": "object"
  },
  ExamPrepSessionResponse: {
    "additionalProperties": false,
    "description": "A session of problems (#440, UX_EXPERIENCE 4.7): a fixed number, topics mixed, weakest first, each with its reason. session is null exactly when unavailable says why: no session is made for a plan with nothing to practise, a passed assessment, or when every topic was solved on the learner's own less than three days ago.",
    "properties": {
      "session": {
        "additionalProperties": false,
        "description": "The session, or null when unavailable is set.",
        "properties": {
          "finished": {
            "description": "Whether the learner finished the session.",
            "type": "boolean"
          },
          "index": {
            "description": "1 for the plan's first session, then 2, and so on: an ordinal, not a score.",
            "type": "integer"
          },
          "items": {
            "description": "In the order to work them. Never two of one topic in a row while another has something left.",
            "items": {
              "$ref": "https://davinci.local/schemas/exam-prep-item.json"
            },
            "type": "array"
          },
          "planId": {
            "description": "The plan it belongs to.",
            "type": "string"
          },
          "sessionId": {
            "description": "An identifier DaVinci minted.",
            "type": "string"
          },
          "showDesirableDifficultyNotice": {
            "description": "True exactly once per learner, in the response that starts their first mixed session: show \"Mixed practice feels harder than doing one kind in a row. That is the point: it is what holds up in the exam.\" Always false on a read.",
            "type": "boolean"
          }
        },
        "required": [
          "sessionId",
          "planId",
          "index",
          "finished",
          "showDesirableDifficultyNotice",
          "items"
        ],
        "type": [
          "object",
          "null"
        ]
      },
      "unavailable": {
        "description": "Why there is no session, or null.",
        "enum": [
          "assessment_passed",
          "plan_has_no_problems",
          "nothing_due_yet",
          null
        ],
        "type": [
          "string",
          "null"
        ]
      }
    },
    "required": [
      "session",
      "unavailable"
    ],
    "title": "ExamPrepSessionResponse",
    "type": "object"
  },
  ExamPrepSessionSummaryResponse: {
    "additionalProperties": false,
    "description": "Answer of POST /exam-prep/sessions/{sessionId}/finish (#440, UX_EXPERIENCE 4.7): what became of each item, as a list - never a total, a score or a percentage. An item left unanswered is not yet. A not yet returns in a later session of the plan.",
    "properties": {
      "finished": {
        "const": true,
        "description": "Always true.",
        "type": "boolean"
      },
      "items": {
        "description": "One entry per item, in session order.",
        "items": {
          "additionalProperties": false,
          "description": "What became of one item.",
          "properties": {
            "itemId": {
              "description": "The item.",
              "type": "string"
            },
            "outcome": {
              "description": "on_your_own: solved with no help. with_help: solved after a hint. not_yet: not solved, or the answer was revealed. proof_pointer: a proof to do on paper from the sheet.",
              "enum": [
                "on_your_own",
                "with_help",
                "not_yet",
                "proof_pointer"
              ],
              "type": "string"
            },
            "question": {
              "description": "The question label (untrusted text, returned as data), or null.",
              "type": [
                "string",
                "null"
              ]
            },
            "sheet": {
              "description": "The sheet it came from (untrusted text, returned as data), or null.",
              "type": [
                "string",
                "null"
              ]
            },
            "topic": {
              "additionalProperties": false,
              "description": "Its topic.",
              "properties": {
                "sectionName": {
                  "description": "The section's name as Moodle gave it (untrusted text, returned as data), or null.",
                  "type": [
                    "string",
                    "null"
                  ]
                },
                "topicKey": {
                  "description": "An identifier, moodleCourseId:sectionNumber.",
                  "type": "string"
                }
              },
              "required": [
                "topicKey",
                "sectionName"
              ],
              "type": "object"
            }
          },
          "required": [
            "itemId",
            "topic",
            "sheet",
            "question",
            "outcome"
          ],
          "type": "object"
        },
        "type": "array"
      },
      "planId": {
        "description": "Its plan.",
        "type": "string"
      },
      "sessionId": {
        "description": "The finished session.",
        "type": "string"
      }
    },
    "required": [
      "sessionId",
      "planId",
      "finished",
      "items"
    ],
    "title": "ExamPrepSessionSummaryResponse",
    "type": "object"
  },
  ExamPrepTopicOrderRequest: {
    "additionalProperties": false,
    "description": "Body of POST /exam-prep/plans/{planId}/topic-order (#440): the learner's own order of the plan's topics. A declaration: the newest order wins, older ones are kept.",
    "properties": {
      "topicKeys": {
        "description": "Exactly the topic keys of the plan's topics (not the ones listed apart), each once, in the learner's order. Anything else is refused.",
        "items": {
          "maxLength": 40,
          "minLength": 1,
          "type": "string"
        },
        "minItems": 1,
        "type": "array",
        "uniqueItems": true
      }
    },
    "required": [
      "topicKeys"
    ],
    "title": "ExamPrepTopicOrderRequest",
    "type": "object"
  },
  ExportDownloadRequest: {
    "additionalProperties": false,
    "description": "Redeems the ticket POST /export minted, and receives the archive bytes. One required field and nothing else, which is the whole design: the caller cannot name a path, a table, a filter, or a destination, so there is no input here through which a request could widen what it receives or choose where it lands. What comes back is the archive the preview described, and the only thing the founder can then do with it is save it somewhere they picked (FRONTEND_DESIGN §9).",
    "properties": {
      "ticket": {
        "description": "The value from the export response's ticket. Checked server-side for existence, expiry and single use. It is not a path and never becomes one — it is a key into a map the server holds in memory, so a redeemed ticket cannot address anything the export did not already build.",
        "maxLength": 128,
        "minLength": 16,
        "type": "string"
      }
    },
    "required": [
      "ticket"
    ],
    "title": "ExportDownloadRequest",
    "type": "object"
  },
  ExportResponse: {
    "additionalProperties": false,
    "description": "The manifest of an archive that has already been built, and the ticket that fetches it. BACKEND_DESIGN §14.3 and FRONTEND_DESIGN §4.10: the founder sees what is in the export — and what is deliberately not — before any of it reaches the disk. Nothing here is a copy of the data; every field is a name, a kind, a digest, or a sentence explaining an omission. The archive itself is held in the server's memory until the ticket below is redeemed or expires, which is what makes §9's \"do not silently write exports to arbitrary paths\" structural rather than a promise: there is no path for the server to write to, and the only way the archive reaches the filesystem is the founder choosing where to save it.",
    "properties": {
      "appVersion": {
        "description": "Which build wrote the archive.",
        "maxLength": 64,
        "type": "string"
      },
      "createdAt": {
        "format": "date-time",
        "type": "string"
      },
      "entries": {
        "description": "Every file in the archive with its SHA-256, which is what makes the archive verifiable by something other than DaVinci: re-hash each entry and compare. No byte length — a size is a numeric type, and the digest is the part that answers whether the file is intact.",
        "items": {
          "additionalProperties": false,
          "properties": {
            "path": {
              "description": "Forward-slash-separated and relative to the archive root. A blob's own path is its digest, which is a fingerprint of content — but of content this founder is asking to be given a copy of, on their own machine, over a session only the launch secret can establish. That is a different question from whether a digest may appear in a deletion receipt, where the point is that nothing survives.",
              "maxLength": 512,
              "type": "string"
            },
            "sha256": {
              "description": "Hex, lower case. No pattern: the no-magnitude rule forbids a digit-admitting pattern in any schema, which a hex constraint would be, and the length bound plus the archive's own manifest carry the real check.",
              "maxLength": 64,
              "type": "string"
            }
          },
          "required": [
            "path",
            "sha256"
          ],
          "type": "object"
        },
        "maxItems": 4096,
        "type": "array"
      },
      "formatVersion": {
        "description": "The archive layout version. A restorer that does not recognise it refuses rather than guessing, so a reader needs to see it before trusting anything else here.",
        "maxLength": 16,
        "type": "string"
      },
      "omissions": {
        "description": "What this export deliberately does not contain, each with the reason. §14.3 requires omissions to be enumerated rather than inferred from absence: an archive missing a file because nobody wrote one and an archive missing a file because it was excluded look identical from outside.",
        "items": {
          "additionalProperties": false,
          "properties": {
            "reason": {
              "maxLength": 1024,
              "type": "string"
            },
            "what": {
              "maxLength": 256,
              "type": "string"
            }
          },
          "required": [
            "what",
            "reason"
          ],
          "type": "object"
        },
        "maxItems": 64,
        "type": "array"
      },
      "retainedByDesign": {
        "description": "Data this archive contains that a per-learner deletion would nonetheless not remove, with the reason. Shown on the export screen and not only the deletion screen, because it is the same gap seen from the other side — issue #20 owns closing it, and until then the honest thing is to say so in both places.",
        "items": {
          "additionalProperties": false,
          "properties": {
            "reason": {
              "maxLength": 512,
              "type": "string"
            },
            "table": {
              "maxLength": 128,
              "type": "string"
            }
          },
          "required": [
            "table",
            "reason"
          ],
          "type": "object"
        },
        "maxItems": 256,
        "type": "array"
      },
      "schemaVersion": {
        "description": "The migration tag the database was at. Named schemaVersion rather than migration because the no-magnitude rule matches any field name containing \"ratio\", and mig-ratio-n does — the same false positive delete-all-data-response.json renamed around, and the guard should stay blunt.",
        "maxLength": 128,
        "type": "string"
      },
      "suggestedFileName": {
        "description": "What to call the file when it is saved. Proposed by the server so the name carries the export's own timestamp, and it is only a proposal — the browser's save dialog is where the destination is actually chosen (§9).",
        "maxLength": 128,
        "minLength": 1,
        "type": "string"
      },
      "tables": {
        "description": "Every application table in the archive, with the classification invariant 2 requires: whether it holds what the learner did or what was concluded from it. Carried into this preview rather than left inside the archive, because a founder deciding whether to keep an export needs to know it distinguishes the two. Deliberately no row count — schema-rules.test.ts forbids a numeric type in any schema, and the distinction that matters here is what kind of record a table holds, not how much of it there is.",
        "items": {
          "additionalProperties": false,
          "properties": {
            "name": {
              "maxLength": 128,
              "type": "string"
            },
            "recordKind": {
              "description": "Invariant 2's distinction, stated per table. A reader can tell an observation from a conclusion drawn about it without knowing anything about this codebase. `derived_view` is the answer for a view, which is the one object that presents an observation and a conclusion in one shape in the database: invariant 2 forbids STORING them that way there, and a view has no INSERT and no INSTEAD OF trigger, so it never does. This export entry is the one place that same view's rows are deliberately written to disk in that joined shape — sanctioned separately, by BACKEND_DESIGN section 6.2's denormalized export record, precisely because an export is not the ledger.",
              "enum": [
                "observation",
                "inference",
                "reference",
                "operational",
                "derived_view"
              ],
              "type": "string"
            },
            "rows": {
              "description": "The archive entry holding this table's rows, so a reader need not guess the name.",
              "maxLength": 256,
              "type": "string"
            },
            "scope": {
              "description": "Whose rows these are. `derived` is a SQLite view: its scope is whatever its sources' scope is, because it stores nothing of its own IN THE DATABASE — this export entry is where its rows actually land once selected out.",
              "enum": [
                "learner_root",
                "learner_scoped",
                "shared",
                "scope_addressed",
                "derived"
              ],
              "type": "string"
            }
          },
          "required": [
            "name",
            "scope",
            "recordKind",
            "rows"
          ],
          "type": "object"
        },
        "maxItems": 256,
        "type": "array"
      },
      "ticket": {
        "additionalProperties": false,
        "description": "Redeem once, at POST /export/download, to receive the bytes. Single-use and expiring for the same reason the deletion token is: the archive is a complete copy of everything the installation holds, sitting in the memory of a server on an interface shared with every other process on the machine. A ticket that never expired would make that copy permanently fetchable by anything that could read one.",
        "properties": {
          "expiresAt": {
            "format": "date-time",
            "type": "string"
          },
          "value": {
            "maxLength": 128,
            "minLength": 16,
            "type": "string"
          }
        },
        "required": [
          "value",
          "expiresAt"
        ],
        "type": "object"
      }
    },
    "required": [
      "formatVersion",
      "appVersion",
      "schemaVersion",
      "createdAt",
      "suggestedFileName",
      "tables",
      "entries",
      "omissions",
      "retainedByDesign",
      "ticket"
    ],
    "title": "ExportResponse",
    "type": "object"
  },
  HealthResponse: {
    "additionalProperties": false,
    "properties": {
      "status": {
        "const": "ok",
        "type": "string"
      },
      "version": {
        "type": "string"
      }
    },
    "required": [
      "status",
      "version"
    ],
    "title": "HealthResponse",
    "type": "object"
  },
  HomeworkConfidenceRequest: {
    "additionalProperties": false,
    "description": "Body of POST /homework/{id}/confidence (#438 slice 2, HYP-UX-005): before the first check, the learner says how sure they are of each question of the sheet - sure, not sure, or guessed - or skips. A LEARNER OBSERVATION, stored as given and read by nothing yet: it is no measure of the learner, never a status, a score or a count, and it never changes a verdict. A skipped prompt is sent as skipped, because a skip is data. Refused once a check run exists for the upload.",
    "properties": {
      "answers": {
        "description": "One entry per question asked, 1 to 64, with no label appearing twice (null, the sheet as a whole, at most once).",
        "items": {
          "additionalProperties": false,
          "properties": {
            "answer": {
              "description": "What the learner said: sure, not_sure, guessed, or skipped (the prompt was skipped). Their own word about their own confidence, not a measure of what they know.",
              "enum": [
                "sure",
                "not_sure",
                "guessed",
                "skipped"
              ],
              "type": "string"
            },
            "questionLabel": {
              "description": "The question label the prompt named (for example 5ב or 3a), or null for the sheet as a whole when the upload has no labelled lines. Stored trimmed; blank-after-trim, longer than 32 characters or holding a control character is refused. Learner content, never an instruction.",
              "maxLength": 64,
              "minLength": 1,
              "type": [
                "string",
                "null"
              ]
            }
          },
          "required": [
            "questionLabel",
            "answer"
          ],
          "type": "object"
        },
        "maxItems": 64,
        "minItems": 1,
        "type": "array"
      }
    },
    "required": [
      "answers"
    ],
    "title": "HomeworkConfidenceRequest",
    "type": "object"
  },
  HomeworkConfidenceResponse: {
    "additionalProperties": false,
    "description": "Answer to POST /homework/{id}/confidence (#438 slice 2). The learner's confidence answers were recorded as their own observation before any verdict. It carries no answer back and no measure of the learner.",
    "properties": {
      "recorded": {
        "const": true,
        "description": "Always true: the answers were stored as given.",
        "type": "boolean"
      }
    },
    "required": [
      "recorded"
    ],
    "title": "HomeworkConfidenceResponse",
    "type": "object"
  },
  HomeworkConfirmRequest: {
    "additionalProperties": false,
    "description": "Body of POST /homework/pages/{pageId}/confirm (#376, R1): the learner confirms WHAT THEY WERE SHOWN. regionsDigest is the digest the transcription response carried when it was displayed. If the server's current digest differs - a correction, a reconciliation or a late reading changed the page since - the confirmation is refused (409 transcription_changed) and nothing is recorded, so a learner can never confirm text they did not see. The digest is an opaque token to the client: it is compared, never interpreted.",
    "properties": {
      "regionsDigest": {
        "description": "The regionsDigest of the HomeworkTranscriptionResponse the learner was looking at: 64 lower-case hex characters. No pattern (the no-magnitude rule forbids digit-admitting patterns); the length bound carries the shape and the server compares it byte for byte.",
        "maxLength": 64,
        "minLength": 64,
        "type": "string"
      }
    },
    "required": [
      "regionsDigest"
    ],
    "title": "HomeworkConfirmRequest",
    "type": "object"
  },
  HomeworkCorrectionRequest: {
    "additionalProperties": false,
    "description": "Body of POST /homework/pages/{pageId}/regions/{regionId}/corrections (#376, R1): what the learner says about one region after seeing what was read. Recorded as the learner's observation in its own append-only table - it never overwrites a reader's line. `corrected` carries the learner's own text; `confirmed_as_read` accepts the one candidate text the region already has and carries none; `not_written` (#392) rejects it - the learner says that line is not on their page - and carries none either. A region whose two readers disagree has no single candidate, so it can be neither confirmed as read nor rejected as not written: the server refuses that (transcription_unresolved) rather than pick a reader. ~~Such a region is resolved only by typing.~~ *Amended 2026-10-31 (#393, founder ruling):* on such a region the learner may instead tap the reader that is right: `chose_reading` with `reader`, and no text. The server copies that reader's stored text; it is the learner's observation, never the policy picking a reader. Allowed only when both readings exist and differ. *Added 2026-11-04 (#658):* `skipped` says neither reading is right and leaves the line out of the check; no text, and it is allowed on any region (one candidate, two readings, or a line a reader settled): the learner may always decline to have a line checked.",
    "properties": {
      "kind": {
        "description": "corrected, confirmed_as_read, not_written, chose_reading (#393) or skipped (#658: neither reading is right, leave this line out of the check; no text, allowed on any region).",
        "enum": [
          "corrected",
          "confirmed_as_read",
          "not_written",
          "chose_reading",
          "skipped"
        ],
        "type": "string"
      },
      "reader": {
        "description": "Required for chose_reading, forbidden otherwise (the route checks the pair): which reader the learner says is right. The server copies that reader's stored text; the client never sends it.",
        "enum": [
          "primary",
          "second"
        ],
        "type": "string"
      },
      "text": {
        "description": "Required for corrected, forbidden for confirmed_as_read, not_written, chose_reading and skipped: JSON Schema cannot say which, so the route checks the pair. The learner's own text, recorded exactly as typed (no case, accent or symbol folding) - it is content, never an instruction. Blank-after-trim is refused.",
        "maxLength": 4000,
        "minLength": 1,
        "type": "string"
      }
    },
    "required": [
      "kind"
    ],
    "title": "HomeworkCorrectionRequest",
    "type": "object"
  },
  HomeworkDisputeResponse: {
    "additionalProperties": false,
    "description": "Answer to POST /homework/{id}/claims/{claimId}/dispute (#438 slice 3). The learner's flag on a refuted line they were shown was filed. It carries no verdict and no measure of the learner; the founder's ruling arrives later as the line's mark.",
    "properties": {
      "disputeId": {
        "description": "The dispute that was filed.",
        "minLength": 1,
        "type": "string"
      },
      "ruleBy": {
        "description": "When the founder rules by: 48 hours after filing, or the exercise's due date if that comes first. UTC, ISO 8601.",
        "minLength": 1,
        "type": "string"
      }
    },
    "required": [
      "disputeId",
      "ruleBy"
    ],
    "title": "HomeworkDisputeResponse",
    "type": "object"
  },
  HomeworkExercisesResponse: {
    "additionalProperties": false,
    "description": "The homework sheets the learner can name before uploading (#437, UX 4.4; the answer to GET /homework/exercises): every exercise in a verified course context file (courses/<moodle_course_id>/context.json), sorted by due date ascending. `state` is a status of the SHEET for THIS learner, derived from their own uploads and check runs, never stored and never a statement about what they know: not_posted (nothing uploaded for it), posted (uploaded, not yet checked), checked (an upload of it has a check run), fixed (an upload of it was made after one that has a check run). `solutionOpen` is whether the due date has passed on the server's clock. No count, score or percentage appears anywhere.",
    "properties": {
      "exercises": {
        "description": "One entry per exercise of every accepted course context file, due date ascending (ties by course then exercise id).",
        "items": {
          "additionalProperties": false,
          "properties": {
            "courseRef": {
              "description": "The Moodle course id as text (digits, no leading zero): the course context file's own folder name. Send it back in POST /courses/{courseRef}/exercises/{exerciseId}/homework.",
              "maxLength": 10,
              "minLength": 1,
              "type": "string"
            },
            "dueAt": {
              "description": "ISO-8601 UTC instant the sheet is due, from the context file.",
              "format": "date-time",
              "type": "string"
            },
            "exerciseId": {
              "description": "The exercise's id inside that course's context file.",
              "maxLength": 80,
              "minLength": 1,
              "type": "string"
            },
            "solutionOpen": {
              "description": "True once dueAt is no later than the server's current time.",
              "type": "boolean"
            },
            "state": {
              "description": "The sheet's state for this learner: not_posted, posted, checked or fixed. Not a status of the learner.",
              "enum": [
                "not_posted",
                "posted",
                "checked",
                "fixed"
              ],
              "type": "string"
            },
            "title": {
              "description": "The exercise's title as the verified context file states it. Course material, shown as text.",
              "type": "string"
            }
          },
          "required": [
            "courseRef",
            "exerciseId",
            "title",
            "dueAt",
            "state",
            "solutionOpen"
          ],
          "type": "object"
        },
        "type": "array"
      }
    },
    "required": [
      "exercises"
    ],
    "title": "HomeworkExercisesResponse",
    "type": "object"
  },
  HomeworkFeedbackChannelResponse: {
    "additionalProperties": false,
    "description": "The feedback channel of an upload (#469, homework-feedback@1): where a reviewing model suggests the learner look again. It is NOT a check: a flag is a pointer to a line, never a verdict, an observation, evidence or a status, and not_flagged does not say a line is right. The model's reason is never in this response: the learner pulls it (POST .../flags/{regionId}/reason), and that pull is recorded as help already given. The flags are help too: a flagged line is a location the learner is shown, so a read (GET) returns them only when they were already shown to this learner; otherwise it returns flagsHidden: true and no flag. They are shown, and the showing recorded, by POST .../feedback-channel/open, which returns this same body with the flags. feedbackState: pending (the review is scheduled or running), produced (flags below), withheld_model_drift (the reviewing model was not the pinned one; nothing from its reply is shown), failed (see failureKind; nothing is shown), not_requested (no reviewer is configured), none (no review exists for the upload). flags is empty unless feedbackState is produced. The record names its model and policy version. No count, score or percentage appears.",
    "properties": {
      "failureKind": {
        "description": "Why the review failed, exactly when feedbackState is failed (for example consent_refused, budget_refused, unavailable, invalid_output); otherwise null.",
        "pattern": "^[a-z][a-z_]*$",
        "type": [
          "string",
          "null"
        ]
      },
      "feedbackRunId": {
        "description": "The stored review this response reads, or null when none is stored.",
        "type": [
          "string",
          "null"
        ]
      },
      "feedbackState": {
        "description": "Where the feedback review of the upload stands.",
        "enum": [
          "pending",
          "produced",
          "withheld_model_drift",
          "failed",
          "not_requested",
          "none"
        ],
        "type": "string"
      },
      "flags": {
        "description": "One entry per line the review covered, in region order; empty unless feedbackState is produced and flagsHidden is false.",
        "items": {
          "additionalProperties": false,
          "properties": {
            "flag": {
              "description": "check_this: look at this line again. not_flagged: the review did not point at it, which is not a statement that it is right.",
              "enum": [
                "check_this",
                "not_flagged"
              ],
              "type": "string"
            },
            "location": {
              "additionalProperties": false,
              "description": "Where the line is on its page, or null when it cannot be placed. frame: a DASHED rectangle in page-image pixels around the line, an area and not an exact position. quote: no frame is drawn because the position cannot be trusted (reason says why). Same derivation as a homework note's location (note-location@1). kind decides which fields are present: style and bbox exactly for frame, reason exactly for quote.",
              "properties": {
                "bbox": {
                  "additionalProperties": false,
                  "description": "Present exactly for frame: the line's box in pixels of the page image (origin top-left), x0 < x1 and y0 < y1, inside the page. A position on a scanned page, not a measure of the learner.",
                  "properties": {
                    "x0": {
                      "description": "Left edge, in pixels.",
                      "type": "integer"
                    },
                    "x1": {
                      "description": "Right edge, in pixels.",
                      "type": "integer"
                    },
                    "y0": {
                      "description": "Top edge, in pixels.",
                      "type": "integer"
                    },
                    "y1": {
                      "description": "Bottom edge, in pixels.",
                      "type": "integer"
                    }
                  },
                  "required": [
                    "x0",
                    "y0",
                    "x1",
                    "y1"
                  ],
                  "type": "object"
                },
                "kind": {
                  "description": "frame or quote.",
                  "enum": [
                    "frame",
                    "quote"
                  ],
                  "type": "string"
                },
                "reason": {
                  "description": "Present exactly for quote: why no frame is drawn.",
                  "enum": [
                    "position_uncalibrated",
                    "position_off_page",
                    "frame_covers_another_line"
                  ],
                  "type": "string"
                },
                "style": {
                  "description": "Present exactly for frame.",
                  "enum": [
                    "dashed"
                  ],
                  "type": "string"
                }
              },
              "required": [
                "kind"
              ],
              "type": [
                "object",
                "null"
              ]
            },
            "pageId": {
              "description": "The homework page the line is on, or null when it cannot be placed.",
              "type": [
                "string",
                "null"
              ]
            },
            "reasonAvailable": {
              "description": "True when the review gave a reason for this flag that the learner can pull. The reason itself is never in this response.",
              "type": "boolean"
            },
            "regionId": {
              "description": "The transcription region (the learner's line) the flag is about.",
              "minLength": 1,
              "type": "string"
            }
          },
          "required": [
            "regionId",
            "pageId",
            "flag",
            "reasonAvailable",
            "location"
          ],
          "type": "object"
        },
        "type": "array"
      },
      "flagsHidden": {
        "description": "True when the review is produced but its flags have not been shown to this learner yet, so this response carries none (call POST .../feedback-channel/open to show them; that records the showing). Says nothing about how many flags there are. False otherwise.",
        "type": "boolean"
      },
      "modelId": {
        "description": "The pinned model the review named, or null when no model was called.",
        "type": [
          "string",
          "null"
        ]
      },
      "policyVersion": {
        "description": "The feedback policy the stored review was made under (homework-feedback@N), or null when none is stored.",
        "type": [
          "string",
          "null"
        ]
      },
      "uploadId": {
        "description": "The homework upload.",
        "minLength": 1,
        "type": "string"
      },
      "withoutQuestionSheet": {
        "description": "True when the latest review was produced without a question sheet (the learner has none, for instance because Moodle is not connected): it ran on the learner's pages alone and is weaker. Connecting Moodle later allows a new review. False otherwise.",
        "type": "boolean"
      }
    },
    "required": [
      "uploadId",
      "feedbackState",
      "failureKind",
      "feedbackRunId",
      "policyVersion",
      "modelId",
      "flagsHidden",
      "withoutQuestionSheet",
      "flags"
    ],
    "title": "HomeworkFeedbackChannelResponse",
    "type": "object"
  },
  HomeworkFeedbackReasonResponse: {
    "additionalProperties": false,
    "description": "The reason a reviewing model gave for one flag (#469), returned only after the pull was recorded as delivered help (a hint rung anchored to the flag): a line fixed after this is evidence with help. The reason is the model's text, untrusted data to display as text, never instructions and never a verdict.",
    "properties": {
      "reason": {
        "description": "The model's stated reason for pointing at the line. Untrusted text.",
        "type": "string"
      },
      "regionId": {
        "description": "The line the flag is about.",
        "minLength": 1,
        "type": "string"
      }
    },
    "required": [
      "regionId",
      "reason"
    ],
    "title": "HomeworkFeedbackReasonResponse",
    "type": "object"
  },
  HomeworkFeedbackResponse: {
    "additionalProperties": false,
    "description": "What a checked homework shows (#406 slice 2): the learner's latest check run over one upload, as notes on the learner's own lines. Every mark is a fact about what was CHECKED, never a grade: proved means the certified check proved exactly what the note's text says (the text is assembled from the coverage phrases of the check, never written freely, so a proved note cannot claim more than was checked); refuted means a certified check refuted it, and is shown only in a response that has just RECORDED its error_location rung as delivered help; read_not_checked, refused, undecided and withheld all mean that no verdict was reached. EVERY SHOWING OF HELP IS RECORDED: help shown again and not recorded would age out of the window the independence query looks back over, so only a response that recorded a line's rungs may show them. POST /homework/{id}/check opens every checked line (recording every rung it shows); a rung pull opens its one line; GET /homework/{id}/feedback records nothing and so opens nothing - every checked line comes back on_request, alike whatever its verdict, because hiding only the refuted ones would reveal them by elimination. There is no status, no score, no count and no percentage, and uploaded work is never independent evidence (D31). run is null until the upload has been checked.",
    "properties": {
      "fixedSinceEarlier": {
        "description": "Questions of this upload that an earlier upload of the same sheet had a standing mistake on (a ✗ not under dispute and not overturned) and that this upload now proves (#415), by the same rule the weak-point record uses for 'fixed on a later upload'. A question is listed only when this response opens all of its notes, and only on the upload's latest run, so a closed feedback discloses nothing. Fixed after feedback: help-assisted work, never an independence claim. A date and a label, never a count or a score.",
        "items": {
          "additionalProperties": false,
          "properties": {
            "earlierUploadedAt": {
              "description": "When the earlier upload that had the mistake was uploaded (ISO 8601 UTC).",
              "minLength": 1,
              "type": "string"
            },
            "questionLabel": {
              "description": "The question label the learner confirmed. UNTRUSTED text.",
              "minLength": 1,
              "type": "string"
            }
          },
          "required": [
            "questionLabel",
            "earlierUploadedAt"
          ],
          "type": "object"
        },
        "type": "array"
      },
      "notCheckedRegionIds": {
        "description": "The lines of the upload's latest check run that DaVinci did NOT check (#646): the lines it read and LEFT OUT of the check (the run recorded each as read_not_checked: no text, no claim, no verdict), then the graded lines that no claim covers (each has a read_not_checked note), page by page. Lines, not a count, because a numeric type is refused in a response by schema-rules.test.ts; the page shows how many there are. Empty when the upload has not been checked or every line was checked. Such a line was not seen to be right or wrong, which says nothing about the work, and which lines a claim covers says nothing about verdicts either (a refused or undecided claim covers its lines too): the feedback page tells the learner how many lines were not checked once the feedback is opened, and a later upload does not count as fixing a question that still has a left-out line (fixed-by.ts).",
        "items": {
          "maxLength": 36,
          "minLength": 36,
          "type": "string"
        },
        "type": "array"
      },
      "notes": {
        "description": "One note per checked claim and one per line no claim covers, page by page in page order, each page's notes in line order.",
        "items": {
          "additionalProperties": false,
          "properties": {
            "certificate": {
              "additionalProperties": false,
              "description": "The certificate an opened proved or refuted note was decided by (#377, #361): its kind and the check that produced it. Null for every other mark, on_request included - a certificate's kind is pinned to its verdict, so naming it on a note the response did not open would reveal the verdict.",
              "properties": {
                "checkId": {
                  "description": "The certified check that produced the certificate.",
                  "minLength": 1,
                  "type": "string"
                },
                "kind": {
                  "description": "The certificate kind as the kernel names it (for example step_chain_proof, step_chain_refutation, counterexample_valid_proof, witness_failure, set_relation_proof). A name, never the certificate's contents: a refutation's values are the counterexample rung's to show.",
                  "minLength": 1,
                  "type": "string"
                }
              },
              "required": [
                "kind",
                "checkId"
              ],
              "type": [
                "object",
                "null"
              ]
            },
            "claimId": {
              "description": "homework_claims.claim_id, or null for a line no claim covers (read_not_checked).",
              "maxLength": 36,
              "minLength": 36,
              "type": [
                "string",
                "null"
              ]
            },
            "covers": {
              "description": "The coverage ids a proved note's text is assembled from. Empty for every other mark.",
              "items": {
                "enum": [
                  "each_line_equals_the_line_before",
                  "values_break_the_claim_as_written",
                  "values_satisfy_the_condition_as_written",
                  "the_two_sets_are_equal"
                ],
                "type": "string"
              },
              "type": "array"
            },
            "disputable": {
              "description": "True exactly for an opened refuted note with no dispute filed: the learner may say it looks wrong (POST /homework/{id}/claims/{claimId}/dispute). False for every other note, on_request included, so it reveals nothing about an unopened line.",
              "type": "boolean"
            },
            "errorRegionIds": {
              "description": "For refuted: for an equality chain, the line before the step that does not follow and that step's line; for any other kind of claim (a counterexample, a witness, a set equation) every line the claim is about. Empty otherwise.",
              "items": {
                "minLength": 1,
                "type": "string"
              },
              "type": "array"
            },
            "examPrepNoted": {
              "description": "True exactly when the note is a refuted mark (not under review, not ruled correct, not on request), the sheet has a topic, and the run shown is the upload's latest run - the refutation is then in the weak-point record that the line 'Noted for your exam prep.' refers to. False for every other note.",
              "type": "boolean"
            },
            "learnerCorrectedRegionIds": {
              "description": "The note's lines whose text the learner typed themselves, so the reading shown beside the note is theirs. The same lines readings marks learnerCorrected.",
              "items": {
                "minLength": 1,
                "type": "string"
              },
              "type": "array"
            },
            "location": {
              "additionalProperties": false,
              "description": "Where the note sits (note-location@1), or null exactly when the note shows no lines (on_request). frame: a DASHED rectangle in page-image pixels around the note's lines - for refuted, around errorRegionIds - meaning an area, not an exact position (a solid, exact frame needs vector ink, which no upload records). quote: no frame is drawn, because the position cannot be trusted, and the note quotes its readings in place of one: position_uncalibrated (the page's reader boxes could not be calibrated, so a line could be anywhere), position_off_page (a box is not a non-empty rectangle inside the page) or frame_covers_another_line (the frame would also enclose a line the note is not about). A note pinned to the wrong line is worse than no frame. kind decides which fields are present: style and bbox exactly for frame, reason exactly for quote (one object rather than a oneOf, which this package does not use).",
              "properties": {
                "bbox": {
                  "additionalProperties": false,
                  "description": "Present exactly for frame: the union of the reader boxes of the framed lines, in pixels of the page image (origin top-left; the page's widthPx and heightPx), x0 < x1 and y0 < y1, inside the page. Positions are content facts about the page, not measures of the learner.",
                  "properties": {
                    "x0": {
                      "description": "Left edge, in pixels.",
                      "type": "integer"
                    },
                    "x1": {
                      "description": "Right edge, in pixels.",
                      "type": "integer"
                    },
                    "y0": {
                      "description": "Top edge, in pixels.",
                      "type": "integer"
                    },
                    "y1": {
                      "description": "Bottom edge, in pixels.",
                      "type": "integer"
                    }
                  },
                  "required": [
                    "x0",
                    "y0",
                    "x1",
                    "y1"
                  ],
                  "type": "object"
                },
                "kind": {
                  "description": "frame: a frame is drawn. quote: no frame; the note quotes its readings instead.",
                  "enum": [
                    "frame",
                    "quote"
                  ],
                  "type": "string"
                },
                "reason": {
                  "description": "Present exactly for quote: why no frame is drawn.",
                  "enum": [
                    "position_uncalibrated",
                    "position_off_page",
                    "frame_covers_another_line"
                  ],
                  "type": "string"
                },
                "style": {
                  "description": "Present exactly for frame. dashed: an area. The only style until vector ink is recorded.",
                  "enum": [
                    "dashed"
                  ],
                  "type": "string"
                }
              },
              "required": [
                "kind"
              ],
              "type": [
                "object",
                "null"
              ]
            },
            "mark": {
              "description": "What was checked, as a fact: proved, refuted, under_review (the learner said a refuted line they were shown looks wrong and the founder has not ruled: no verdict is concluded, its lines stay; #438), ruled_correct (the founder overturned the refutation: the step is shown as correct by the ruling, with no coverage phrase and no certificate), on_request (a checked line this response did not open - its verdict is not shown, whatever it is; opening the feedback shows it), read_not_checked, refused (the check needs a course rule not yet shown to be stated), undecided (the kernel could not decide) or withheld (a stored verdict that does not verify again - shown as not checked).",
              "enum": [
                "proved",
                "refuted",
                "under_review",
                "ruled_correct",
                "on_request",
                "read_not_checked",
                "refused",
                "undecided",
                "withheld"
              ],
              "type": "string"
            },
            "pageId": {
              "description": "The homework page the note is on.",
              "minLength": 1,
              "type": "string"
            },
            "pullable": {
              "description": "The rungs the learner can pull now with POST /homework/claims/{claimId}/rungs. Pulling one records it before it is shown.",
              "items": {
                "enum": [
                  "hint",
                  "counterexample",
                  "solution"
                ],
                "type": "string"
              },
              "type": "array"
            },
            "questionHint": {
              "description": "DISPLAY ONLY (#646, question-hint@1): a best guess at the question the note's first line belongs to, computed when the feedback is read and never stored. Present only where questionLabel is null and the line's label is not the learner's. May be wrong; nothing is graded, decided or counted on it (the one use besides display is choosing which worked solution to offer). questionLabel stays the recorded label. Untrusted, displayed as text only.",
              "maxLength": 32,
              "minLength": 1,
              "type": "string"
            },
            "questionLabel": {
              "description": "Which question of the exercise the note's first line belongs to, as the learner confirmed it before the check (#406 slice 3), or null: no label was in force, the run recorded none, or the note is on_request (a note that shows no lines names no question). A label groups notes; it never changes a mark. Handwriting or learner-typed content, untrusted, displayed as text only.",
              "maxLength": 32,
              "minLength": 1,
              "type": [
                "string",
                "null"
              ]
            },
            "readings": {
              "description": "Each of the note's lines as the check run read it, in line order (#406 slice 5): the text of the learner's confirmed transcription as of the run - not whatever the review screen shows now - and whether the learner typed it. Shown beside every note so the learner can see what was checked. Empty exactly when regionIds is. Handwriting or learner-typed content, untrusted, displayed as text only.",
              "items": {
                "additionalProperties": false,
                "properties": {
                  "learnerCorrected": {
                    "description": "True when the learner typed this text over what the readers produced.",
                    "type": "boolean"
                  },
                  "regionId": {
                    "description": "The line, one of regionIds.",
                    "minLength": 1,
                    "type": "string"
                  },
                  "text": {
                    "description": "The line's text as checked. Untrusted, displayed as text only.",
                    "minLength": 1,
                    "type": "string"
                  }
                },
                "required": [
                  "regionId",
                  "text",
                  "learnerCorrected"
                ],
                "type": "object"
              },
              "type": "array"
            },
            "regionIds": {
              "description": "The transcription regions (the learner's lines) the note is about, in line order; empty for on_request. Their text as the run checked it is in readings, and where they are on the page in location.",
              "items": {
                "minLength": 1,
                "type": "string"
              },
              "type": "array"
            },
            "ruleBy": {
              "description": "For an under_review note: when the founder rules by (48 hours after filing, or the due date if sooner), UTC ISO 8601. Null for every other note.",
              "minLength": 1,
              "type": [
                "string",
                "null"
              ]
            },
            "rules": {
              "description": "The course rules an opened note rests on (#377, #366), in the order the check names them; empty for on_request, read_not_checked, undecided and withheld. used: the verdict of a proved or refuted note was decided under the rule. not_sourced: a refused note's check needs the rule and the course materials have not been shown to state it. quote, where, page and moduleName are present exactly for a used rule quoted from the same verified course context the run cites (by digest); a context that has changed since the run quotes nothing, rather than a text the verdict was not decided under.",
              "items": {
                "additionalProperties": false,
                "properties": {
                  "moduleName": {
                    "description": "The Moodle module (the material) the quote is from.",
                    "minLength": 1,
                    "type": [
                      "string",
                      "null"
                    ]
                  },
                  "page": {
                    "description": "The 1-based page of the material the quote is on (an address, never a measure).",
                    "type": [
                      "integer",
                      "null"
                    ]
                  },
                  "quote": {
                    "description": "The rule verbatim from the course materials' text layer. Course text, displayed as text only.",
                    "minLength": 1,
                    "type": [
                      "string",
                      "null"
                    ]
                  },
                  "ruleId": {
                    "description": "The rule's id in the course context.",
                    "minLength": 1,
                    "type": "string"
                  },
                  "standing": {
                    "description": "used or not_sourced, as above.",
                    "enum": [
                      "used",
                      "not_sourced"
                    ],
                    "type": "string"
                  },
                  "where": {
                    "description": "Where in the material the quote is, as the course context states it.",
                    "minLength": 1,
                    "type": [
                      "string",
                      "null"
                    ]
                  }
                },
                "required": [
                  "ruleId",
                  "standing",
                  "quote",
                  "where",
                  "page",
                  "moduleName"
                ],
                "type": "object"
              },
              "type": "array"
            },
            "ruling": {
              "additionalProperties": false,
              "description": "For a ruled_correct note: the ruling it rests on, in place of a certificate (every derived conclusion names its evidence and policy version). The founder's reason is never shown. Null for every other note.",
              "properties": {
                "policyVersion": {
                  "minLength": 1,
                  "type": "string"
                },
                "rulingId": {
                  "minLength": 1,
                  "type": "string"
                }
              },
              "required": [
                "rulingId",
                "policyVersion"
              ],
              "type": [
                "object",
                "null"
              ]
            },
            "rungs": {
              "description": "The rungs THIS response recorded as delivered for this claim and shows, in ladder order. Empty for every line the response did not open.",
              "items": {
                "additionalProperties": false,
                "properties": {
                  "deliveredAt": {
                    "description": "When it was recorded as delivered, ISO-8601 UTC at whole seconds.",
                    "format": "date-time",
                    "type": "string"
                  },
                  "rung": {
                    "description": "The post-answer-ladder@1 rung.",
                    "enum": [
                      "error_location",
                      "hint",
                      "counterexample",
                      "solution"
                    ],
                    "type": "string"
                  },
                  "solutionSource": {
                    "description": "Only on the solution rung (#586): where its text came from. davinci is DaVinci's own worked solution, written by a model - NOT official and able to be wrong, and the web says so beside it; authored is a worked solution a person wrote, delivered before DaVinci wrote its own (#480). Absent on every other rung.",
                    "enum": [
                      "davinci",
                      "authored"
                    ],
                    "type": "string"
                  },
                  "text": {
                    "description": "What was shown, exactly as recorded.",
                    "minLength": 1,
                    "type": "string"
                  }
                },
                "required": [
                  "rung",
                  "text",
                  "deliveredAt"
                ],
                "type": "object"
              },
              "type": "array"
            },
            "solutionOpensAt": {
              "description": "When the worked solution opens, UTC ISO 8601 (#480): the sheet's due date, for an opened refuted note whose claim has a sheet question (DaVinci writes its worked solution when it is pulled, #586) and whose solution is still closed - the web shows the solution control disabled with this date. Null otherwise: the solution is already pullable (then it is in pullable), the claim has no sheet question, the due date is unknown, or the note is not an opened refutation. It never carries the solution's text.",
              "minLength": 1,
              "type": [
                "string",
                "null"
              ]
            },
            "text": {
              "description": "The note's wording. For proved, exactly the coverage phrases listed in covers; for refuted, the error type of the error_location rung; for on_request, a fixed phrase that says nothing of the verdict; otherwise a fixed not-checked phrase.",
              "minLength": 1,
              "type": "string"
            }
          },
          "required": [
            "claimId",
            "pageId",
            "mark",
            "text",
            "covers",
            "regionIds",
            "errorRegionIds",
            "learnerCorrectedRegionIds",
            "rungs",
            "pullable",
            "solutionOpensAt",
            "questionLabel",
            "readings",
            "location",
            "certificate",
            "rules",
            "disputable",
            "ruleBy",
            "ruling",
            "examPrepNoted"
          ],
          "type": "object"
        },
        "type": "array"
      },
      "run": {
        "additionalProperties": false,
        "description": "The check run the notes come from, with the versions it was decided under (invariant 3), or null if the upload has not been checked.",
        "properties": {
          "checkedAt": {
            "description": "When the run was recorded, ISO-8601 UTC at whole seconds.",
            "format": "date-time",
            "type": "string"
          },
          "courseContextDigest": {
            "description": "The digest of the verified course context the run read: 64 lower-case hex characters, compared and never interpreted.",
            "maxLength": 64,
            "minLength": 64,
            "type": "string"
          },
          "courseContextPolicyVersion": {
            "description": "The course-context policy the course rules were verified under.",
            "minLength": 1,
            "type": "string"
          },
          "exerciseId": {
            "description": "The exercise in the course's own context file that the upload was checked against.",
            "minLength": 1,
            "type": "string"
          },
          "kernelVersion": {
            "description": "The certificate kernel that decided every verdict (certificate-kernel@N).",
            "minLength": 1,
            "type": "string"
          },
          "locationPolicyVersion": {
            "description": "The policy every note's location was derived under (note-location@N, #406 slice 5). Derived on every read from the reader's boxes and the page's calibration, never stored, so it is always the current policy.",
            "minLength": 1,
            "type": "string"
          },
          "policyVersion": {
            "description": "The claim-finding policy the run was made under (homework-check@N).",
            "minLength": 1,
            "type": "string"
          },
          "questionHintPolicyVersion": {
            "description": "The question-hint policy (question-hint@N) the notes' questionHint values were derived under. Present only when at least one note has a questionHint.",
            "minLength": 1,
            "type": "string"
          },
          "questionLabelPolicyVersion": {
            "description": "The question-label policy (question-label@N) the run recorded each line's question label under, or null for a run recorded before question labels were (#406 slice 3): such a run's notes have no label, which says nothing about the work.",
            "minLength": 1,
            "type": [
              "string",
              "null"
            ]
          },
          "runId": {
            "description": "homework_check_runs.run_id - a uuidv7.",
            "maxLength": 36,
            "minLength": 36,
            "type": "string"
          }
        },
        "required": [
          "runId",
          "checkedAt",
          "exerciseId",
          "policyVersion",
          "kernelVersion",
          "courseContextPolicyVersion",
          "courseContextDigest",
          "questionLabelPolicyVersion",
          "locationPolicyVersion"
        ],
        "type": [
          "object",
          "null"
        ]
      },
      "selfReportOpen": {
        "description": "Whether confidence answers (HYP-UX-005, #438) are still accepted for this upload - false once any check run exists for this upload or for another upload of this learner's for the same exercise (course_ref, assignment_ref); a fact about the record, not about the learner.",
        "type": "boolean"
      },
      "submissionRules": {
        "additionalProperties": false,
        "description": "The sheet's submission rules (#415): the five FORMAT rules the exercise and course procedures state - one question per page, questions in ascending order, no page upside down, a title with the course name and exercise number, no name or ID on the pages - each decided deterministically from the learner-confirmed question labels and transcription text (never a model call). They grade the submission's format, not what the learner knows: no evidence, topic status or weak point comes of them. Names its policy (invariant 3). Null until the upload has been checked.",
        "properties": {
          "policyVersion": {
            "description": "The rule set the marks were decided under, e.g. submission-rules@N.",
            "minLength": 1,
            "type": "string"
          },
          "rules": {
            "items": {
              "additionalProperties": false,
              "properties": {
                "mark": {
                  "description": "met (shown as a check), not_met (a cross) or not_checked (a circle: DaVinci could not decide it, and reason says why).",
                  "enum": [
                    "met",
                    "not_met",
                    "not_checked"
                  ],
                  "type": "string"
                },
                "reason": {
                  "description": "Why the rule was not checked; present exactly when the mark is not_checked.",
                  "enum": [
                    "no_question_labels",
                    "labels_not_numbers",
                    "orientation_not_read",
                    "page_unreadable",
                    "no_display_name",
                    "exercise_number_unknown",
                    "course_unknown",
                    "lines_not_checked"
                  ],
                  "type": "string"
                },
                "ruleId": {
                  "enum": [
                    "one_question_per_page",
                    "ascending_order",
                    "upright_pages",
                    "title",
                    "no_name_or_id"
                  ],
                  "type": "string"
                }
              },
              "required": [
                "ruleId",
                "mark"
              ],
              "type": "object"
            },
            "type": "array"
          }
        },
        "required": [
          "policyVersion",
          "rules"
        ],
        "type": [
          "object",
          "null"
        ]
      },
      "topic": {
        "additionalProperties": false,
        "description": "The course topic of this sheet (#439, #438): the Moodle section its homework sheet sits in, read from what Moodle listed. Null when the sheet's section cannot be shown (no module id, module not synced, no course data). Never a score or a count.",
        "properties": {
          "sectionName": {
            "minLength": 1,
            "type": [
              "string",
              "null"
            ]
          },
          "sectionNumber": {
            "description": "Moodle's section number as written, a label and not a magnitude (a numeric type is refused in a response by schema-rules.test.ts).",
            "maxLength": 8,
            "minLength": 1,
            "type": "string"
          }
        },
        "required": [
          "sectionNumber",
          "sectionName"
        ],
        "type": [
          "object",
          "null"
        ]
      },
      "uploadId": {
        "description": "homework_uploads.upload_id - a uuidv7, always exactly 36 characters.",
        "maxLength": 36,
        "minLength": 36,
        "type": "string"
      }
    },
    "required": [
      "uploadId",
      "run",
      "selfReportOpen",
      "topic",
      "fixedSinceEarlier",
      "submissionRules",
      "notCheckedRegionIds",
      "notes"
    ],
    "title": "HomeworkFeedbackResponse",
    "type": "object"
  },
  HomeworkLabelRequest: {
    "additionalProperties": false,
    "description": "Body of POST /homework/pages/{pageId}/regions/{regionId}/label (#406 slice 3): the learner says which question of the exercise one line belongs to, or that it belongs to none. Recorded as the learner's observation in its own append-only table; it never changes the line's text. The label set here applies to this line and is carried down the page to the lines below it until the next line that starts with a marker or has a label of its own. Setting or clearing a label after the page was confirmed voids the confirmation until the page is confirmed again, exactly as a correction does. A label never changes a verdict.",
    "properties": {
      "label": {
        "description": "The question label (for example 5ב or 3a), or null to say the line belongs to no question. Stored trimmed; blank-after-trim, longer than 32 characters, or containing a control character is refused. Learner content, never an instruction.",
        "maxLength": 64,
        "minLength": 1,
        "type": [
          "string",
          "null"
        ]
      }
    },
    "required": [
      "label"
    ],
    "title": "HomeworkLabelRequest",
    "type": "object"
  },
  HomeworkListResponse: {
    "additionalProperties": false,
    "description": "The learner's own most recent homework uploads, newest first, at most 20 (#575; the answer to GET /homework). Each entry is an OBSERVATION of an upload plus its derived state; nothing here is a judgement about the work or a status of what the learner knows. `state` is the same overall state GET /homework/{id} reports (processing, ready or failed). `checked` is whether a check run exists for the upload. No count, score or percentage appears anywhere.",
    "properties": {
      "uploads": {
        "description": "This learner's uploads, newest first, capped at 20.",
        "items": {
          "additionalProperties": false,
          "properties": {
            "checked": {
              "description": "True once a check run exists for this upload.",
              "type": "boolean"
            },
            "courseRef": {
              "description": "The Moodle course id the upload was filed under, when the learner named an exercise.",
              "maxLength": 10,
              "minLength": 1,
              "type": "string"
            },
            "createdAt": {
              "description": "ISO-8601 UTC instant the upload was accepted.",
              "format": "date-time",
              "type": "string"
            },
            "exerciseId": {
              "description": "The exercise id the upload was filed under, when the learner named one.",
              "maxLength": 80,
              "minLength": 1,
              "type": "string"
            },
            "state": {
              "description": "Derived from the rows, as GET /homework/{id}: processing, ready, or failed.",
              "enum": [
                "processing",
                "ready",
                "failed"
              ],
              "type": "string"
            },
            "uploadId": {
              "description": "homework_uploads.upload_id - a uuidv7, always exactly 36 characters.",
              "maxLength": 36,
              "minLength": 36,
              "type": "string"
            }
          },
          "required": [
            "uploadId",
            "createdAt",
            "state",
            "checked"
          ],
          "type": "object"
        },
        "maxItems": 20,
        "type": "array"
      }
    },
    "required": [
      "uploads"
    ],
    "title": "HomeworkListResponse",
    "type": "object"
  },
  HomeworkRungRequest: {
    "additionalProperties": false,
    "description": "Body of POST /homework/claims/{claimId}/rungs (#406 slice 2): the learner pulls one more rung of the post-answer ladder for one refuted line of their checked homework. The rung is RECORDED as delivered help before it is shown, and recording it is what makes a later fix with-help evidence (invariant 1); a refused rung records nothing and shows nothing. error_location is shown by default (opening the feedback with POST /homework/{id}/check records and shows it) and needs no request. The response opens only this line, and shows again - recording again - every rung it already had. Which rungs a note can still pull is the note's pullable list; anything else is refused (rung_unavailable): counterexample is shown only against a certified counterexample, which no equality-chain refutation is, and solution is the founder-authored worked solution of the line's sheet question (#480): it stays closed until the due date and while the due date is unknown, and is refused when none was authored.",
    "properties": {
      "rung": {
        "description": "The rung asked for, from post-answer-ladder@1.",
        "enum": [
          "hint",
          "counterexample",
          "solution"
        ],
        "type": "string"
      }
    },
    "required": [
      "rung"
    ],
    "title": "HomeworkRungRequest",
    "type": "object"
  },
  HomeworkTranscriptionResponse: {
    "additionalProperties": false,
    "description": "What was read from one page of homework, region by region, and what the learner has done about it (#376, R1; the answer to GET /homework/pages/{pageId}/transcription and to the two POSTs that change it). This is the 'present what you grade' surface: the learner sees what the readers produced BEFORE anything is graded, and corrects or confirms it. Three different kinds of thing are shown, and kept apart (invariant 2): what a READER said (`readings`, untrusted handwriting content - displayed as text, never interpreted), what DaVinci CONCLUDED from the two readings (`agreement`, drawn under `policyVersion`, naming no judgement of the work), and what the LEARNER did (`learnerEntry`). Only an `agreed` region is certain. A disagreement is never resolved by picking a reader: both readings are shown and the learner supplies the text. Nothing here says whether the work is right.",
    "properties": {
      "allResolved": {
        "description": "True when every region is resolved - the precondition of confirming the page.",
        "type": "boolean"
      },
      "confirmed": {
        "description": "True when the learner confirmed the transcription exactly as it now stands. A correction after a confirmation voids it until the page is confirmed again. Confirmed means seen and accepted by the learner, not that the work is right.",
        "type": "boolean"
      },
      "coordinates": {
        "additionalProperties": false,
        "description": "How each reader's boxes were interpreted when this page was reconciled (the calibration of the reader-reconcile@N that wrote the regions), the evidence for any position-dependent conclusion. pixel: used as given. normalised: 0-1000 of the page, converted to pixels. normalised_swapped: 0-1000 with y before x, converted. undetermined: no decisive calibration (or fewer than three text-identical lines to calibrate with), so NOTHING on this page is agreed and the boxes are kept raw and approximate. Chosen by evidence from the page's own text-identical lines, never by trusting the reader.",
        "properties": {
          "primary": {
            "description": "The primary reader's interpretation.",
            "enum": [
              "pixel",
              "normalised",
              "normalised_swapped",
              "undetermined"
            ],
            "type": "string"
          },
          "second": {
            "description": "The second reader's interpretation.",
            "enum": [
              "pixel",
              "normalised",
              "normalised_swapped",
              "undetermined"
            ],
            "type": "string"
          }
        },
        "required": [
          "primary",
          "second"
        ],
        "type": "object"
      },
      "heightPx": {
        "description": "Height in pixels of the same image.",
        "type": "integer"
      },
      "pageId": {
        "description": "homework_pages.page_id.",
        "maxLength": 36,
        "minLength": 36,
        "type": "string"
      },
      "pageIndex": {
        "description": "0-based position of the page in the PDF.",
        "type": "integer"
      },
      "policyVersion": {
        "description": "The reconciliation policy the regions were drawn under (invariant 3): reader-reconcile@N.",
        "maxLength": 64,
        "minLength": 1,
        "type": "string"
      },
      "questionHintPolicyVersion": {
        "description": "The question-hint policy (question-hint@N) the regions' questionHint values were derived under. Present only when at least one region has a questionHint.",
        "minLength": 1,
        "type": "string"
      },
      "readers": {
        "description": "Each reader heard from on this page, naming the model asked and the version the provider says answered.",
        "items": {
          "additionalProperties": false,
          "properties": {
            "coordinateSpace": {
              "description": "Where this reading's boxes appear to live. pixels: they fit the page image. suspect: every box fits inside 0-1000 although the image is larger than 1000 px on both axes, so the reader probably returned normalised coordinates; the boxes are NOT rescaled and the reading is not failed (its text is valid), but the positions it gave are approximate. not_applicable: the reading failed and has no boxes.",
              "enum": [
                "pixels",
                "suspect",
                "not_applicable"
              ],
              "type": "string"
            },
            "failureKind": {
              "description": "Present only when outcome is failed.",
              "enum": [
                "consent_required",
                "budget_refused",
                "api_key_missing",
                "paid_tier_unattested",
                "image_unreadable",
                "remote_call_failed",
                "response_blocked",
                "response_truncated",
                "empty_response",
                "invalid_output",
                "settings_echo_mismatch",
                "unclassified"
              ],
              "type": "string"
            },
            "outcome": {
              "description": "succeeded or failed.",
              "enum": [
                "succeeded",
                "failed"
              ],
              "type": "string"
            },
            "promptVersion": {
              "description": "The name of the fixed instruction the reader was given.",
              "maxLength": 128,
              "minLength": 1,
              "type": "string"
            },
            "readerName": {
              "description": "The provider.",
              "enum": [
                "gemini",
                "anthropic",
                "openai"
              ],
              "type": "string"
            },
            "readerSlot": {
              "description": "primary or second.",
              "enum": [
                "primary",
                "second"
              ],
              "type": "string"
            },
            "reportedModelVersion": {
              "description": "The version the provider's response reported, when it reported one.",
              "maxLength": 128,
              "minLength": 1,
              "type": "string"
            },
            "requestedModel": {
              "description": "The model id asked.",
              "maxLength": 128,
              "minLength": 1,
              "type": "string"
            }
          },
          "required": [
            "coordinateSpace",
            "outcome",
            "promptVersion",
            "readerName",
            "readerSlot",
            "requestedModel"
          ],
          "type": "object"
        },
        "type": "array"
      },
      "readingComplete": {
        "description": "True when both readers have been heard from (each succeeded or failed) and the regions exist. While false the page may still be read.",
        "type": "boolean"
      },
      "regions": {
        "description": "The lines of the page, in reading order, each drawn from the two readers' lines for the same stretch of ink.",
        "items": {
          "additionalProperties": false,
          "properties": {
            "agreedText": {
              "description": "Present exactly when agreement is agreed: the text both readers produced. UNTRUSTED handwriting content.",
              "maxLength": 4000,
              "type": "string"
            },
            "agreement": {
              "description": "What two readings of this stretch of ink came to. agreed: both readers produced the same text (after Unicode NFC and whitespace collapse only - never case, accent or symbol folding) and neither marked it uncertain. disagreed: they produced different text. reader_uncertain: a reader itself marked the line uncertain. single_reader: only one reader had the line, or the other reading failed. Only agreed is certain. An agreed line also needs overlapping boxes: when two readers produced the same text for lines that are not in the same place, or the page's coordinates could not be calibrated, the line is NOT agreed - it is shown as two single_reader regions, each where its own reader read it.",
              "enum": [
                "agreed",
                "disagreed",
                "reader_uncertain",
                "single_reader"
              ],
              "type": "string"
            },
            "bbox": {
              "additionalProperties": false,
              "description": "The box of this line in pixels of the page image (origin top-left), as the primary reader gave it, or the only reader's when only one had the line. x0 < x1 and y0 < y1. A hint of where to look: pairing and agreement are by text and never use it. Positions are content facts about the page, not measures of the learner.",
              "properties": {
                "x0": {
                  "description": "Left edge, in pixels.",
                  "type": "integer"
                },
                "x1": {
                  "description": "Right edge, in pixels.",
                  "type": "integer"
                },
                "y0": {
                  "description": "Top edge, in pixels.",
                  "type": "integer"
                },
                "y1": {
                  "description": "Bottom edge, in pixels.",
                  "type": "integer"
                }
              },
              "required": [
                "x0",
                "y0",
                "x1",
                "y1"
              ],
              "type": "object"
            },
            "bboxApproximate": {
              "description": "True unless the box is exact: it is the primary reader's (or the only reader's) box for the line, in page pixels under the page's calibration, and exact only where that reader's boxes were pixels. A box converted from a normalised interpretation, or kept raw on an undetermined page, is approximate. A box is where to look, never evidence.",
              "type": "boolean"
            },
            "contextReason": {
              "description": "Present only when effectiveSource is reader_context_resolved: why that reading was taken (the rule and the operands it looked at). UNTRUSTED content: it quotes the learner's operands.",
              "maxLength": 4000,
              "type": "string"
            },
            "effectiveSource": {
              "description": "Present when resolved and not rejected as not_written: whose reading the effective text is. reader_agreed: both readers agreed. reader_single_unconfirmed (#465): one reading, or a flagged identical pair, used without asking the learner; the READERS' reading, not the learner's. reader_context_resolved (#465): two readings differing only in symbols of one context rule, settled by reader-context@N (see contextReason). learner_corrected, learner_confirmed_as_read and learner_chose_reading (#393) are the learner's own word. learner_skipped (#658) is present WITHOUT effectiveText: the learner declined to have the line checked.",
              "enum": [
                "reader_agreed",
                "learner_corrected",
                "learner_confirmed_as_read",
                "learner_chose_reading",
                "reader_single_unconfirmed",
                "reader_context_resolved",
                "learner_skipped"
              ],
              "type": "string"
            },
            "effectiveText": {
              "description": "Present when resolved and not rejected as not_written and not readNotChecked: the text this region stands for. UNTRUSTED content.",
              "maxLength": 4000,
              "type": "string"
            },
            "identicalPartnerRegionId": {
              "description": "Derived by the server and never stored (#392): when this region and another are the two halves of a text-identical pair that split into single_reader regions (one with only the primary reader's line, one with only the second reader's, same text), the other's regionId; null otherwise. Pairing is greedy in region order and mutual. It decides resolution: neither half is used without the learner (using both would duplicate the line), so the screen asks one question (one line or two?) instead of two.",
              "maxLength": 36,
              "minLength": 36,
              "type": [
                "string",
                "null"
              ]
            },
            "learnerCorrected": {
              "description": "True when the effective text is one the learner typed over what the readers produced.",
              "type": "boolean"
            },
            "learnerEntry": {
              "additionalProperties": false,
              "description": "The learner's latest word on this region, if any. It never replaces a reading: the readings above stay exactly what the readers said.",
              "properties": {
                "chosenReader": {
                  "description": "Present only for chose_reading (#393): which reader the learner said is right. The learner's observation of what they wrote, never the reconcile policy picking a reader.",
                  "enum": [
                    "primary",
                    "second"
                  ],
                  "type": "string"
                },
                "kind": {
                  "description": "corrected (the learner typed the text), confirmed_as_read (the learner accepted the one candidate text) not_written (the learner rejected the one candidate text: that line is not on their page) chose_reading (#393: the readers differed and the learner said which reader is right; text is that reader's text and chosenReader names it) or skipped (#658: the learner declined to have the line checked; no text, no claim about what was written).",
                  "enum": [
                    "corrected",
                    "confirmed_as_read",
                    "not_written",
                    "chose_reading",
                    "skipped"
                  ],
                  "type": "string"
                },
                "observedAt": {
                  "description": "ISO-8601 UTC instant the entry was recorded.",
                  "maxLength": 20,
                  "minLength": 20,
                  "type": "string"
                },
                "text": {
                  "description": "The learner's own text: what they typed (corrected) the one candidate text they accepted (confirmed_as_read), or the one candidate text they rejected (not_written). Stored so the entry names exactly what was accepted or rejected.",
                  "maxLength": 4000,
                  "type": "string"
                }
              },
              "required": [
                "kind",
                "observedAt"
              ],
              "type": "object"
            },
            "primaryReading": {
              "additionalProperties": false,
              "description": "What the primary reader said for this stretch of ink. Present for a region that is not agreed, when that reader had a line here.",
              "properties": {
                "markedUncertain": {
                  "description": "The reader itself said it was unsure of this line.",
                  "type": "boolean"
                },
                "text": {
                  "description": "UNTRUSTED handwriting content, exactly as the reader returned it.",
                  "maxLength": 4000,
                  "type": "string"
                },
                "uncertainTokens": {
                  "description": "The doubtful tokens this reader listed for the line. UNTRUSTED handwriting content, shown for the learner to judge. Empty when it listed none.",
                  "items": {
                    "maxLength": 4000,
                    "type": "string"
                  },
                  "type": "array"
                }
              },
              "required": [
                "text",
                "markedUncertain",
                "uncertainTokens"
              ],
              "type": "object"
            },
            "questionHint": {
              "description": "DISPLAY ONLY (#646, question-hint@1): a best guess at the question this line belongs to, computed when the page is read and never stored. Present only where questionLabel is null and questionLabelSource is not learner. It is derived from the readers' readings, settled or not, and may be wrong; nothing is graded, decided or counted on it, and it is never sent to a reviewer. questionLabel stays the recorded label. Handwriting-derived, untrusted, displayed as text only.",
              "maxLength": 32,
              "minLength": 1,
              "type": "string"
            },
            "questionLabel": {
              "description": "Which question of the exercise this line belongs to (#406 slice 3, question-label@3), or null when none is: the learner's own label for the line if they set one, else the marker the line starts with (\"שאלה 5ב\", \"3)\", \"(ב)\", a circled number), else the label carried down from the nearest line above that set one. It is shown so the learner can correct it (POST /homework/pages/{pageId}/regions/{regionId}/label), and it is part of what confirming the page covers. Handwriting or learner-typed content, untrusted, displayed as text only. A label never changes a verdict: it only says which question a note belongs to.",
              "maxLength": 32,
              "minLength": 1,
              "type": [
                "string",
                "null"
              ]
            },
            "questionLabelSource": {
              "description": "Where questionLabel came from: learner (the learner set or cleared it on this line - cleared is the one learner case with a null label), reader_marker (the line starts with a marker), carried (from a line above) or none (no label is in force).",
              "enum": [
                "learner",
                "reader_marker",
                "carried",
                "none"
              ],
              "type": "string"
            },
            "readNotChecked": {
              "const": true,
              "description": "Present, and true, only when the check does not use this line (#465): the readers' readings are unsettled, but no claim cites it under any reading, so it is left out of the check. It reads as resolved, has no effectiveText, and is never shown to the learner as a step. This is the READERS' reading, not the learner's word and not a rejection; the learner may still correct the line.",
              "type": "boolean"
            },
            "regionId": {
              "description": "homework_transcription_regions.region_id - the address of POST /homework/pages/{pageId}/regions/{regionId}/corrections. Regions arrive in reading order.",
              "maxLength": 36,
              "minLength": 36,
              "type": "string"
            },
            "resolved": {
              "description": "True when this region is settled: it is agreed, or the learner has corrected it, or the learner has confirmed its single candidate text as read, or the learner has said its single candidate text is not on their page (not_written - then it stands for no text and has no effectiveText).",
              "type": "boolean"
            },
            "secondReading": {
              "additionalProperties": false,
              "description": "What the second reader said for this stretch of ink. Present for a region that is not agreed, when that reader had a line here.",
              "properties": {
                "markedUncertain": {
                  "description": "The reader itself said it was unsure of this line.",
                  "type": "boolean"
                },
                "text": {
                  "description": "UNTRUSTED handwriting content, exactly as the reader returned it.",
                  "maxLength": 4000,
                  "type": "string"
                },
                "uncertainTokens": {
                  "description": "The doubtful tokens this reader listed for the line. UNTRUSTED handwriting content, shown for the learner to judge. Empty when it listed none.",
                  "items": {
                    "maxLength": 4000,
                    "type": "string"
                  },
                  "type": "array"
                }
              },
              "required": [
                "text",
                "markedUncertain",
                "uncertainTokens"
              ],
              "type": "object"
            }
          },
          "required": [
            "regionId",
            "bbox",
            "agreement",
            "resolved",
            "learnerCorrected",
            "identicalPartnerRegionId",
            "bboxApproximate",
            "questionLabel",
            "questionLabelSource"
          ],
          "type": "object"
        },
        "type": "array"
      },
      "regionsDigest": {
        "description": "A digest of exactly what this response shows: each region's id, agreement and effective text. Send it back as regionsDigest when confirming the page; the server refuses a confirmation whose digest is not the current one (409 transcription_changed). Opaque to the client.",
        "maxLength": 64,
        "minLength": 64,
        "type": "string"
      },
      "uploadId": {
        "description": "The upload this page belongs to.",
        "maxLength": 36,
        "minLength": 36,
        "type": "string"
      },
      "widthPx": {
        "description": "Width in pixels of the page image every bounding box below is in the coordinates of.",
        "type": "integer"
      }
    },
    "required": [
      "pageId",
      "uploadId",
      "pageIndex",
      "widthPx",
      "heightPx",
      "policyVersion",
      "readers",
      "readingComplete",
      "regions",
      "allResolved",
      "confirmed",
      "regionsDigest",
      "coordinates"
    ],
    "title": "HomeworkTranscriptionResponse",
    "type": "object"
  },
  HomeworkTriageResponse: {
    "additionalProperties": false,
    "description": "How DaVinci will handle each question of an uploaded homework (#469, homework-triage@1), worked out without a model from the confirmed transcription and shown BEFORE the check. It says how a question is HANDLED, never how it was answered: can_verify means a deterministic check can decide a line of the question, feedback_only means the question gets a pointer to look again and no verdict, cant_read means a page or line could not be read well enough to be sent anywhere. It is not a grade, a verdict or a status, and carries no count or percentage. questions is empty when the triage has not been computed for the upload's current transcription (POST /homework/{id}/triage computes it; GET never does).",
    "properties": {
      "policyVersion": {
        "description": "The triage policy this list was derived under (homework-triage@N).",
        "minLength": 1,
        "type": "string"
      },
      "questions": {
        "description": "One entry per question label, in the order the lines appear.",
        "items": {
          "additionalProperties": false,
          "properties": {
            "questionLabel": {
              "description": "The question label as the learner confirmed it, or null for the lines that carry no label.",
              "type": [
                "string",
                "null"
              ]
            },
            "triageClass": {
              "description": "How the question will be handled.",
              "enum": [
                "can_verify",
                "feedback_only",
                "cant_read"
              ],
              "type": "string"
            }
          },
          "required": [
            "questionLabel",
            "triageClass"
          ],
          "type": "object"
        },
        "type": "array"
      },
      "uploadId": {
        "description": "The homework upload.",
        "minLength": 1,
        "type": "string"
      }
    },
    "required": [
      "uploadId",
      "policyVersion",
      "questions"
    ],
    "title": "HomeworkTriageResponse",
    "type": "object"
  },
  HomeworkUploadResponse: {
    "additionalProperties": false,
    "description": "One uploaded homework PDF and where each of its pages has got to (#376, R1; the answer to POST /homework and GET /homework/{id}). An upload is an OBSERVATION: the learner put these bytes into DaVinci at this moment. Nothing on this response is a judgement about the work - there is no grade, no correctness and no status of the learner's knowledge - and uploaded work is never independent evidence (D31): that conclusion belongs to deriveIndependence alone, from the channel the server records from WHICH ENDPOINT received the upload, so no channel field is carried here and none can be submitted. `state` is derived from the rows the reading job writes, not stored: `processing` until every page is rendered and both readers have been heard from, `ready` when they have, `failed` when a page could not be rendered (the file is then not read further). A page being `ready` means its transcription may be REVIEWED, not that it is right: nothing is graded until the learner has confirmed the page's transcription (GET /homework/pages/{pageId}/transcription).",
    "properties": {
      "createdAt": {
        "description": "ISO-8601 UTC instant the upload was accepted (homework_uploads.uploaded_at). An observation; the start of the clock a reading pace is measured from.",
        "maxLength": 20,
        "minLength": 20,
        "type": "string"
      },
      "failureKind": {
        "description": "Present only when state is failed: why a page could not be rendered. A closed vocabulary; page_too_large is #251's per-page pixel limit.",
        "enum": [
          "page_too_large",
          "pdf_unreadable",
          "render_failed",
          "renderer_not_installed",
          "invalid_result",
          "transport",
          "unclassified"
        ],
        "type": "string"
      },
      "pageCount": {
        "description": "The number of pages pdfium reported when the upload was accepted. A fact about the file, like byteSize in import-upload-response.json - never a measure of the learner's progress.",
        "type": "integer"
      },
      "pages": {
        "description": "The pages rendered so far, in page order. Fewer than pageCount while the job is still running.",
        "items": {
          "additionalProperties": false,
          "properties": {
            "heightPx": {
              "description": "Height in pixels of the same image.",
              "type": "integer"
            },
            "pageId": {
              "description": "homework_pages.page_id - a uuidv7, always exactly 36 characters. Address of GET /homework/pages/{pageId}/transcription.",
              "maxLength": 36,
              "minLength": 36,
              "type": "string"
            },
            "pageIndex": {
              "description": "0-based position of the page in the PDF.",
              "type": "integer"
            },
            "readAt": {
              "description": "ISO-8601 UTC instant the last of this page's readers finished (the latest homework_page_readings.read_at), or null while the page is still being read. A measurement of when, used only to estimate time left; never a judgement.",
              "maxLength": 20,
              "minLength": 20,
              "type": [
                "string",
                "null"
              ]
            },
            "readers": {
              "description": "Each reader heard from on this page - its CURRENT reading: the succeeded one if it has one, otherwise its latest failed attempt - naming the model that was asked and the version the provider says answered (invariant 3). A reader that failed is listed with its failureKind; its lines do not exist. Earlier failed attempts stay on record and are not listed here.",
              "items": {
                "additionalProperties": false,
                "properties": {
                  "coordinateSpace": {
                    "description": "Where this reading's boxes appear to live. pixels: they fit the page image. suspect: every box fits inside 0-1000 although the image is larger than 1000 px on both axes, so the reader probably returned normalised coordinates; the boxes are NOT rescaled and the reading is not failed (its text is valid), but the positions it gave are approximate. not_applicable: the reading failed and has no boxes.",
                    "enum": [
                      "pixels",
                      "suspect",
                      "not_applicable"
                    ],
                    "type": "string"
                  },
                  "failureKind": {
                    "description": "Present only when outcome is failed.",
                    "enum": [
                      "consent_required",
                      "budget_refused",
                      "api_key_missing",
                      "paid_tier_unattested",
                      "image_unreadable",
                      "remote_call_failed",
                      "response_blocked",
                      "response_truncated",
                      "empty_response",
                      "invalid_output",
                      "settings_echo_mismatch",
                      "unclassified"
                    ],
                    "type": "string"
                  },
                  "outcome": {
                    "description": "succeeded or failed.",
                    "enum": [
                      "succeeded",
                      "failed"
                    ],
                    "type": "string"
                  },
                  "promptVersion": {
                    "description": "The name of the fixed instruction the reader was given.",
                    "maxLength": 128,
                    "minLength": 1,
                    "type": "string"
                  },
                  "readerName": {
                    "description": "The provider.",
                    "enum": [
                      "gemini",
                      "anthropic",
                      "openai"
                    ],
                    "type": "string"
                  },
                  "readerSlot": {
                    "description": "primary or second - which of the two independent readings this was.",
                    "enum": [
                      "primary",
                      "second"
                    ],
                    "type": "string"
                  },
                  "reportedModelVersion": {
                    "description": "The version the provider's own response reported. Absent when it reported none - never the requested id relabelled as confirmed.",
                    "maxLength": 128,
                    "minLength": 1,
                    "type": "string"
                  },
                  "requestedModel": {
                    "description": "The model id the pin file named and the request asked for.",
                    "maxLength": 128,
                    "minLength": 1,
                    "type": "string"
                  }
                },
                "required": [
                  "coordinateSpace",
                  "outcome",
                  "promptVersion",
                  "readerName",
                  "readerSlot",
                  "requestedModel"
                ],
                "type": "object"
              },
              "type": "array"
            },
            "reviewStage": {
              "description": "Where this page is on the way to being gradable: reading (a reader has not been heard from yet), unreadable (both readers were heard from and BOTH failed, so there is nothing on the page to review - each reader's failureKind says why, and POST /homework/{id}/read-again tries again), awaiting_review (read; the learner has not confirmed what was read as it now stands), or confirmed (the learner confirmed the transcription exactly as it now stands). Confirmed does not mean right - it means the learner has seen it and said so, which is what grading requires.",
              "enum": [
                "reading",
                "unreadable",
                "awaiting_review",
                "confirmed"
              ],
              "type": "string"
            },
            "widthPx": {
              "description": "Width in pixels of the page image the readers were shown; every bounding box on the transcription is in this image's coordinates.",
              "type": "integer"
            }
          },
          "required": [
            "pageId",
            "pageIndex",
            "widthPx",
            "heightPx",
            "reviewStage",
            "readers"
          ],
          "type": "object"
        },
        "type": "array"
      },
      "state": {
        "description": "Derived from the rows, never stored: processing, ready, or failed.",
        "enum": [
          "processing",
          "ready",
          "failed"
        ],
        "type": "string"
      },
      "uploadId": {
        "description": "homework_uploads.upload_id - a uuidv7, always exactly 36 characters.",
        "maxLength": 36,
        "minLength": 36,
        "type": "string"
      }
    },
    "required": [
      "uploadId",
      "pageCount",
      "createdAt",
      "state",
      "pages"
    ],
    "title": "HomeworkUploadResponse",
    "type": "object"
  },
  ImportQuerystring: {
    "additionalProperties": false,
    "description": "The query string POST /imports accepts (#141, #144; fix pass on #144's schema-reviewer/boundary-reviewer findings, tracked as C1). Neither field is marked 'required' here: title is required unless sourceId is given, sourceId is optional, and both together is refused — that REQUIRED-UNLESS-EACH-OTHER relationship is business logic import-store.ts's createImport checks (the same choice study-session-store.ts makes for availableSeconds), not something a JSON Schema required array can name a reason for. additionalProperties: false is what makes Ajv refuse an unknown query parameter. Before this schema existed, POST /imports' querystring was a hand-written object literal inside apps/server/src/http/import-routes.ts, entirely outside packages/contracts: openapi.json's own path item carried no 'parameters' key at all, so every call through the generated client sent no query string and returned 400 — a required input sitting outside the single source of truth. Published here so it reaches the OpenAPI document (as an object-style query parameter, style: form, explode: true, schema: $ref to this component — OpenAPI 3.1's spelling for 'each of this object's properties is its own query parameter'), the generated client, and schema-rules.test.ts's per-file rules.",
    "properties": {
      "sourceId": {
        "description": "Names an existing sources row this upload is a rescan of, in place of title. Bounded the same as every other sourceId field in this API (import-runs-response.json, import-upload-response.json) — a uuidv7 string, always exactly 36 characters. A value of the right shape naming no source of this learner is refused by createImport with validation_failed, not by this schema, which cannot see the learner's rows.",
        "maxLength": 36,
        "minLength": 36,
        "type": "string"
      },
      "title": {
        "description": "The title of a new source, when this upload is not a rescan of an existing one. Bounded the same as sourceTitle in import-runs-response.json — the two describe the same sources.title column, one as a caller's input and one as the server's echo of it back. Blank-after-trim (including a blank formed only by zero-width or bidi-control code points) is refused by createImport, not by this schema: JSON Schema has no 'not composed only of these code points' keyword, and minLength: 1 alone would not catch a title of nothing but U+200B.",
        "maxLength": 500,
        "minLength": 1,
        "type": "string"
      }
    },
    "title": "ImportQuerystring",
    "type": "object"
  },
  ImportRunsResponse: {
    "additionalProperties": false,
    "description": "The answer to GET /imports (#144, Track C1; BACKEND_DESIGN §5.2): every import run this learner has started, newest started first. lifecycleState is read from import_runs.lifecycle_state directly — never inferred from which other rows happen to exist — and pageCount/heldPageCount are counts of observations reported beside the state, never used to derive it and never a ratio or a percentage of one another.",
    "properties": {
      "runs": {
        "description": "One entry per import_runs row this learner owns.",
        "items": {
          "additionalProperties": false,
          "properties": {
            "failureDetail": {
              "description": "import_runs.failure_detail — free text authored by whichever stage failed. Non-null ONLY IF lifecycleState is 'failed' (import_runs_failure_iff_failed, migration 0017, requires failure_detail IS NULL whenever lifecycle_state <> 'failed') — corrected here (#144 fix pass, schema-reviewer finding C3) from a claimed 'exactly when' (iff): the migration does not require the converse, and a failed row with a NULL failure_detail is legal (only failure_kind is required non-null on a failed row).",
              "maxLength": 2000,
              "type": [
                "string",
                "null"
              ]
            },
            "failureKind": {
              "description": "import_runs.failure_kind — non-null exactly when lifecycleState is 'failed'.",
              "enum": [
                "rasterisation_failed",
                "ocr_failed",
                "structure_proposal_failed",
                "manual_fallback_required",
                null
              ],
              "type": [
                "string",
                "null"
              ]
            },
            "heldPageCount": {
              "description": "The count of distinct source_pages among pageCount that have an unresolved source_page_holds row (resolved_at IS NULL — that table's own resolution semantics). A count of observations, never a ratio of pageCount and never rendered as one.",
              "type": "integer"
            },
            "importRunId": {
              "description": "import_runs.import_run_id.",
              "maxLength": 36,
              "minLength": 36,
              "type": "string"
            },
            "lifecycleState": {
              "description": "import_runs.lifecycle_state, read from the column directly (BACKEND_DESIGN §5.2). Never computed from source_pages/source_page_holds counts — a view that inferred this from what data happens to exist would be a conclusion wearing a status. 'cancelled' (#373) is a run that was still queued when the learner withdrew consent for feedback; it is terminal and is not a failure.",
              "enum": [
                "uploaded",
                "extracting",
                "candidate_review",
                "ready_to_publish",
                "published",
                "failed",
                "cancelled"
              ],
              "type": "string"
            },
            "originalFilename": {
              "description": "source_files.original_filename. Null on every file imported through POST /imports, which reads no filename from the request (#141) — present in the contract because the column is nullable, not because anything writes it today.",
              "maxLength": 500,
              "minLength": 1,
              "type": [
                "string",
                "null"
              ]
            },
            "pageCount": {
              "description": "COUNT(*) of source_pages rows for this run's source_file_id — a count of observations, reported beside the lifecycle state, never used to derive it. Not a percentage, not a ratio, not compared against any total.",
              "type": "integer"
            },
            "sha256": {
              "description": "source_files.sha256 — the content address of the uploaded PDF this run is processing.",
              "maxLength": 64,
              "minLength": 64,
              "type": "string"
            },
            "sourceFileId": {
              "description": "import_runs.source_file_id — the exact PDF this run is carrying through the pipeline.",
              "maxLength": 36,
              "minLength": 36,
              "type": "string"
            },
            "sourceId": {
              "description": "import_runs.source_id — the work this run is importing.",
              "maxLength": 36,
              "minLength": 36,
              "type": "string"
            },
            "sourceTitle": {
              "description": "sources.title, joined in so the screen can group runs under the book's own name rather than a bare id. Free text — never branched on.",
              "maxLength": 500,
              "minLength": 1,
              "type": "string"
            },
            "startedAt": {
              "description": "import_runs.started_at — when this run began.",
              "format": "date-time",
              "type": "string"
            },
            "stateEnteredAt": {
              "description": "import_runs.state_entered_at — when this run last entered lifecycleState.",
              "format": "date-time",
              "type": "string"
            }
          },
          "required": [
            "importRunId",
            "sourceId",
            "sourceTitle",
            "sourceFileId",
            "sha256",
            "originalFilename",
            "lifecycleState",
            "failureKind",
            "failureDetail",
            "startedAt",
            "stateEnteredAt",
            "pageCount",
            "heldPageCount"
          ],
          "type": "object"
        },
        "maxItems": 500,
        "type": "array"
      }
    },
    "required": [
      "runs"
    ],
    "title": "ImportRunsResponse",
    "type": "object"
  },
  ImportUploadResponse: {
    "additionalProperties": false,
    "description": "The answer to POST /imports (#141, amended #144): where the uploaded PDF landed, the validation facts about it, and the rows #144 writes on top of the blob store — a sources row (new or named by the caller), a source_files row, and an import_runs row at 'uploaded'. The digest is still the address those rows reference. Nothing here names a filename, a path, or a directory, because nothing the client sent named one either (BACKEND_DESIGN §14.2's write-side rule: a client cannot choose where a stored file lands).",
    "properties": {
      "alreadyStored": {
        "description": "True when a blob was already present under this address before this request — BlobStore#has, called before BlobStore#put (import-routes.ts), so the answer describes the state this upload found rather than the state it left. This is the observable proof that re-uploading identical bytes stores one copy: has() is a presence check, not an integrity check (blob-store.ts explains why), so true here means 'an address was occupied', never 'the same bytes were verified present'.",
        "type": "boolean"
      },
      "byteSize": {
        "description": "The size in bytes of the stored PDF, exactly as BlobStore#put reported it. A fact about content already on disk, not a judgement about the learner or their evidence — see RESPONSE_CONTENT_FACT_FIELDS in packages/contracts/src/schema-rules.test.ts for why this is allowed to be the first integer this contract emits in a response and why that exception is kept narrow. No bound: NUMERIC_KEYWORDS in that same file excludes minimum/maximum, on the same reasoning session-request.json's availableSeconds carries none.",
        "type": "integer"
      },
      "importRunId": {
        "description": "import_runs.import_run_id — the pipeline run for this file (#144, BACKEND_DESIGN §5.2). ~~the new pipeline run this upload started, at lifecycle_state 'uploaded'~~ — amended in the #144 fix pass: a run is at 'uploaded' only when this upload actually started it. When resolvedToExistingImport is true the run already existed and may have advanced well past 'uploaded', so this id must not be assumed newly started. GET /imports is where its state is tracked from here.",
        "maxLength": 36,
        "minLength": 36,
        "type": "string"
      },
      "mediaType": {
        "const": "application/pdf",
        "description": "Always application/pdf — the one media type POST /imports accepts. A const rather than an open string so a future importable type is a deliberate schema change, not a value this endpoint quietly starts returning.",
        "type": "string"
      },
      "resolvedToExistingImport": {
        "description": "True when this upload wrote no rows because these exact bytes were already on record for this learner (#144 fix pass): sourceId, sourceFileId and importRunId then name rows an earlier upload created, and any ?title= sent with this request was NOT used. Distinct from alreadyStored, which is a fact about the BLOB and is true on paths that DO create a new source — the two answer different questions, and only this one says whether a book was created. Reported rather than refused so an ordinary retry stays non-blocking; the rule it protects is that a caller must never believe a new import began when none did.",
        "type": "boolean"
      },
      "sha256": {
        "description": "Hex, lower case, exactly 64 characters — the content address BlobStore#put (apps/server/src/blobs/blob-store.ts) stored these bytes under. No pattern: the no-magnitude rule forbids a digit-admitting pattern in any schema (schema-rules.test.ts), the same convention export-response.json's own sha256 field already established — the length bound carries the real check, and BlobStore#get re-hashes on every read regardless.",
        "maxLength": 64,
        "minLength": 64,
        "type": "string"
      },
      "sourceFileId": {
        "description": "source_files.source_file_id — the row naming this exact PDF as a copy of sourceId (#144). ~~the new row this upload created~~ — amended in the #144 fix pass: when resolvedToExistingImport is true this upload created no row at all and this is the earlier one, so read the flag before treating this id as new. A rescan (#225 §4) is a second sourceFileId against the same sourceId, never a second sha256 on the same row.",
        "maxLength": 36,
        "minLength": 36,
        "type": "string"
      },
      "sourceId": {
        "description": "sources.source_id — the work this file was imported as a copy of (#144). Newly created UNLESS the caller named an existing source with ?sourceId=, or resolvedToExistingImport is true (these bytes were already on record for this learner, so this names the source a previous upload created).",
        "maxLength": 36,
        "minLength": 36,
        "type": "string"
      }
    },
    "required": [
      "sha256",
      "byteSize",
      "mediaType",
      "alreadyStored",
      "sourceId",
      "sourceFileId",
      "importRunId",
      "resolvedToExistingImport"
    ],
    "title": "ImportUploadResponse",
    "type": "object"
  },
  LearnerDeletionPreviewResponse: {
    "additionalProperties": false,
    "description": "What deleting the signed-in learner's own account would remove, and what would survive it (#373, BACKEND_DESIGN section 14.4). Names and presence only - never a row, never a value, never a filename, and never a count (the no-magnitude rule; issue #24 carries whether a deletion preview is the one place a count is legitimate). The token is single-use, bound to this learner, and expires server-side. A learner cannot reach this for anyone but themselves: the request has no body and the learner is the session's.",
    "properties": {
      "confirmationPhrase": {
        "description": "The exact phrase the confirmation must echo back. Dictated by the server so the guarantee cannot be edited out of a client.",
        "maxLength": 64,
        "minLength": 1,
        "type": "string"
      },
      "keepsSharedFiles": {
        "description": "Whether any uploaded file this learner's records name would be KEPT because another record still names it (another learner's, or published course material). A file kept this way is not this learner's data.",
        "type": "boolean"
      },
      "outsideOurControl": {
        "description": "What DaVinci cannot delete: server backups until they expire, anything already sent to an AI provider, and anything the learner downloaded. Kept consistent with the consent statement's storage disclosures.",
        "items": {
          "maxLength": 512,
          "type": "string"
        },
        "maxItems": 32,
        "type": "array"
      },
      "removesUploadedFiles": {
        "description": "Whether any uploaded file that only this learner's records name would be deleted along with them.",
        "type": "boolean"
      },
      "retainedByDesign": {
        "description": "What survives this deletion by design, each with the reason: the sign-up time on the invite that created the account, audit rows that matched no account, and policy bindings that are not this learner's. Shown before the learner decides, because a deletion screen silent about its limits implies a completeness it does not have.",
        "items": {
          "additionalProperties": false,
          "properties": {
            "reason": {
              "maxLength": 512,
              "type": "string"
            },
            "table": {
              "maxLength": 128,
              "type": "string"
            }
          },
          "required": [
            "table",
            "reason"
          ],
          "type": "object"
        },
        "maxItems": 256,
        "type": "array"
      },
      "tablesHoldingData": {
        "description": "Which tables hold something of this learner's, read before anything is removed. The deletion removes every row of theirs in each, their sessions and sign-in, and the account itself.",
        "items": {
          "maxLength": 128,
          "type": "string"
        },
        "maxItems": 256,
        "type": "array"
      },
      "token": {
        "additionalProperties": false,
        "description": "Single-use, bound to this learner, and expires server-side; the client shows the deadline but never enforces it.",
        "properties": {
          "expiresAt": {
            "format": "date-time",
            "type": "string"
          },
          "value": {
            "maxLength": 128,
            "minLength": 16,
            "type": "string"
          }
        },
        "required": [
          "value",
          "expiresAt"
        ],
        "type": "object"
      }
    },
    "required": [
      "tablesHoldingData",
      "removesUploadedFiles",
      "keepsSharedFiles",
      "retainedByDesign",
      "outsideOurControl",
      "confirmationPhrase",
      "token"
    ],
    "title": "LearnerDeletionPreviewResponse",
    "type": "object"
  },
  LearnerDeletionRequest: {
    "additionalProperties": false,
    "description": "Confirms the deletion of the signed-in learner's own account and data (#373), after the preview showed what it would remove. The same two fields as the whole-installation deletion and for the same reasons: the token proves this confirmation follows a preview the learner actually saw (it is minted by the preview, bound to this learner, single-use, and expires server-side), and the retyped phrase proves a person decided. There is no learner id field: the learner is the session's, so there is no input through which a request could name anyone else.",
    "properties": {
      "confirmationPhrase": {
        "description": "The exact phrase the screen asked for. Compared server-side so the check cannot be skipped by a client that decides to be helpful.",
        "maxLength": 64,
        "minLength": 1,
        "type": "string"
      },
      "token": {
        "description": "The value from the preview's token. Checked server-side for existence, expiry, single use, and that it was issued to this learner.",
        "maxLength": 128,
        "minLength": 16,
        "type": "string"
      }
    },
    "required": [
      "token",
      "confirmationPhrase"
    ],
    "title": "LearnerDeletionRequest",
    "type": "object"
  },
  LearnerDeletionResponse: {
    "additionalProperties": false,
    "description": "The receipt for deleting the signed-in learner's account (#373). Content-free in the same sense as the whole-installation receipt: table names, kinds and timestamps - no row, no value, no digest, no filename, no learner id and no username. The session that made this request is already ended when it arrives: the next request is unauthenticated.",
    "properties": {
      "appVersion": {
        "maxLength": 64,
        "type": "string"
      },
      "deletedAt": {
        "format": "date-time",
        "type": "string"
      },
      "outsideOurControl": {
        "items": {
          "maxLength": 512,
          "type": "string"
        },
        "maxItems": 32,
        "type": "array"
      },
      "receiptWritten": {
        "description": "Whether the copy of this receipt reached disk. False never means the deletion did not happen: the receipt is written after the rows are gone.",
        "type": "boolean"
      },
      "retainedByDesign": {
        "items": {
          "additionalProperties": false,
          "properties": {
            "reason": {
              "maxLength": 512,
              "type": "string"
            },
            "table": {
              "maxLength": 128,
              "type": "string"
            }
          },
          "required": [
            "table",
            "reason"
          ],
          "type": "object"
        },
        "maxItems": 256,
        "type": "array"
      },
      "schemaVersion": {
        "description": "The migration tag the installation was at. Named schemaVersion rather than migration for the reason delete-all-data-response.json gives.",
        "maxLength": 128,
        "type": "string"
      },
      "storageScrubbed": {
        "description": "Whether the freed database pages and the write-ahead log were scrubbed after the deletion (checkpoint, truncate and VACUUM). False means the rows are gone but bytes may still be recoverable from the database's own free space until the next successful scrub; the server logs why.",
        "type": "boolean"
      },
      "tablesEmptied": {
        "description": "Which tables held data of this learner's that the deletion removed, read before the removal. Names only.",
        "items": {
          "maxLength": 128,
          "type": "string"
        },
        "maxItems": 256,
        "type": "array"
      }
    },
    "required": [
      "deletedAt",
      "appVersion",
      "schemaVersion",
      "receiptWritten",
      "storageScrubbed",
      "tablesEmptied",
      "retainedByDesign",
      "outsideOurControl"
    ],
    "title": "LearnerDeletionResponse",
    "type": "object"
  },
  LearnerStatus: {
    "enum": [
      "not_tested",
      "learning",
      "can_use_with_help",
      "can_use_independently",
      "retained"
    ],
    "title": "LearnerStatus",
    "type": "string"
  },
  LoginRequest: {
    "additionalProperties": false,
    "description": "Body of POST /auth/login (#371, D30). Two fields and nothing else: additionalProperties is false, so a client cannot smuggle in a learner id — the learner a session belongs to is decided by which account the credentials match, never by the request. The bounds are deliberately loose: username allows any text up to 64 characters and password any non-empty text up to 256, because a login form that rejects a value as malformed before checking it tells an attacker something about the account policy, and a mistyped password must fail exactly as a wrong one does. The 12-character floor and the username alphabet are enforced where an account is created (SignupRequest), not here.",
    "properties": {
      "password": {
        "maxLength": 256,
        "minLength": 1,
        "type": "string"
      },
      "username": {
        "description": "Case-insensitive. The server folds it (Unicode NFC, then lower case) before looking it up.",
        "maxLength": 64,
        "minLength": 1,
        "type": "string"
      }
    },
    "required": [
      "username",
      "password"
    ],
    "title": "LoginRequest",
    "type": "object"
  },
  LogoutResponse: {
    "additionalProperties": false,
    "description": "Answer to POST /auth/logout (#371). The session is revoked server-side and both cookies are cleared in the same response.",
    "properties": {
      "signedOut": {
        "const": true,
        "type": "boolean"
      }
    },
    "required": [
      "signedOut"
    ],
    "title": "LogoutResponse",
    "type": "object"
  },
  MoodleConnectionResponse: {
    "additionalProperties": false,
    "description": "Answer of GET /moodle/connection and DELETE /moodle/connection (#497): whether the signed-in learner has connected their own Moodle account. Never carries the token, in whole or in part. The times are when a connection was made and when this learner's files were last synced; they are facts about the connection, not about what the learner knows.",
    "properties": {
      "connected": {
        "description": "Whether a Moodle token is held for this learner.",
        "type": "boolean"
      },
      "connectedAt": {
        "description": "When the token was connected (UTC, ISO 8601). Absent when not connected.",
        "type": "string"
      },
      "lastSyncedAt": {
        "description": "When this learner's Moodle files were last synced (UTC, ISO 8601). Absent when nothing has been synced.",
        "type": "string"
      },
      "securityKeysUrl": {
        "description": "Where the learner opens their own Moodle security keys page (the configured Moodle base plus /user/managetoken.php), so the key flow can link to it. A public page address, never a secret and never the token. Null when the configured Moodle base is not a well-formed https address, in which case the screen describes the path in words.",
        "type": [
          "string",
          "null"
        ]
      }
    },
    "required": [
      "connected"
    ],
    "title": "MoodleConnectionResponse",
    "type": "object"
  },
  MoodleConnectRequest: {
    "additionalProperties": false,
    "description": "Body of POST /moodle/connection (#497): the learner's own Moodle web-service token, created in Moodle (Preferences > User account > Security keys, service moodle_mobile_app). Surrounding whitespace is trimmed; anything that is not 32 hexadecimal characters is refused. The token is a full-account credential: it is never logged, echoed or returned.",
    "properties": {
      "token": {
        "description": "The Moodle token.",
        "maxLength": 200,
        "minLength": 1,
        "type": "string"
      }
    },
    "required": [
      "token"
    ],
    "title": "MoodleConnectRequest",
    "type": "object"
  },
  MoodleConnectResponse: {
    "additionalProperties": false,
    "description": "Answer of POST /moodle/connection (#497): the connection is saved and the learner's first sync has run. Two facts about the sync as booleans, because the contracts carry no number at all (no count can be mistaken for a measure of what the learner knows); no file name, URL or token.",
    "properties": {
      "anyFilesRecorded": {
        "description": "Whether the sync found any course file, new or already recorded.",
        "type": "boolean"
      },
      "anyReadFailed": {
        "description": "Whether any file or course listing could not be read. The learner can connect again to retry.",
        "type": "boolean"
      },
      "connected": {
        "description": "Always true on success.",
        "type": "boolean"
      },
      "connectedAt": {
        "description": "When the token was connected (UTC, ISO 8601).",
        "type": "string"
      },
      "syncedAt": {
        "description": "When the sync that followed ran (UTC, ISO 8601).",
        "type": "string"
      }
    },
    "required": [
      "connected",
      "connectedAt",
      "syncedAt",
      "anyFilesRecorded",
      "anyReadFailed"
    ],
    "title": "MoodleConnectResponse",
    "type": "object"
  },
  MyDataResponse: {
    "additionalProperties": false,
    "description": "Everything DaVinci keeps about the signed-in learner, for display on screen (#585: Israeli PPL section 13 inspection is met by a display, not a download). What the learner DID (observations) and what DaVinci CONCLUDED from it (inferences) are separate sections and never share a row shape's meaning (invariant 2); every inference row carries its policy version id among its fields (invariant 3). Bookkeeping and DaVinci's own configuration are not shown, and neither is any credential. A row that holds content DaVinci delivers as help (a reviewer's reason, a check's certificate, a stored practice answer, a delivered hint) or a claim-derived weak point is listed with `withheld` set and that content left out, always, whatever the learner has opened: this view shows that things exist and what happened, never DaVinci's answer-bearing content, so reading it is never a way to get help unrecorded (invariant 1). Every value is a string: this carries no magnitude about what the learner knows.",
    "properties": {
      "consents": {
        "items": {
          "additionalProperties": false,
          "properties": {
            "decision": {
              "maxLength": 64,
              "type": "string"
            },
            "purpose": {
              "maxLength": 64,
              "type": "string"
            },
            "recordedAt": {
              "maxLength": 64,
              "type": "string"
            },
            "statementVersion": {
              "maxLength": 128,
              "type": "string"
            }
          },
          "required": [
            "purpose",
            "decision",
            "statementVersion",
            "recordedAt"
          ],
          "type": "object"
        },
        "maxItems": 1024,
        "type": "array"
      },
      "identity": {
        "additionalProperties": false,
        "properties": {
          "displayName": {
            "maxLength": 128,
            "type": [
              "string",
              "null"
            ]
          },
          "language": {
            "maxLength": 32,
            "type": [
              "string",
              "null"
            ]
          },
          "tables": {
            "description": "The rest of the learner's own records that are neither something they did nor a conclusion: course choices with colours and short names, uploaded sources, and the like.",
            "items": {
              "$ref": "https://davinci.local/schemas/my-data-table.json"
            },
            "maxItems": 256,
            "type": "array"
          },
          "username": {
            "maxLength": 128,
            "type": [
              "string",
              "null"
            ]
          }
        },
        "required": [
          "username",
          "displayName",
          "language",
          "tables"
        ],
        "type": "object"
      },
      "inferences": {
        "description": "What DaVinci concluded about the learner, each row with its evidence references and the policy version it was derived under.",
        "items": {
          "$ref": "https://davinci.local/schemas/my-data-table.json"
        },
        "maxItems": 256,
        "type": "array"
      },
      "observations": {
        "description": "What the learner did: uploads, confirmations and corrections, help opened, what they said about their own confidence, disputes, practice attempts.",
        "items": {
          "$ref": "https://davinci.local/schemas/my-data-table.json"
        },
        "maxItems": 256,
        "type": "array"
      },
      "pages": {
        "description": "The learner's uploaded homework pages, each with the URL of the existing page-image endpoint.",
        "items": {
          "additionalProperties": false,
          "properties": {
            "courseRef": {
              "maxLength": 128,
              "type": [
                "string",
                "null"
              ]
            },
            "imageUrl": {
              "maxLength": 256,
              "type": "string"
            },
            "pageId": {
              "maxLength": 128,
              "type": "string"
            },
            "uploadedAt": {
              "maxLength": 64,
              "type": [
                "string",
                "null"
              ]
            },
            "uploadId": {
              "maxLength": 128,
              "type": "string"
            }
          },
          "required": [
            "pageId",
            "uploadId",
            "uploadedAt",
            "courseRef",
            "imageUrl"
          ],
          "type": "object"
        },
        "maxItems": 4096,
        "type": "array"
      },
      "scopeOfClaim": {
        "description": "SEC-027: every conclusion in `inferences` is scoped to help DaVinci itself delivered or observed.",
        "maxLength": 500,
        "minLength": 1,
        "type": "string"
      }
    },
    "required": [
      "scopeOfClaim",
      "observations",
      "inferences",
      "identity",
      "consents",
      "pages"
    ],
    "title": "MyDataResponse",
    "type": "object"
  },
  MyDataTable: {
    "additionalProperties": false,
    "description": "One table of the learner's own rows in the My data view (#585): a readable label and each row's time and readable fields. Part of MyDataResponse and no operation's request body.",
    "properties": {
      "label": {
        "maxLength": 128,
        "type": "string"
      },
      "rows": {
        "items": {
          "additionalProperties": false,
          "properties": {
            "fields": {
              "description": "Readable column values, shortened, in column order. Null columns are left out.",
              "items": {
                "additionalProperties": false,
                "properties": {
                  "name": {
                    "maxLength": 128,
                    "type": "string"
                  },
                  "value": {
                    "maxLength": 300,
                    "type": "string"
                  }
                },
                "required": [
                  "name",
                  "value"
                ],
                "type": "object"
              },
              "maxItems": 128,
              "type": "array"
            },
            "id": {
              "maxLength": 256,
              "type": "string"
            },
            "when": {
              "maxLength": 64,
              "type": [
                "string",
                "null"
              ]
            },
            "withheld": {
              "description": "Set when this row's revealing fields are left out. `feedback_unopened` and `check_unopened`: the learner has not opened the feedback or the check yet. `help_content`: the row holds content DaVinci delivers as help (a reason, a certificate, a stored answer, a delivered hint), which is never shown here; it is seen where the product offers it, and seeing it there is recorded as help. `source_text_excluded`: text taken from a source (OCR text, a page folio, a proposed import payload) is not shown here.",
              "enum": [
                "feedback_unopened",
                "check_unopened",
                "help_content",
                "source_text_excluded",
                null
              ],
              "type": [
                "string",
                "null"
              ]
            }
          },
          "required": [
            "id",
            "when",
            "fields",
            "withheld"
          ],
          "type": "object"
        },
        "maxItems": 1000,
        "type": "array"
      },
      "table": {
        "maxLength": 128,
        "type": "string"
      },
      "truncated": {
        "description": "True when the table held more rows than one response carries.",
        "type": "boolean"
      }
    },
    "required": [
      "table",
      "label",
      "rows",
      "truncated"
    ],
    "title": "MyDataTable",
    "type": "object"
  },
  ObjectiveEvidenceResponse: {
    "additionalProperties": false,
    "description": "Answer of GET /objectives/{id}/evidence (BACKEND_DESIGN §8, §12, #33) — for every slice of the objective, the events GET /objectives/{id}/status's projection actually cited: what was asked, what the learner answered, what the verdict was, when, and what help (if any) preceded it. Never the expected response — reaching that from the evidence view is a content delivery through POST /content-deliveries with surface evidence_view and no attemptId, recorded and classified as an exposure there (§6.3), not returned inline here. isIndependent/answerWasRevealed/independenceReason/independencePolicyVersion/independenceScopePolicyVersion are deriveIndependence's own output (packages/domain/src/assistance.ts) plus the window policy selectAssistanceBefore applied (D24, #43, #106), re-derived per cited event exactly as the projection derived it — see SERVER_DERIVED_ALLOWED in schema-rules.test.ts for why a bare boolean would be refused here and this pairing is not.",
    "properties": {
      "objectiveId": {
        "description": "learning_objectives.objective_id.",
        "maxLength": 36,
        "minLength": 36,
        "type": "string"
      },
      "scopeOfClaim": {
        "description": "SEC-027 — same statement and same reason as objective-status-response.json's field of the same name.",
        "maxLength": 500,
        "minLength": 1,
        "type": "string"
      },
      "slices": {
        "items": {
          "additionalProperties": false,
          "properties": {
            "citedEvents": {
              "description": "SliceProjection.basisCitation and .contradicting for this slice, each enriched with what produced it. Empty for not_tested, and for learning with no qualifying negative on record.",
              "items": {
                "additionalProperties": false,
                "properties": {
                  "activityId": {
                    "description": "activity_definitions.activity_id for the attempt this event belongs to — which activity's content `prompt` is. Added at the Phase 1 exit gate (#67 boundary-reviewer finding) so the client can name what it is asking to reveal: FRONTEND_DESIGN §4.5 collapses `prompt` behind `Show source/answer` outside an activity, and activating that control records the reveal through `POST /content-deliveries`, which needs an `activityId` to resolve. Never the expected response itself — see this schema's own top-level description for why that is never returned inline.",
                    "maxLength": 36,
                    "minLength": 36,
                    "type": "string"
                  },
                  "answerBearingResponse": {
                    "description": "True when showing responseText would also show the answer, so the client must collapse it behind the same recorded disclosure the prompt uses. Added 2026-08-17 (invariant-auditor on the #69 fix): #33 held that a learner's own response is learner data and belongs on this page, which is right and incomplete — a correct response is character-identical to the expected text, so 'Answered: coffee' is the recognition answer. The server decides this; the client is never given an expected response to compare against. Computed over-cautiously as (verdict is correct) OR (the text grades correct against the stored expected response), because a false gate annoys and a missed gate manufactures evidence.",
                    "type": "boolean"
                  },
                  "answerWasRevealed": {
                    "description": "deriveIndependence's floor (D15, #67) — true when answer-revealing help preceded this response. Not the negation of isIndependent: between them lies every informative rung.",
                    "type": "boolean"
                  },
                  "eventId": {
                    "description": "response_events.event_id.",
                    "maxLength": 36,
                    "minLength": 36,
                    "type": "string"
                  },
                  "independencePolicyVersion": {
                    "description": "ASSISTANCE_LADDER_VERSION — the ladder this event's independence was decided under.",
                    "maxLength": 64,
                    "minLength": 1,
                    "type": "string"
                  },
                  "independenceReason": {
                    "description": "deriveIndependence's own reason string — invariant 3's 'names its evidence' applied to independence, and this response's answer to 'what help preceded it'.",
                    "maxLength": 400,
                    "minLength": 1,
                    "type": "string"
                  },
                  "independenceScopePolicyVersion": {
                    "description": "INDEPENDENCE_SCOPE_POLICY_VERSION (D24, #43, #106) — the window that bounds how far outside this event's own attempt selectAssistanceBefore looked for a contaminating exposure. A second, separate policy version from independencePolicyVersion, never concatenated with it — the composite would not resolve against policy_versions.policy_version.",
                    "maxLength": 64,
                    "minLength": 1,
                    "type": "string"
                  },
                  "isIndependent": {
                    "description": "Server-derived by deriveIndependence alone, re-derived for this event exactly as the projection read it. No client may submit this in any spelling (SERVER_DERIVED_FIELDS).",
                    "type": "boolean"
                  },
                  "occurredAt": {
                    "description": "response_events.occurred_at.",
                    "format": "date-time",
                    "type": "string"
                  },
                  "prompt": {
                    "description": "prompt_versions.prompt_text for the attempt this event belongs to — what was asked. Never the expected response.",
                    "maxLength": 2000,
                    "minLength": 1,
                    "type": "string"
                  },
                  "responseKind": {
                    "description": "response_events.response_kind.",
                    "enum": [
                      "typed",
                      "selected",
                      "declared_unknown"
                    ],
                    "type": "string"
                  },
                  "responseText": {
                    "description": "response_events.response_text — the learner's own answer. Learner data, not an inference (§6.2 includes it in the standard export).",
                    "maxLength": 2000,
                    "type": "string"
                  },
                  "role": {
                    "description": "Why the projection cited this event — mastery_hypothesis_evidence.role.",
                    "enum": [
                      "basis",
                      "contradicting"
                    ],
                    "type": "string"
                  },
                  "verdict": {
                    "description": "evaluations.verdict for the evaluation this citation read. null only if the cited evaluation cannot be resolved, which the projection's own qualifying rules make unreachable in practice — carried nullable rather than assumed non-null, because a conclusion asserting more certainty than the query can support is the failure this instrument exists to avoid.",
                    "enum": [
                      "correct",
                      "incorrect",
                      "partially_correct",
                      "uncertain",
                      null
                    ],
                    "type": [
                      "string",
                      "null"
                    ]
                  }
                },
                "required": [
                  "eventId",
                  "role",
                  "occurredAt",
                  "activityId",
                  "prompt",
                  "responseKind",
                  "responseText",
                  "verdict",
                  "isIndependent",
                  "answerWasRevealed",
                  "independenceReason",
                  "independencePolicyVersion",
                  "independenceScopePolicyVersion",
                  "answerBearingResponse"
                ],
                "type": "object"
              },
              "maxItems": 51,
              "type": "array"
            },
            "sliceId": {
              "description": "objective_slices.slice_id.",
              "maxLength": 36,
              "minLength": 36,
              "type": "string"
            },
            "statusPolicyVersion": {
              "description": "STATUS_POLICY_VERSION — the status-eligibility policy that decided each cited event's role. Added 2026-08-16 (invariant-auditor, Phase 1 exit): 'basis' and 'contradicting' are conclusions of projectSliceStatus, not facts about the events, and every other conclusion in this response is versioned (each cited event carries independencePolicyVersion and independenceScopePolicyVersion). The version moved @1 to @2 with D15, which changed which events contradict — so without this field two evidence responses with identical roles can be answers to different questions with nothing to tell them apart.",
              "maxLength": 64,
              "minLength": 1,
              "type": "string"
            }
          },
          "required": [
            "sliceId",
            "citedEvents",
            "statusPolicyVersion"
          ],
          "type": "object"
        },
        "maxItems": 20,
        "minItems": 1,
        "type": "array"
      }
    },
    "required": [
      "objectiveId",
      "scopeOfClaim",
      "slices"
    ],
    "title": "ObjectiveEvidenceResponse",
    "type": "object"
  },
  ObjectiveStatusResponse: {
    "additionalProperties": false,
    "description": "Answer of GET /objectives/{id}/status (BACKEND_DESIGN §8, §8.1, §11, §12, #33). The envelope schema-rules.test.ts's STATUS_REF_ALLOWED ban was waiting for: every status here — the roll-up and each slice's own — travels with its evidence state, basis and contradicting event ids, policy version, and a one-sentence reason (invariant 3), never as a bare enum value. Where a status rests on an independent retrieval, retentionInterval carries the two instants the retention delay spans rather than the delay itself: NUMERIC_FIELDS confines allowlisted integers to request schemas, so BACKEND_DESIGN §8.1's 'the exact delay is always shown and exported' is met by the interval, and a renderer computes the exact duration from the two timestamps without a magnitude ever crossing the wire. scopeOfClaim is SEC-027 (D11, #39, docs/PROJECT_CONTEXT.md §3): every conclusion here is scoped to help DaVinci itself delivered or observed, carried beside the conclusion at the API rather than left to UI copy that may or may not render it.",
    "properties": {
      "mixedEvidence": {
        "description": "True when the required slices disagree (ObjectiveRollUp.note === 'mixed_evidence'). A note attached to the status, spelled as a boolean rather than as the literal string schema-rules.test.ts's NOT_A_STATUS bans from every schema — never a sixth status and never ordered against the five.",
        "type": "boolean"
      },
      "objectiveId": {
        "description": "learning_objectives.objective_id.",
        "maxLength": 36,
        "minLength": 36,
        "type": "string"
      },
      "policyVersion": {
        "description": "STATUS_POLICY_VERSION — the precedence rule every slice below was projected under (invariant 3).",
        "maxLength": 64,
        "minLength": 1,
        "type": "string"
      },
      "scopeOfClaim": {
        "description": "SEC-027: the scope of every conclusion in this response — help DaVinci delivered or observed, never help it could not see. Prose for a human, like nextEvidenceNeeded below; guaranteed non-empty; never a quality dimension and never ordered against anything.",
        "maxLength": 500,
        "minLength": 1,
        "type": "string"
      },
      "slices": {
        "description": "One entry per objective_slices row. EVID-004: production is never inferred from recognition, so each slice's evidence is shown on its own terms rather than collapsed into the roll-up alone.",
        "items": {
          "additionalProperties": false,
          "properties": {
            "basisEventIds": {
              "description": "SliceProjection.basisEventIds — the events this conclusion rests on, in the order they were read (invariant 3).",
              "items": {
                "maxLength": 36,
                "minLength": 36,
                "type": "string"
              },
              "maxItems": 20,
              "type": "array"
            },
            "contradictingEventIds": {
              "description": "SliceProjection.contradicting, by event id — qualifying evidence that points the other way, named even when it did not change the outcome.",
              "items": {
                "maxLength": 36,
                "minLength": 36,
                "type": "string"
              },
              "maxItems": 50,
              "type": "array"
            },
            "evidenceState": {
              "description": "SliceProjection.evidenceState — §8's four evidence states. Not a status and never shown as one.",
              "enum": [
                "none",
                "limited",
                "conflicting",
                "sufficient_for_label"
              ],
              "type": "string"
            },
            "nextEvidenceNeeded": {
              "description": "SliceProjection.nextEvidenceNeeded. Plain language, guaranteed non-empty (mastery_hypotheses_next_evidence_not_empty). Not to be parsed.",
              "maxLength": 400,
              "minLength": 1,
              "type": "string"
            },
            "policyVersion": {
              "description": "SliceProjection.policyVersion for this slice (STATUS_POLICY_VERSION) — carried per slice too, so one slice's evidence is self-describing on its own and not only as part of the envelope.",
              "maxLength": 64,
              "minLength": 1,
              "type": "string"
            },
            "reason": {
              "description": "SliceProjection.reason — one sentence, invariant 3's 'names ... why'.",
              "maxLength": 400,
              "minLength": 1,
              "type": "string"
            },
            "requiredForObjectiveStatus": {
              "description": "objective_slices.required_for_objective_status — whether this slice counts toward the roll-up above.",
              "type": "boolean"
            },
            "retentionInterval": {
              "additionalProperties": false,
              "description": "The two instants §8.1's retention delay spans: the latest relevant learning opportunity, and this slice's basis retrieval. Present only when the status rests on an independent retrieval with a prior opportunity to measure from (can_use_independently or retained); null otherwise. The exact delay is always available without a magnitude ever crossing the wire — a renderer subtracts the two timestamps.",
              "properties": {
                "anchoredAt": {
                  "description": "The latest relevant learning opportunity strictly before the basis retrieval (§6.3, §10.3) — occurred_at of a prior response or content delivery on this objective.",
                  "format": "date-time",
                  "type": "string"
                },
                "demonstratedAt": {
                  "description": "The basis event's own occurred_at — response_events.occurred_at for the retrieval this status rests on.",
                  "format": "date-time",
                  "type": "string"
                }
              },
              "required": [
                "anchoredAt",
                "demonstratedAt"
              ],
              "type": [
                "object",
                "null"
              ]
            },
            "sliceId": {
              "description": "objective_slices.slice_id.",
              "maxLength": 36,
              "minLength": 36,
              "type": "string"
            },
            "status": {
              "$ref": "https://davinci.local/schemas/learner-status.json"
            }
          },
          "required": [
            "sliceId",
            "requiredForObjectiveStatus",
            "status",
            "evidenceState",
            "basisEventIds",
            "contradictingEventIds",
            "policyVersion",
            "reason",
            "nextEvidenceNeeded",
            "retentionInterval"
          ],
          "type": "object"
        },
        "maxItems": 20,
        "minItems": 1,
        "type": "array"
      },
      "status": {
        "$ref": "https://davinci.local/schemas/learner-status.json"
      }
    },
    "required": [
      "objectiveId",
      "status",
      "mixedEvidence",
      "policyVersion",
      "scopeOfClaim",
      "slices"
    ],
    "title": "ObjectiveStatusResponse",
    "type": "object"
  },
  PractisableActivitiesResponse: {
    "additionalProperties": false,
    "description": "Answer of GET /practisable-activities (#186, sub-issue of #83/Track D1). Lists every activity /today can start a session against — activity_definitions joined through objective_slices and learning_objectives to units — so the browser has a legitimate way to learn which activity/slice to practise instead of hardcoding the seeded UUIDs scripts/phase1-walkthrough.ps1 uses. FRONTEND_DESIGN §4.4: 'Plan cards contain only non-answer-bearing section/activity metadata. They never preview the target form, passage, prompt, expected answer, vocabulary answer, or hint.' This response carries no promptText, no expected response, and no hint of either — nothing here answers 'what is the prompt' or 'what is the expected response', only 'what could be practised, and where does it sit in the curriculum'. objectiveStatement is the one exception worth naming explicitly: it is safe to render un-gated because GET /progress/summary already renders it un-gated on every summary view, and phase1-seed.ts's own header records that its wording was deliberately chosen to name no slice's expected text.",
    "properties": {
      "activities": {
        "description": "Every practisable activity for the single local learner, ordered by units.sort_key, then learning_objectives.sort_key, then activity_definitions.subtype — deterministic and never by id.",
        "items": {
          "additionalProperties": false,
          "properties": {
            "activityId": {
              "description": "activity_definitions.activity_id — the id POST /sessions' items.activityId expects.",
              "maxLength": 36,
              "minLength": 36,
              "type": "string"
            },
            "family": {
              "description": "activity_definitions.family (activity_definitions_family_valid CHECK, migration 0001).",
              "enum": [
                "vocabulary",
                "grammar",
                "reading",
                "sentence_production"
              ],
              "type": "string"
            },
            "gradeable": {
              "description": "Whether this activity's current expected response version is gradeable (migration 0028's predicate, D26, #182): at least one content_provenance row names it, and none of them have an authority outside textbook/answer_key/approved_correction/founder_authored. An activity with gradeable: false is still listed here — it is practisable, per #182's requirement that an exercise with no confirmed answer remain usable for practice — this flag carries no answer-bearing content (a boolean is not the expected text) and exists only so that the exclusion from grading is visible here and in GET /answer-key-completeness rather than silent.",
              "type": "boolean"
            },
            "objectiveId": {
              "description": "learning_objectives.objective_id that this activity's primary slice belongs to.",
              "maxLength": 36,
              "minLength": 36,
              "type": "string"
            },
            "objectiveSliceId": {
              "description": "activity_definitions.primary_slice_id (objective_slices.slice_id) — this activity's primary scored slice, and the id POST /sessions' items.objectiveSliceId expects alongside activityId (BACKEND_DESIGN §6.2: it must be this activity's primary scored slice).",
              "maxLength": 36,
              "minLength": 36,
              "type": "string"
            },
            "objectiveStatement": {
              "description": "learning_objectives.statement — see this schema's own description for why it is safe to render un-gated.",
              "maxLength": 500,
              "minLength": 1,
              "type": "string"
            },
            "responseMode": {
              "description": "activity_definitions.response_mode (activity_definitions_response_mode_valid CHECK, migration 0001) — what kind of control #187 will need, never rendered here as one.",
              "enum": [
                "choice",
                "short_text",
                "sentence"
              ],
              "type": "string"
            },
            "subtype": {
              "description": "activity_definitions.subtype. Which values are legal depends on family (activity_definitions_family_subtype_valid CHECK, migration 0001: vocabulary -> recognition_target_to_support/production_support_to_target/meaning_in_context/gender/number/collocation/sentence_use; grammar -> recognition/form_completion/independent_production/error_correction/new_sentence_use; reading -> main_idea/detail_retrieval/vocabulary_grammar_in_context/short_comprehension_answer; sentence_production -> controlled_completion/transformation/prompted_answer/short_original_sentence). This enum is the union across every family rather than a conditional on family, because JSON Schema composition keywords (allOf/oneOf/if-then-else) are forbidden in this package (schema-rules.test.ts) — the (family, subtype) pairing itself is enforced only by the database CHECK this response is reading back.",
              "enum": [
                "recognition_target_to_support",
                "production_support_to_target",
                "meaning_in_context",
                "gender",
                "number",
                "collocation",
                "sentence_use",
                "recognition",
                "form_completion",
                "independent_production",
                "error_correction",
                "new_sentence_use",
                "main_idea",
                "detail_retrieval",
                "vocabulary_grammar_in_context",
                "short_comprehension_answer",
                "controlled_completion",
                "transformation",
                "prompted_answer",
                "short_original_sentence"
              ],
              "type": "string"
            },
            "unitTitle": {
              "description": "units.title, reached by activity_definitions → objective_slices → learning_objectives → units — the same path attempt-start-response.json's sourceIndicator.unitTitle uses.",
              "maxLength": 200,
              "minLength": 1,
              "type": "string"
            }
          },
          "required": [
            "activityId",
            "objectiveSliceId",
            "objectiveId",
            "objectiveStatement",
            "unitTitle",
            "family",
            "subtype",
            "responseMode",
            "gradeable"
          ],
          "type": "object"
        },
        "maxItems": 500,
        "type": "array"
      }
    },
    "required": [
      "activities"
    ],
    "title": "PractisableActivitiesResponse",
    "type": "object"
  },
  ProfileResponse: {
    "additionalProperties": false,
    "description": "Identity configuration only. This response carries no status and no isIndependent: BACKEND_DESIGN §5 gives the identity module the profile and forbids it learner performance, and §11 derives status per objective slice, never per learner. A status here would also violate invariant 3, because a bare label names neither its evidence nor its policy version — a slice status returns basis IDs, evidence state, exact delay, and next evidence needed alongside the label. isIndependent is per-attempt and comes only from deriveIndependence, which takes the assistance delivered before one response; a profile has no attempt to derive it from, so any value put here would be manufactured (invariant 1). Both belong on the objective-status endpoints, not on identity.",
    "properties": {
      "displayName": {
        "description": "Optional (BACKEND_DESIGN §6.1): learning records reference only the opaque learnerId, and this name can be deleted without rewriting the learning ledger.",
        "maxLength": 64,
        "minLength": 1,
        "type": "string"
      },
      "learnerId": {
        "format": "uuid",
        "type": "string"
      },
      "supportLanguage": {
        "pattern": "^[a-z]{2}(-[A-Z]{2})?$",
        "type": "string"
      },
      "targetLanguage": {
        "pattern": "^[a-z]{2}(-[A-Z]{2})?$",
        "type": "string"
      },
      "uiLanguage": {
        "pattern": "^[a-z]{2}(-[A-Z]{2})?$",
        "type": "string"
      }
    },
    "required": [
      "learnerId",
      "targetLanguage",
      "supportLanguage",
      "uiLanguage"
    ],
    "title": "ProfileResponse",
    "type": "object"
  },
  ProfileUpdateRequest: {
    "additionalProperties": false,
    "description": "Body of PATCH /profile (BACKEND_DESIGN §12). Every property is settable by the learner. additionalProperties is false so that a server-derived field — isIndependent above all — cannot be smuggled in by a client that sends one; that keyword is the enforcement, and packages/contracts/src/schema-rules.test.ts asserts it is present on every request schema.",
    "minProperties": 1,
    "properties": {
      "displayName": {
        "description": "Optional. `null` clears it; an absent key leaves it unchanged. The distinction is load-bearing rather than tidy: BACKEND_DESIGN §6.1 quarantines display_name in its own table precisely so it can be deleted without rewriting anything in the learning ledger, and a schema with no way to express removal makes that separation decorative — the only way to remove a name would be delete-all-data, which removes the evidence too, which is the exact coupling §6.1 exists to break. minLength stays at 1 so the column never holds two spellings of no-name (issue #23).",
        "maxLength": 64,
        "minLength": 1,
        "type": [
          "string",
          "null"
        ]
      },
      "supportLanguage": {
        "pattern": "^[a-z]{2}(-[A-Z]{2})?$",
        "type": "string"
      },
      "targetLanguage": {
        "pattern": "^[a-z]{2}(-[A-Z]{2})?$",
        "type": "string"
      },
      "uiLanguage": {
        "pattern": "^[a-z]{2}(-[A-Z]{2})?$",
        "type": "string"
      }
    },
    "title": "ProfileUpdateRequest",
    "type": "object"
  },
  ProgressSummaryResponse: {
    "additionalProperties": false,
    "description": "Answer of GET /progress/summary (BACKEND_DESIGN §12, #33) — every known objective with its current status, for the Progress destination's list. Each entry names the deciding slice's policy version and reason (packages/domain/src/projection.ts's SliceProjection, drawn from whichever required slice's own status equals the roll-up), so a status never appears bare here either. Full drill-down — every slice, every basis and contradicting event — is GET /objectives/{id}/status and GET /objectives/{id}/evidence's job, not this list's.",
    "properties": {
      "objectives": {
        "items": {
          "additionalProperties": false,
          "properties": {
            "mixedEvidence": {
              "description": "Same meaning as objective-status-response.json's field of the same name.",
              "type": "boolean"
            },
            "objectiveId": {
              "description": "learning_objectives.objective_id.",
              "maxLength": 36,
              "minLength": 36,
              "type": "string"
            },
            "policyVersion": {
              "description": "The deciding slice's SliceProjection.policyVersion (STATUS_POLICY_VERSION).",
              "maxLength": 64,
              "minLength": 1,
              "type": "string"
            },
            "reason": {
              "description": "The deciding slice's SliceProjection.reason — the required slice whose own status equals the roll-up status above.",
              "maxLength": 400,
              "minLength": 1,
              "type": "string"
            },
            "statement": {
              "description": "learning_objectives.statement.",
              "maxLength": 500,
              "minLength": 1,
              "type": "string"
            },
            "status": {
              "$ref": "https://davinci.local/schemas/learner-status.json"
            }
          },
          "required": [
            "objectiveId",
            "statement",
            "status",
            "mixedEvidence",
            "policyVersion",
            "reason"
          ],
          "type": "object"
        },
        "maxItems": 500,
        "type": "array"
      },
      "scopeOfClaim": {
        "description": "SEC-027, stated once for the whole list rather than once per row: every status below is scoped to help DaVinci itself delivered or observed.",
        "maxLength": 500,
        "minLength": 1,
        "type": "string"
      }
    },
    "required": [
      "scopeOfClaim",
      "objectives"
    ],
    "title": "ProgressSummaryResponse",
    "type": "object"
  },
  ResponseSubmissionRequest: {
    "additionalProperties": false,
    "description": "Body of POST /attempts/{id}/responses (BACKEND_DESIGN §6.2, §6.4, §7, §11.1, §12; FRONTEND_DESIGN §4.5, §5, #63). responseText must be empty exactly when responseKind is declared_unknown (response_events_declared_unknown_has_no_text) — this schema cannot express that conditional (no composition keywords), so the store validates it before the insert reaches the CHECK, the same way it validates availableSeconds' bound in the sessions store. confidence is recorded here, on the request, before any feedback is computed — the ordering §12 requires is structural: nothing past the request can un-write it (FRONTEND_DESIGN §4.5's ordinal three-choice control, never arithmetic). timingSegments/focusLossCount/answerEditCount/clockAnomaly are FRONTEND_DESIGN §5's AttemptTelemetryController output: monotonic segments, focus-loss count, and edit count as the browser measured them — observations the client reports, never a server judgement, which is what makes the integers here request-only rather than a magnitude this API ever emits.",
    "properties": {
      "answerEditCount": {
        "description": "FRONTEND_DESIGN §5's edit count: changes to the response, not raw keystrokes or typing cadence.",
        "type": "integer"
      },
      "clockAnomaly": {
        "description": "True when the client detected its own clock moving unexpectedly (system clock change, sleep/wake) during this attempt. Recorded, never corrected (BACKEND_DESIGN assumption 5).",
        "type": "boolean"
      },
      "confidence": {
        "description": "response_events.confidence (response_events_confidence_valid). FRONTEND_DESIGN §4.5's ordinal three-choice control, collected before any correctness feedback is shown. An ordinal label, never a magnitude — see schema-rules.test.ts's ORDINAL_LABEL_FIELDS.",
        "enum": [
          "not_sure",
          "somewhat_sure",
          "sure"
        ],
        "type": "string"
      },
      "focusLossCount": {
        "description": "FRONTEND_DESIGN §5's focus-loss count for this attempt's response — a count the client observed, not a server judgement.",
        "type": "integer"
      },
      "responseKind": {
        "description": "response_events.response_kind (response_events_response_kind_valid).",
        "enum": [
          "typed",
          "selected",
          "declared_unknown"
        ],
        "type": "string"
      },
      "responseText": {
        "description": "response_events.response_text. Empty exactly when responseKind is declared_unknown — validated by the store, not expressible here.",
        "maxLength": 2000,
        "minLength": 0,
        "type": "string"
      },
      "timingSegments": {
        "description": "FRONTEND_DESIGN §5's monotonic active/idle/focused/blurred segments, checkpointed on submit. May be empty when the client reported no segments at all (§6.4's 'missing' timing quality is exactly this case).",
        "items": {
          "additionalProperties": false,
          "properties": {
            "clientEndMs": {
              "description": "timing_segments.client_end_ms — same clock, not before clientStartMs (timing_segments_end_not_before_start).",
              "type": "integer"
            },
            "clientStartMs": {
              "description": "timing_segments.client_start_ms — the client's own monotonic clock, milliseconds. Never a wall clock.",
              "type": "integer"
            },
            "segmentKind": {
              "description": "timing_segments.segment_kind (timing_segments_kind_valid).",
              "enum": [
                "active",
                "idle",
                "focused",
                "blurred"
              ],
              "type": "string"
            }
          },
          "required": [
            "segmentKind",
            "clientStartMs",
            "clientEndMs"
          ],
          "type": "object"
        },
        "maxItems": 200,
        "minItems": 0,
        "type": "array"
      }
    },
    "required": [
      "responseKind",
      "responseText",
      "confidence",
      "timingSegments",
      "focusLossCount",
      "answerEditCount",
      "clockAnomaly"
    ],
    "title": "ResponseSubmissionRequest",
    "type": "object"
  },
  ResponseSubmissionResponse: {
    "additionalProperties": false,
    "description": "Answer of POST /attempts/{id}/responses (BACKEND_DESIGN §6.2, §6.3, §7, §11.1, §12, #63; #182/D26 for the graded/ungraded split). No expected response, no assistance level, no percentage — deterministic evaluation stops at rung 2 (packages/domain/src/evaluation.ts) and this response never reaches past it. graded is the required discriminant #182 adds: an exercise with no confirmed answer (migration 0028's gradeability predicate) is still practisable, but produces no gradeable evidence, and that has to be visible in this response rather than silently absent. When graded is false, verdict/feedback/isIndependent/independenceReason/independencePolicyVersion/independenceScopePolicyVersion/evidenceQuality/errorCodes are all absent — no verdict exists, so nothing may be stored or returned that implies one — and ungradedReason is present instead. JSON Schema composition keywords are banned in this package (schema-rules.test.ts), so this pairing cannot be expressed here; apps/server/src/http/study-session-routes.test.ts asserts both directions directly and is the thing standing in for the composition keyword this schema cannot use. isIndependent, when present, is derived exclusively by deriveIndependence (packages/domain/src/assistance.ts) from selectAssistanceBefore's selection over the ledger — nothing else may produce it, and no request schema in this API may declare it (schema-rules.test.ts's SERVER_DERIVED_FIELDS ban). independenceReason carries deriveIndependence's own reason string alongside the boolean, which is what invariant 3 requires and what unblocks this schema's entry in SERVER_DERIVED_ALLOWED: a bare boolean names no evidence, but the boolean plus its reason does. evidenceQuality is the export-facing composite ('usable'|'limited'|'excluded', schema.ts's EVIDENCE_QUALITY_LEVELS) — a different vocabulary from packages/domain/src/timing.ts's EvidenceQuality ('strong'|'adequate'|'weak'), which feeds into the timing dimension and never appears here directly. D24 (#43, #106) added independenceScopePolicyVersion alongside independencePolicyVersion, as a second field rather than a concatenation: since D24 the conclusion depends on two separately-versioned policies (the assistance ladder, and the delay window selectAssistanceBefore applies outside the current attempt), and a composite string would not resolve against policy_versions.policy_version, which is the exact failure that table exists to prevent.",
    "properties": {
      "errorCodes": {
        "description": "Present exactly when graded is true. Always empty at this evaluator rung (packages/domain/src/evaluation.ts: the error taxonomy is docs/research_protocol.md's to define, #8, and this module never invents a code).",
        "items": {
          "maxLength": 64,
          "minLength": 1,
          "type": "string"
        },
        "maxItems": 10,
        "type": "array"
      },
      "eventId": {
        "description": "response_events.event_id — the evidence event this response was recorded as, graded or not.",
        "maxLength": 36,
        "minLength": 36,
        "type": "string"
      },
      "evidenceQuality": {
        "description": "Present exactly when graded is true. The evidence_quality_assessments composite for this event ('usable'|'limited'|'excluded'), derived from the three independent quality dimensions (evaluation reliability, interaction validity, timing quality) computed in the same transaction as the response.",
        "enum": [
          "usable",
          "limited",
          "excluded"
        ],
        "type": "string"
      },
      "feedback": {
        "additionalProperties": false,
        "description": "Present exactly when graded is true. The feedback delivered to the learner, generic and never echoing the expected response, an approved variant, or the learner's own text (packages/domain/src/evaluation.ts's composeFeedback).",
        "properties": {
          "text": {
            "maxLength": 400,
            "minLength": 1,
            "type": "string"
          }
        },
        "required": [
          "text"
        ],
        "type": "object"
      },
      "graded": {
        "description": "True when this response was evaluated and an evaluations row was appended; false when the activity's current expected response is not gradeable (migration 0028's predicate, D26, #182) — the response was recorded as an observation and no verdict exists. The discriminant this schema cannot express with a composition keyword (schema-rules.test.ts bans them) — see this schema's own top-level description.",
        "type": "boolean"
      },
      "independencePolicyVersion": {
        "description": "Present exactly when graded is true. ASSISTANCE_LADDER_VERSION — the ladder and independence rule that produced isIndependent (invariant 3: every derived conclusion names its policy version). Added 2026-08-15 with D15 (#67), which changed what independence means at rungs 2-4: without this field, a body saying isIndependent false before D15 and one saying it after are answers to different questions with nothing to tell them apart.",
        "maxLength": 64,
        "minLength": 1,
        "type": "string"
      },
      "independenceReason": {
        "description": "Present exactly when graded is true. deriveIndependence's own reason string, carried beside the boolean (invariant 3: every derived conclusion names its evidence).",
        "maxLength": 400,
        "minLength": 1,
        "type": "string"
      },
      "independenceScopePolicyVersion": {
        "description": "Present exactly when graded is true. INDEPENDENCE_SCOPE_POLICY_VERSION (D24, #43, #106) — the window that bounds how far outside the current attempt selectAssistanceBefore looks for a contaminating exposure. A second, separate policy version from independencePolicyVersion: since D24 the independence conclusion is a function of both, and a version stamped independence-scope@2 must not be read as if it were @1. Never concatenated with independencePolicyVersion — the composite would not resolve against policy_versions.policy_version.",
        "maxLength": 64,
        "minLength": 1,
        "type": "string"
      },
      "isIndependent": {
        "description": "Present exactly when graded is true. Server-derived by deriveIndependence alone (CLAUDE.md, invariant 1). No client may submit this value in any spelling — see SERVER_DERIVED_FIELDS.",
        "type": "boolean"
      },
      "ungradedReason": {
        "description": "Present exactly when graded is false. Learner-facing prose naming which of migration 0028's two conjuncts the current expected response fails: no recorded provenance at all, or an authority the POC may not grade against (a model-proposed answer that was never confirmed, D26). Never a copy of the trigger's own ABORT text — see apps/server/src/curriculum/expected-response-gradeability.ts's UNGRADED_REASON_TEXT, the one place this wording is written.",
        "maxLength": 400,
        "minLength": 1,
        "type": "string"
      },
      "verdict": {
        "description": "Present exactly when graded is true. evaluations.verdict (evaluations_verdict_valid). The deterministic evaluator (rungs 1-2) never returns partially_correct — see packages/domain/src/evaluation.ts's DeterministicVerdict — but this resource states the full vocabulary a later evaluator rung may use.",
        "enum": [
          "correct",
          "incorrect",
          "partially_correct",
          "uncertain"
        ],
        "type": "string"
      }
    },
    "required": [
      "eventId",
      "graded"
    ],
    "title": "ResponseSubmissionResponse",
    "type": "object"
  },
  SessionRequest: {
    "additionalProperties": false,
    "description": "Body of POST /sessions (BACKEND_DESIGN §6.2, 2026-08-14 amendment, #62). Phase 1 has no scheduler (planning is Phase 4; intervals are #32), so the plan is this array: the client states which activity and objective slice each planned item is, in order. The server assigns each item's position from its index in this array (position = index + 1) and derives planned_item_count from its length — neither is a field here, because sending both would be two spellings of one fact the server already has, and both are integers a client does not get to choose (issue #61's amendment). scheduler_decisions stays empty for a session created this way, and that is the truthful record: migration 0003 requires a decision to match its item when one exists, never that one exists, and a session created through this endpoint genuinely had no scheduler behind it. There is no researchCondition field: docs/research_protocol.md (#8) does not exist yet, and a condition label against a protocol nobody has written would be a label naming no evidence.",
    "properties": {
      "availableSeconds": {
        "description": "The time the learner said they had, in seconds (FRONTEND_DESIGN §4.4) — a duration the learner states and the client reports, never a server judgement. Request-only: it is never on SessionResponse, because a number in a response would be the server stating a magnitude (invariant 4) — and because it is not on the response, it is not \"optional on the resource\" either, so the null-clearing rule for optional request fields does not reach it.",
        "type": "integer"
      },
      "items": {
        "description": "The plan, in order. The server assigns each item's position from this array's index (position = index + 1); reordering this array is how a caller changes the plan order.",
        "items": {
          "additionalProperties": false,
          "properties": {
            "activityId": {
              "description": "activity_definitions.activity_id. UUIDv7 (BACKEND_DESIGN §5.3) — the server resolves the format, not this schema.",
              "maxLength": 36,
              "minLength": 36,
              "type": "string"
            },
            "objectiveSliceId": {
              "description": "objective_slices.slice_id — must be this activity's primary scored slice (BACKEND_DESIGN §6.2). UUIDv7 — the server resolves the format, not this schema.",
              "maxLength": 36,
              "minLength": 36,
              "type": "string"
            }
          },
          "required": [
            "activityId",
            "objectiveSliceId"
          ],
          "type": "object"
        },
        "maxItems": 50,
        "minItems": 1,
        "type": "array"
      }
    },
    "required": [
      "items"
    ],
    "title": "SessionRequest",
    "type": "object"
  },
  SessionResponse: {
    "additionalProperties": false,
    "description": "The study-session resource: the answer of POST /sessions, GET /sessions/{id}, and POST /sessions/{id}/complete alike — one resource, three operations, never a second shape for completion (BACKEND_DESIGN §6.2, 2026-08-14 amendment, #62). items is ordered by position ascending, and that ordering is the only place position is expressed in this response — nothing here states how many items were planned versus how many exist, because the array is the answer to both.",
    "properties": {
      "appVersion": {
        "maxLength": 64,
        "minLength": 1,
        "type": "string"
      },
      "endedAt": {
        "description": "Null while the session is open. Set once, when the session completes (study_sessions.ended_at).",
        "maxLength": 20,
        "minLength": 20,
        "type": [
          "string",
          "null"
        ]
      },
      "items": {
        "description": "The session's items, ordered by position ascending — the only place position is expressed in this response. Its length answers both how many items were planned and how many exist.",
        "items": {
          "additionalProperties": false,
          "properties": {
            "activityId": {
              "maxLength": 36,
              "minLength": 36,
              "type": "string"
            },
            "objectiveSliceId": {
              "maxLength": 36,
              "minLength": 36,
              "type": "string"
            },
            "plannedAt": {
              "description": "session_items.occurred_at — when this item was placed in the plan. UTC ISO-8601.",
              "maxLength": 20,
              "minLength": 20,
              "type": "string"
            },
            "resolution": {
              "description": "session_items.resolution: one of \"completed\", \"skipped\", or \"abandoned\", or null while the item is not yet resolved (SESSION_ITEM_RESOLUTIONS in db/schema.ts is the vocabulary of record). Deliberately not an enum here — tsPropertyType renders a nullable string enum as a union that also carries the string literal \"null\" alongside the null type, which is wrong. A server test binds this description's vocabulary to SESSION_ITEM_RESOLUTIONS directly instead.",
              "maxLength": 9,
              "minLength": 7,
              "type": [
                "string",
                "null"
              ]
            },
            "resolutionReason": {
              "description": "session_items.resolution_reason — required for a skip, free text read by a human. Null when there is none.",
              "maxLength": 512,
              "minLength": 1,
              "type": [
                "string",
                "null"
              ]
            },
            "resolvedAt": {
              "description": "session_items.resolved_at. Null until the item is resolved.",
              "maxLength": 20,
              "minLength": 20,
              "type": [
                "string",
                "null"
              ]
            },
            "sessionItemId": {
              "maxLength": 36,
              "minLength": 36,
              "type": "string"
            }
          },
          "required": [
            "sessionItemId",
            "activityId",
            "objectiveSliceId",
            "plannedAt",
            "resolution",
            "resolutionReason",
            "resolvedAt"
          ],
          "type": "object"
        },
        "maxItems": 50,
        "minItems": 0,
        "type": "array"
      },
      "sessionId": {
        "maxLength": 36,
        "minLength": 36,
        "type": "string"
      },
      "startedAt": {
        "description": "study_sessions.occurred_at. UTC ISO-8601, YYYY-MM-DDTHH:MM:SSZ.",
        "maxLength": 20,
        "minLength": 20,
        "type": "string"
      }
    },
    "required": [
      "sessionId",
      "startedAt",
      "endedAt",
      "appVersion",
      "items"
    ],
    "title": "SessionResponse",
    "type": "object"
  },
  SignupRequest: {
    "additionalProperties": false,
    "description": "Body of POST /auth/signup (#371, D30). Self-service sign-up is gated, not open: the founder issues a single-use, expiring invite code out of band and the classmate redeems it here to create their own account. additionalProperties is false so no field can name a learner or a role — there are none. The password has a length rule and nothing else (12 to 256 characters, no composition rules); the server re-checks the length in code points because JSON Schema counts UTF-16 units. username is stored lower-cased, so any case is accepted on the way in.",
    "properties": {
      "displayName": {
        "description": "Optional. What the learner would like to be called; it lives in the profile and can be changed or cleared there (PATCH /profile). Absent means no name.",
        "maxLength": 64,
        "minLength": 1,
        "type": "string"
      },
      "inviteCode": {
        "description": "The code the founder handed over. 22 base64url characters. Never stored — only its SHA-256 is — and never echoed.",
        "maxLength": 64,
        "minLength": 8,
        "type": "string"
      },
      "password": {
        "maxLength": 256,
        "minLength": 12,
        "type": "string"
      },
      "username": {
        "description": "3 to 32 characters from letters, digits, dot, underscore and hyphen. Stored lower-cased; the account is looked up case-insensitively.",
        "pattern": "^[A-Za-z0-9._-]{3,32}$",
        "type": "string"
      }
    },
    "required": [
      "inviteCode",
      "username",
      "password"
    ],
    "title": "SignupRequest",
    "type": "object"
  },
  UsageResponse: {
    "additionalProperties": false,
    "description": "Answer of GET /usage (#443) - the learner's own budget usage meter. It is about money, not about what the learner knows: CLAUDE.md's no-percentage rule exempts exactly this (narrowed 2026-10-23; UX_EXPERIENCE §4.3a). It carries no dollar amount, no cap, and nothing about any other learner. `state` is `no_period` when no budget period covers today (and then nothing else is present), else `metered`. A metered body carries `usedPercent` (a whole number 0-100: how much of this learner's own share is used), `locked`, and, only when locked, why.",
    "properties": {
      "locked": {
        "description": "Present when state is `metered`. True when this learner's paid model calls are currently refused.",
        "type": "boolean"
      },
      "reason": {
        "description": "Present only when `locked` is true. `share_used`: this learner's own share is spent. `budget_paused`: the semester budget as a whole is exhausted.",
        "enum": [
          "share_used",
          "budget_paused"
        ],
        "type": "string"
      },
      "state": {
        "description": "`no_period`: no budget period covers today, so there is nothing to meter. `metered`: a period is active and this learner's share is being measured.",
        "enum": [
          "no_period",
          "metered"
        ],
        "type": "string"
      },
      "usedPercent": {
        "description": "Present when state is `metered`. The share of this learner's own budget used, floored to a whole number from 0 to 100. A money measure only; never a statement about learning.",
        "maximum": 100,
        "minimum": 0,
        "type": "integer"
      }
    },
    "required": [
      "state"
    ],
    "title": "UsageResponse",
    "type": "object"
  },
} as const;

export type ApiSchemaName = keyof typeof API_SCHEMAS;

/**
 * The error statuses each operation declares, as a runtime value.
 *
 * Emitted so the server can attach the error envelope as an actual
 * response schema on each route rather than restating the OpenAPI
 * document’s claim in a second place. A list of error responses that only
 * a generated document knows about is a description of the server; one the
 * server itself serialises through is a property of it.
 */
export const API_ERROR_RESPONSES = {
  health: ['403', '500'],
  bootstrap: ['401', '403', '500', '503'],
  profile: ['401', '403', '500', '503'],
  updateProfile: ['400', '401', '403', '415', '500', '503'],
  login: ['400', '401', '403', '415', '429', '500'],
  signup: ['400', '403', '409', '415', '429', '500'],
  logout: ['400', '401', '403', '415', '500'],
  authSession: ['401', '403', '500'],
  consentStatement: ['401', '403', '500'],
  consentDecisions: ['401', '403', '500'],
  recordConsentDecision: ['400', '401', '403', '409', '415', '500'],
  deletionPreview: ['400', '401', '403', '415', '500'],
  deleteAllData: ['400', '401', '403', '409', '415', '500'],
  exportData: ['400', '401', '403', '415', '500'],
  downloadExport: ['400', '401', '403', '409', '415', '500'],
  getMyData: ['401', '403', '500'],
  downloadMyData: ['401', '403', '500'],
  previewMyDeletion: ['400', '401', '403', '409', '415', '500'],
  deleteMyAccount: ['400', '401', '403', '409', '415', '500'],
  createSession: ['400', '401', '403', '409', '415', '500', '503'],
  getSession: ['401', '403', '404', '500', '503'],
  completeSession: ['400', '401', '403', '404', '409', '415', '500', '503'],
  startAttempt: ['400', '401', '403', '404', '409', '415', '500', '503'],
  createContentDelivery: ['400', '401', '403', '409', '415', '500', '503'],
  createContentPresentation: ['400', '401', '403', '409', '415', '500', '503'],
  requestAssistance: ['400', '401', '403', '404', '409', '415', '500', '503'],
  submitResponse: ['400', '401', '403', '404', '409', '415', '500', '503'],
  abandonAttempt: ['400', '401', '403', '404', '409', '415', '500', '503'],
  getObjectiveStatus: ['401', '403', '404', '500', '503'],
  getObjectiveEvidence: ['401', '403', '404', '500', '503'],
  getProgressSummary: ['401', '403', '500', '503'],
  getUsage: ['401', '403', '500', '503'],
  getCourses: ['401', '403', '500', '503'],
  updateCourse: ['400', '401', '403', '404', '409', '415', '500', '503'],
  getMoodleConnection: ['401', '403', '500', '503'],
  connectMoodle: ['400', '401', '403', '409', '415', '502', '500', '503'],
  disconnectMoodle: ['401', '403', '500', '503'],
  getCourseTopics: ['401', '403', '404', '500', '503'],
  getAssessments: ['401', '403', '500', '503'],
  createAssessment: ['400', '401', '403', '404', '409', '415', '500', '503'],
  withdrawAssessment: ['401', '403', '404', '409', '500', '503'],
  getExamPrepAssessments: ['401', '403', '500', '503'],
  createExamPrepPlan: ['400', '401', '403', '409', '415', '500', '503'],
  getExamPrepPlan: ['401', '403', '404', '500', '503'],
  reorderExamPrepTopics: ['400', '401', '403', '404', '409', '415', '500', '503'],
  startExamPrepSession: ['400', '401', '403', '404', '409', '415', '500', '503'],
  getExamPrepSession: ['401', '403', '404', '500', '503'],
  finishExamPrepSession: ['400', '401', '403', '404', '409', '415', '500', '503'],
  getExamPrepItem: ['401', '403', '404', '500', '503'],
  submitExamPrepAttempt: ['400', '401', '403', '404', '409', '415', '500', '503'],
  requestExamPrepHint: ['400', '401', '403', '404', '409', '415', '500', '503'],
  declareExamPrepUnknown: ['400', '401', '403', '404', '409', '415', '500', '503'],
  getPractisableActivities: ['401', '403', '500', '503'],
  getAnswerKeyCompleteness: ['401', '403', '500', '503'],
  createImport: ['400', '401', '403', '413', '415', '500', '503'],
  getImports: ['401', '403', '500', '503'],
  createHomework: ['400', '401', '403', '409', '413', '415', '500', '503'],
  listHomework: ['401', '403', '500', '503'],
  homeworkExercises: ['401', '403', '500', '503'],
  createHomeworkForExercise: ['400', '401', '403', '404', '409', '413', '415', '500', '503'],
  getHomework: ['401', '403', '404', '500', '503'],
  readHomeworkAgain: ['400', '401', '403', '404', '409', '415', '500', '503'],
  getHomeworkTranscription: ['401', '403', '404', '500', '503'],
  getHomeworkPageImage: ['401', '403', '404', '500', '503'],
  createHomeworkCorrection: ['400', '401', '403', '404', '409', '413', '415', '500', '503'],
  setHomeworkQuestionLabel: ['400', '401', '403', '404', '409', '413', '415', '500', '503'],
  confirmHomeworkPage: ['400', '401', '403', '404', '409', '413', '415', '500', '503'],
  recordHomeworkConfidence: ['400', '401', '403', '404', '409', '413', '415', '500', '503'],
  disputeHomeworkVerdict: ['400', '401', '403', '404', '409', '415', '500', '503'],
  checkHomework: ['400', '401', '403', '404', '409', '415', '500', '503'],
  getHomeworkFeedback: ['401', '403', '404', '500', '503'],
  pullHomeworkRung: ['400', '401', '403', '404', '409', '413', '415', '500', '503'],
  triageHomework: ['400', '401', '403', '404', '409', '415', '500', '503'],
  getHomeworkTriage: ['401', '403', '404', '500', '503'],
  getHomeworkFeedbackChannel: ['401', '403', '404', '500', '503'],
  openHomeworkFeedbackChannel: ['400', '401', '403', '404', '409', '415', '500', '503'],
  showHomeworkFeedbackReason: ['400', '401', '403', '404', '409', '415', '500', '503'],
  retryHomeworkFeedback: ['400', '401', '403', '404', '409', '415', '500', '503'],
} as const;

/**
 * Each operation's path in Fastify form (`/sessions/:id`), derived from
 * the OpenAPI form (`/sessions/{id}`) in scripts/lib.ts's ENDPOINTS table.
 *
 * This table exists so that #62's path-parameterised routes can register
 * from it rather than spelling `:id` in apps/server/src/http/routes.ts —
 * the same reason API_ERROR_RESPONSES above exists: a route pattern that
 * only a generated document knows about is a description of the server,
 * and one the server registers its handlers from is a property of it.
 * **No route registers from this table yet.** routes.ts still spells
 * every path itself; wiring it through here is #62's change, not #61's —
 * doing it here would put a route refactor inside a contract-layer issue.
 *
 * Every path below is relative to the server's API prefix: it does not
 * include the leading `/api/v1` the server applies itself (apps/server's
 * `apiPrefix` option, `API_PREFIX` in apps/server/src/http/app.ts). The
 * two path forms disagree on syntax only (`{name}` vs `:name`);
 * toFastifyPath in scripts/lib.ts is the one place that translation
 * happens.
 */
export const API_ROUTE_PATHS = {
  health: "/health",
  bootstrap: "/bootstrap",
  profile: "/profile",
  updateProfile: "/profile",
  login: "/auth/login",
  signup: "/auth/signup",
  logout: "/auth/logout",
  authSession: "/auth/session",
  consentStatement: "/consent/statement",
  consentDecisions: "/consent/decisions",
  recordConsentDecision: "/consent/decisions",
  deletionPreview: "/deletion-preview",
  deleteAllData: "/delete-all-data",
  exportData: "/export",
  downloadExport: "/export/download",
  getMyData: "/me/data",
  downloadMyData: "/me/data/download",
  previewMyDeletion: "/me/deletion/preview",
  deleteMyAccount: "/me/deletion/confirm",
  createSession: "/sessions",
  getSession: "/sessions/:id",
  completeSession: "/sessions/:id/complete",
  startAttempt: "/session-items/:id/attempts",
  createContentDelivery: "/content-deliveries",
  createContentPresentation: "/content-presentations",
  requestAssistance: "/attempts/:id/assistance",
  submitResponse: "/attempts/:id/responses",
  abandonAttempt: "/attempts/:id/abandon",
  getObjectiveStatus: "/objectives/:id/status",
  getObjectiveEvidence: "/objectives/:id/evidence",
  getProgressSummary: "/progress/summary",
  getUsage: "/usage",
  getCourses: "/courses",
  updateCourse: "/courses/:courseRef",
  getMoodleConnection: "/moodle/connection",
  connectMoodle: "/moodle/connection",
  disconnectMoodle: "/moodle/connection",
  getCourseTopics: "/courses/:courseRef/topics",
  getAssessments: "/assessments",
  createAssessment: "/courses/:courseRef/assessments",
  withdrawAssessment: "/assessments/:assessmentId",
  getExamPrepAssessments: "/exam-prep/assessments",
  createExamPrepPlan: "/exam-prep/plans",
  getExamPrepPlan: "/exam-prep/plans/:planId",
  reorderExamPrepTopics: "/exam-prep/plans/:planId/topic-order",
  startExamPrepSession: "/exam-prep/plans/:planId/sessions",
  getExamPrepSession: "/exam-prep/sessions/:sessionId",
  finishExamPrepSession: "/exam-prep/sessions/:sessionId/finish",
  getExamPrepItem: "/exam-prep/items/:itemId",
  submitExamPrepAttempt: "/exam-prep/items/:itemId/attempts",
  requestExamPrepHint: "/exam-prep/items/:itemId/hint",
  declareExamPrepUnknown: "/exam-prep/items/:itemId/dont-know",
  getPractisableActivities: "/practisable-activities",
  getAnswerKeyCompleteness: "/answer-key-completeness",
  createImport: "/imports",
  getImports: "/imports",
  createHomework: "/homework",
  listHomework: "/homework",
  homeworkExercises: "/homework/exercises",
  createHomeworkForExercise: "/courses/:courseRef/exercises/:exerciseId/homework",
  getHomework: "/homework/:id",
  readHomeworkAgain: "/homework/:id/read-again",
  getHomeworkTranscription: "/homework/pages/:pageId/transcription",
  getHomeworkPageImage: "/homework/pages/:pageId/image",
  createHomeworkCorrection: "/homework/pages/:pageId/regions/:regionId/corrections",
  setHomeworkQuestionLabel: "/homework/pages/:pageId/regions/:regionId/label",
  confirmHomeworkPage: "/homework/pages/:pageId/confirm",
  recordHomeworkConfidence: "/homework/:id/confidence",
  disputeHomeworkVerdict: "/homework/:id/claims/:claimId/dispute",
  checkHomework: "/homework/:id/check",
  getHomeworkFeedback: "/homework/:id/feedback",
  pullHomeworkRung: "/homework/claims/:claimId/rungs",
  triageHomework: "/homework/:id/triage",
  getHomeworkTriage: "/homework/:id/triage",
  getHomeworkFeedbackChannel: "/homework/:id/feedback-channel",
  openHomeworkFeedbackChannel: "/homework/:id/feedback-channel/open",
  showHomeworkFeedbackReason: "/homework/:id/feedback-channel/flags/:regionId/reason",
  retryHomeworkFeedback: "/homework/:id/feedback-channel/retry",
} as const;

/**
 * Every operation the `Idempotency-Key` guard governs, as `METHOD path`
 * in Fastify form and relative to the API prefix — the same form
 * API_ROUTE_PATHS above uses.
 *
 * Emitted so the coupling between the two halves is mechanical rather
 * than remembered (#62). The contract half is the generated client method
 * taking an `idempotencyKey` argument; the server half is the route
 * declaring `config: { idempotent: true }`. The dangerous direction is a
 * route governed on the server whose client method cannot send a key:
 * every call from that client answers 400, and nothing in either package
 * notices, because each side is internally consistent. A server test
 * asserts the routes it registers as idempotent are exactly this set.
 *
 * A route that is deliberately ungoverned is absent from here and present
 * in the server's IDEMPOTENCY_EXEMPT with its own written argument. The
 * two lists are complements, and neither is derived from the other —
 * which is the point: an exemption is a claim about why a duplicate is
 * safe, and a claim has to be made somewhere a reviewer reads.
 */
export const API_IDEMPOTENT_ROUTES = [
  "POST /sessions",
  "POST /sessions/:id/complete",
  "POST /session-items/:id/attempts",
  "POST /content-deliveries",
  "POST /content-presentations",
  "POST /attempts/:id/assistance",
  "POST /attempts/:id/responses",
  "POST /attempts/:id/abandon",
  "PATCH /courses/:courseRef",
  "POST /courses/:courseRef/assessments",
  "DELETE /assessments/:assessmentId",
  "POST /exam-prep/plans",
  "POST /exam-prep/plans/:planId/topic-order",
  "POST /exam-prep/plans/:planId/sessions",
  "POST /exam-prep/sessions/:sessionId/finish",
  "POST /exam-prep/items/:itemId/attempts",
  "POST /exam-prep/items/:itemId/hint",
  "POST /exam-prep/items/:itemId/dont-know",
  "POST /homework",
  "POST /courses/:courseRef/exercises/:exerciseId/homework",
  "POST /homework/:id/read-again",
  "POST /homework/pages/:pageId/regions/:regionId/corrections",
  "POST /homework/pages/:pageId/regions/:regionId/label",
  "POST /homework/pages/:pageId/confirm",
  "POST /homework/:id/confidence",
  "POST /homework/:id/claims/:claimId/dispute",
  "POST /homework/:id/check",
  "POST /homework/claims/:claimId/rungs",
  "POST /homework/:id/triage",
  "POST /homework/:id/feedback-channel/open",
  "POST /homework/:id/feedback-channel/flags/:regionId/reason",
  "POST /homework/:id/feedback-channel/retry",
] as const;

/**
 * $ref targets — every schema at least one other schema in this API
 * `$ref`s (refTargetNames, scripts/lib.ts) — kept WITH their own `$id`,
 * unlike every entry in API_SCHEMAS above, which strips it. A server
 * registers each of these with `app.addSchema()` **before** any route is
 * registered (http/app.ts); Fastify then resolves a `$ref` naming that
 * `$id` in both the request validator and the response serialiser for
 * every route registered afterward. Empty array is a legal, harmless
 * state — it means no schema in this API `$ref`s another one yet.
 */
export const API_SHARED_SCHEMAS = [
  {
    "$id": "https://davinci.local/schemas/assessment-response.json",
    "additionalProperties": false,
    "description": "One assessment the learner declared (#444): a title, a kind, a date and the topics it covers. A learner-entered declaration - it is neither an observation of how the learner performed nor a conclusion about what they know, and it carries no status, score, count or percentage. The title is untrusted learner text, returned as data.",
    "properties": {
      "assessmentId": {
        "description": "An identifier DaVinci minted when the learner added the assessment.",
        "maxLength": 64,
        "minLength": 1,
        "type": "string"
      },
      "courseRef": {
        "description": "Moodle's course id, as the decimal string the path parameter and the courses/ folders use (an identifier, never a magnitude).",
        "maxLength": 10,
        "minLength": 1,
        "type": "string"
      },
      "date": {
        "description": "The day of the assessment, YYYY-MM-DD.",
        "format": "date",
        "type": "string"
      },
      "kind": {
        "description": "What sort of assessment the learner said it is.",
        "enum": [
          "quiz",
          "midterm",
          "exam",
          "other"
        ],
        "type": "string"
      },
      "scope": {
        "description": "The topics (course sections) it covers, ordered by section number. Never empty.",
        "items": {
          "additionalProperties": false,
          "properties": {
            "sectionName": {
              "description": "The section's name as Moodle gave it (untrusted text, returned as data), or null.",
              "type": [
                "string",
                "null"
              ]
            },
            "sectionNumber": {
              "description": "The course section's number: an identifier, not a magnitude.",
              "type": "integer"
            },
            "topicKey": {
              "description": "An identifier, moodleCourseId:sectionNumber.",
              "type": "string"
            }
          },
          "required": [
            "topicKey",
            "sectionNumber",
            "sectionName"
          ],
          "type": "object"
        },
        "minItems": 1,
        "type": "array"
      },
      "source": {
        "const": "learner",
        "description": "Who declared the assessment. Always learner for now; Moodle-sourced assessments come later.",
        "type": "string"
      },
      "title": {
        "description": "The learner's own title for the assessment (untrusted text, returned as data).",
        "maxLength": 120,
        "minLength": 1,
        "type": "string"
      }
    },
    "required": [
      "assessmentId",
      "courseRef",
      "title",
      "kind",
      "date",
      "scope",
      "source"
    ],
    "title": "AssessmentResponse",
    "type": "object"
  },
  {
    "$id": "https://davinci.local/schemas/course-response.json",
    "additionalProperties": false,
    "description": "One course the pilot's Moodle catalog lists for this learner (#436): its titles (untrusted Moodle text, returned as data), the earliest exercise still due, whether the learner ticked it and the colour that identifies it. Nothing here says how the learner is doing.",
    "properties": {
      "code": {
        "description": "The course code, for example 80134.",
        "type": "string"
      },
      "colour": {
        "description": "The learner's chosen identifier colour, or the default for the course's place in the list.",
        "enum": [
          "slate",
          "teal",
          "plum",
          "ochre",
          "moss",
          "rose"
        ],
        "type": "string"
      },
      "moodleCourseId": {
        "description": "Moodle's own course id, as the decimal string the path parameter and the courses/ folders already use (an identifier, never a magnitude).",
        "maxLength": 10,
        "minLength": 1,
        "type": "string"
      },
      "nextDue": {
        "additionalProperties": false,
        "description": "The earliest exercise of the course's context file due after now, or null when there is none or the course has no context file.",
        "properties": {
          "dueAt": {
            "description": "ISO-8601 UTC.",
            "type": "string"
          },
          "exerciseTitle": {
            "type": "string"
          }
        },
        "required": [
          "exerciseTitle",
          "dueAt"
        ],
        "type": [
          "object",
          "null"
        ]
      },
      "selected": {
        "description": "Ticked by default; false once the learner unticks it.",
        "type": "boolean"
      },
      "shortName": {
        "description": "The name shown in the course-focus picker: the learner's own short name, or the default derived from the title (trailing (1)-style numerals and 'for ... students' / Hebrew 'לתלמידי ...' qualifiers removed).",
        "maxLength": 200,
        "minLength": 1,
        "type": "string"
      },
      "shortNameIsDefault": {
        "description": "True while the learner has not set their own short name (the web offers 'reset' only when false).",
        "type": "boolean"
      },
      "symbol": {
        "additionalProperties": false,
        "description": "The identifier glyph: the learner's override, or the automatic one (subject family from the catalogue snapshot by course number; maths sub-symbol by title keyword inside mathematics only; otherwise the first letter). `letter` is always the first letter of the short name, so a client can render the 'letter' key.",
        "properties": {
          "key": {
            "enum": [
              "medicine",
              "economics",
              "languages",
              "education",
              "sociology",
              "law",
              "history",
              "politics",
              "life-sciences",
              "agriculture",
              "psychology",
              "area-studies",
              "computer-science",
              "chemistry",
              "philosophy",
              "mathematics",
              "physics",
              "religion",
              "geography",
              "earth",
              "communication",
              "arts",
              "statistics",
              "calculus",
              "linear-algebra",
              "discrete",
              "algebra",
              "geometry",
              "number-theory",
              "probability",
              "letter"
            ],
            "type": "string"
          },
          "letter": {
            "maxLength": 8,
            "minLength": 1,
            "type": "string"
          }
        },
        "required": [
          "key",
          "letter"
        ],
        "type": "object"
      },
      "symbolIsDefault": {
        "description": "True while the learner has not overridden the automatic symbol.",
        "type": "boolean"
      },
      "title": {
        "additionalProperties": false,
        "description": "The course title in Hebrew and in English (the same text in both when Moodle gave one language).",
        "properties": {
          "en": {
            "type": "string"
          },
          "he": {
            "type": "string"
          }
        },
        "required": [
          "he",
          "en"
        ],
        "type": "object"
      }
    },
    "required": [
      "moodleCourseId",
      "code",
      "title",
      "nextDue",
      "selected",
      "colour",
      "shortName",
      "shortNameIsDefault",
      "symbol",
      "symbolIsDefault"
    ],
    "title": "CourseResponse",
    "type": "object"
  },
  {
    "$id": "https://davinci.local/schemas/exam-prep-item.json",
    "additionalProperties": false,
    "description": "One problem of an exam-prep session (#440, #362): a changed-number variant of the learner's own sheet problem, computed and graded by the certificate kernel with no model call, or a proof pointer back to the sheet. The answer is never in it until the learner says they do not know yet. Carries no is_independent, status, score or percentage: what the work counted as is its outcome, in words.",
    "properties": {
      "attempts": {
        "description": "Every answer the learner submitted on this item, oldest first. An unparseable one is listed as such: it was re-prompted and never marked wrong.",
        "items": {
          "additionalProperties": false,
          "description": "One submitted answer.",
          "properties": {
            "answerText": {
              "description": "What the learner typed (untrusted text, returned as data); null for \"I don't know yet\".",
              "type": [
                "string",
                "null"
              ]
            },
            "attemptId": {
              "description": "An identifier DaVinci minted.",
              "type": "string"
            },
            "incorrectReason": {
              "description": "For incorrect: what failed, from the kernel's refutation.",
              "enum": [
                "condition_false",
                "condition_undefined",
                "not_in_domain",
                "hypothesis_false",
                "conclusions_hold",
                null
              ],
              "type": [
                "string",
                "null"
              ]
            },
            "result": {
              "description": "unparseable: not an answer in a form DaVinci can check, never graded. correct and incorrect: the kernel's verdict. declared_unknown: the learner said they do not know yet.",
              "enum": [
                "unparseable",
                "correct",
                "incorrect",
                "declared_unknown"
              ],
              "type": "string"
            },
            "submittedAt": {
              "description": "When the answer was submitted.",
              "format": "date-time",
              "type": "string"
            },
            "unparseableReason": {
              "description": "For unparseable: why it could not be read.",
              "enum": [
                "empty",
                "not_an_assignment",
                "two_equals",
                "bad_variable_name",
                "unknown_variable",
                "variable_assigned_twice",
                "variable_unassigned",
                "value_not_exact",
                null
              ],
              "type": [
                "string",
                "null"
              ]
            }
          },
          "required": [
            "attemptId",
            "answerText",
            "result",
            "unparseableReason",
            "incorrectReason",
            "submittedAt"
          ],
          "type": "object"
        },
        "type": "array"
      },
      "help": {
        "additionalProperties": false,
        "description": "Help on this item. A hint costs independence (it supplies information toward the answer); the reveal is answer-revealing. Both are recorded. Help on the item is prior to every later answer on it; help on another item of the topic is prior to every later answer on the topic while this session is open and for 24 hours after the later of the delivery and the finish (topic-status@3 rule 6b, using independence-scope@1's 24-hour window); such help reaches the independence decision as information toward the answer, so it never excuses a wrong answer (rule 6d). Reading an item re-shows help and writes nothing, so its text is returned only while the session is open, and a re-show counts because the open session keeps it counting; the flags below always say that it was given.",
        "properties": {
          "canAskForHint": {
            "description": "May the learner ask for the hint now? Only after a graded answer, once, while the item and session are open.",
            "type": "boolean"
          },
          "canDeclareUnknown": {
            "description": "May the learner say \"I don't know yet\" now? Any time the item and session are open.",
            "type": "boolean"
          },
          "hint": {
            "additionalProperties": false,
            "description": "The hint shown, or null: before it is given, once the session is finished, and once it is more than 24 hours old. The values lie within the bound; it does not name the answer.",
            "properties": {
              "bound": {
                "description": "A decimal string: every value in the answer lies between minus this and this.",
                "type": "string"
              },
              "code": {
                "const": "values_within",
                "description": "Which hint this is.",
                "type": "string"
              }
            },
            "required": [
              "code",
              "bound"
            ],
            "type": [
              "object",
              "null"
            ]
          },
          "hintShown": {
            "description": "A hint was given on this item, whether or not its text is still returned.",
            "type": "boolean"
          },
          "reveal": {
            "additionalProperties": false,
            "description": "The computed answer, shown after \"I don't know yet\"; null before, once the session is finished, and once it is more than 24 hours old (the answer is not re-read outside the window that priced it).",
            "properties": {
              "answerText": {
                "description": "The kernel's own answer, in the form an answer is typed: x = 3, y = -1.",
                "type": "string"
              },
              "code": {
                "const": "answer",
                "description": "Which reveal this is.",
                "type": "string"
              }
            },
            "required": [
              "code",
              "answerText"
            ],
            "type": [
              "object",
              "null"
            ]
          },
          "revealShown": {
            "description": "The answer was shown on this item (by \"I don't know yet\" or by the learner's own data export), whether or not its text is still returned.",
            "type": "boolean"
          }
        },
        "required": [
          "hint",
          "reveal",
          "hintShown",
          "revealShown",
          "canAskForHint",
          "canDeclareUnknown"
        ],
        "type": "object"
      },
      "itemId": {
        "description": "An identifier DaVinci minted.",
        "type": "string"
      },
      "kind": {
        "description": "variant: a problem DaVinci made and can check. proof_pointer: a proof or derivation DaVinci cannot check; it points back to the sheet.",
        "enum": [
          "variant",
          "proof_pointer"
        ],
        "type": "string"
      },
      "outcome": {
        "description": "What the work counted as once the item is closed: on_your_own, with_help (a hint came first), not_yet, or proof_pointer. null while the item is open. Derived by the server.",
        "enum": [
          "on_your_own",
          "with_help",
          "not_yet",
          "proof_pointer",
          null
        ],
        "type": [
          "string",
          "null"
        ]
      },
      "pointer": {
        "additionalProperties": false,
        "description": "For a proof pointer: where to look; null for a variant.",
        "properties": {
          "question": {
            "description": "The question label (untrusted text, returned as data), or null.",
            "type": [
              "string",
              "null"
            ]
          },
          "sheet": {
            "description": "The sheet's title (untrusted text, returned as data), or null.",
            "type": [
              "string",
              "null"
            ]
          },
          "why": {
            "description": "A plain sentence saying DaVinci cannot check a proof here.",
            "type": "string"
          }
        },
        "required": [
          "sheet",
          "question",
          "why"
        ],
        "type": [
          "object",
          "null"
        ]
      },
      "reason": {
        "additionalProperties": false,
        "description": "Why this problem is here (invariant 3 on the screen): \"From Sheet 3, Q2: a miss you were sure of.\"",
        "properties": {
          "question": {
            "description": "The question label (untrusted text, returned as data), or null.",
            "type": [
              "string",
              "null"
            ]
          },
          "returning": {
            "description": "Why the topic is back: after a not yet, or as the check that can earn retained. null for a first serving.",
            "enum": [
              "not_yet",
              "retention_check",
              null
            ],
            "type": [
              "string",
              "null"
            ]
          },
          "sheet": {
            "description": "The sheet's title (untrusted text, returned as data), or null.",
            "type": [
              "string",
              "null"
            ]
          },
          "tier": {
            "description": "What the learner's own homework showed about the topic.",
            "enum": [
              "sure_then_wrong",
              "wrong",
              "fixed_with_help",
              "not_tested_in_app"
            ],
            "type": "string"
          }
        },
        "required": [
          "tier",
          "sheet",
          "question",
          "returning"
        ],
        "type": "object"
      },
      "state": {
        "description": "open: can be answered. solved: answered correctly. revealed: the learner said they do not know yet and was shown the answer; the item is closed. pointer: a proof pointer.",
        "enum": [
          "open",
          "solved",
          "revealed",
          "pointer"
        ],
        "type": "string"
      },
      "stem": {
        "additionalProperties": false,
        "description": "The problem as data, for a variant; null for a proof pointer.",
        "properties": {
          "answerTemplate": {
            "description": "The shape of an answer, the variables with the value left blank: \"x = , y = \". Answers are typed left-to-right as x = 3, y = -1.",
            "type": "string"
          },
          "claim": {
            "description": "A witness: the conditions to satisfy. A counterexample: the conclusions to break.",
            "items": {
              "additionalProperties": false,
              "description": "One condition of the stem.",
              "properties": {
                "tex": {
                  "description": "The condition as LaTeX, for KaTeX. Maths is left-to-right in a right-to-left screen.",
                  "type": "string"
                },
                "text": {
                  "description": "The same condition in the linear syntax the answer is typed in, for assistive technology and copying.",
                  "type": "string"
                }
              },
              "required": [
                "tex",
                "text"
              ],
              "type": "object"
            },
            "type": "array"
          },
          "domain": {
            "description": "Where the variables range: N0 the naturals from 0, N1 the naturals from 1, Z the integers, Q the rationals.",
            "enum": [
              "N0",
              "N1",
              "Z",
              "Q"
            ],
            "type": "string"
          },
          "given": {
            "description": "The hypotheses. Empty for a witness.",
            "items": {
              "additionalProperties": false,
              "description": "One condition of the stem.",
              "properties": {
                "tex": {
                  "description": "The condition as LaTeX, for KaTeX. Maths is left-to-right in a right-to-left screen.",
                  "type": "string"
                },
                "text": {
                  "description": "The same condition in the linear syntax the answer is typed in, for assistive technology and copying.",
                  "type": "string"
                }
              },
              "required": [
                "tex",
                "text"
              ],
              "type": "object"
            },
            "type": "array"
          },
          "quantifierTex": {
            "description": "The quantifier and domain as LaTeX, for example \\exists x, y \\in \\mathbb{Z}.",
            "type": "string"
          },
          "statementTex": {
            "description": "The whole statement on one line as LaTeX.",
            "type": "string"
          },
          "taskKind": {
            "description": "witness: find values that make every condition in claim true. counterexample: the claim \"given implies claim\" is false; find values that satisfy every given condition and break claim.",
            "enum": [
              "witness",
              "counterexample"
            ],
            "type": "string"
          },
          "variables": {
            "description": "The variables an answer assigns, each exactly once, in order.",
            "items": {
              "type": "string"
            },
            "type": "array"
          }
        },
        "required": [
          "taskKind",
          "domain",
          "variables",
          "quantifierTex",
          "given",
          "claim",
          "statementTex",
          "answerTemplate"
        ],
        "type": [
          "object",
          "null"
        ]
      },
      "topic": {
        "additionalProperties": false,
        "description": "The topic it is practising.",
        "properties": {
          "sectionName": {
            "description": "The section's name as Moodle gave it (untrusted text, returned as data), or null.",
            "type": [
              "string",
              "null"
            ]
          },
          "topicKey": {
            "description": "An identifier, moodleCourseId:sectionNumber.",
            "type": "string"
          }
        },
        "required": [
          "topicKey",
          "sectionName"
        ],
        "type": "object"
      }
    },
    "required": [
      "itemId",
      "topic",
      "kind",
      "reason",
      "stem",
      "pointer",
      "state",
      "outcome",
      "attempts",
      "help"
    ],
    "title": "ExamPrepItem",
    "type": "object"
  },
  {
    "$id": "https://davinci.local/schemas/learner-status.json",
    "enum": [
      "not_tested",
      "learning",
      "can_use_with_help",
      "can_use_independently",
      "retained"
    ],
    "title": "LearnerStatus",
    "type": "string"
  },
  {
    "$id": "https://davinci.local/schemas/my-data-table.json",
    "additionalProperties": false,
    "description": "One table of the learner's own rows in the My data view (#585): a readable label and each row's time and readable fields. Part of MyDataResponse and no operation's request body.",
    "properties": {
      "label": {
        "maxLength": 128,
        "type": "string"
      },
      "rows": {
        "items": {
          "additionalProperties": false,
          "properties": {
            "fields": {
              "description": "Readable column values, shortened, in column order. Null columns are left out.",
              "items": {
                "additionalProperties": false,
                "properties": {
                  "name": {
                    "maxLength": 128,
                    "type": "string"
                  },
                  "value": {
                    "maxLength": 300,
                    "type": "string"
                  }
                },
                "required": [
                  "name",
                  "value"
                ],
                "type": "object"
              },
              "maxItems": 128,
              "type": "array"
            },
            "id": {
              "maxLength": 256,
              "type": "string"
            },
            "when": {
              "maxLength": 64,
              "type": [
                "string",
                "null"
              ]
            },
            "withheld": {
              "description": "Set when this row's revealing fields are left out. `feedback_unopened` and `check_unopened`: the learner has not opened the feedback or the check yet. `help_content`: the row holds content DaVinci delivers as help (a reason, a certificate, a stored answer, a delivered hint), which is never shown here; it is seen where the product offers it, and seeing it there is recorded as help. `source_text_excluded`: text taken from a source (OCR text, a page folio, a proposed import payload) is not shown here.",
              "enum": [
                "feedback_unopened",
                "check_unopened",
                "help_content",
                "source_text_excluded",
                null
              ],
              "type": [
                "string",
                "null"
              ]
            }
          },
          "required": [
            "id",
            "when",
            "fields",
            "withheld"
          ],
          "type": "object"
        },
        "maxItems": 1000,
        "type": "array"
      },
      "table": {
        "maxLength": 128,
        "type": "string"
      },
      "truncated": {
        "description": "True when the table held more rows than one response carries.",
        "type": "boolean"
      }
    },
    "required": [
      "table",
      "label",
      "rows",
      "truncated"
    ],
    "title": "MyDataTable",
    "type": "object"
  },
] as const;
